import {
  ArrowLeftRight,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Cpu,
  Crosshair,
  Download,
  FileSpreadsheet,
  Gauge,
  Globe2,
  Image as ImageIcon,
  LayoutGrid,
  ListChecks,
  LoaderCircle,
  Lock,
  MessageSquareQuote,
  Mic2,
  Newspaper,
  Pencil,
  Plus,
  Quote,
  Save,
  Search,
  SlidersHorizontal,
  Tag,
  Tags,
  Target,
  Trash2,
  Undo2,
  Upload,
  Users,
  WandSparkles
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readSettingsPrefill } from "./settings-prefill";
import { Link, useSearchParams } from "react-router-dom";
import { useAppShell } from "@/shell/app-shell-context";
import { canOperate, hasRoleAtLeast, isMaster } from "@/shared/utils/roles";
import { CompanyCenaWeights } from "./CompanyCenaWeights";
import { CatalogImportPanel } from "./CatalogImportPanel";
import { CatalogImportButton } from "./CatalogImportDialog";
import { SettingsFormDialog } from "./SettingsFormDialog";
import { FocusInvertDialog, type InvertedTerm } from "./FocusInvertDialog";
import { CompanyChecklistCard } from "@/features/companies/CompanyChecklistCard";
import { useCompanyChecklist } from "@/features/companies/use-company-checklist";
import { STANDARD_NETWORK_CHANNEL_NAMES, standardChannelsNotice } from "./standard-channels";
import { ConfigExportButton, configFilename } from "./ConfigExportButton";
import { downloadBlob } from "@/features/uploads/upload-shared";
import {
  buildThemeBlocks,
  isFiltering,
  THEME_BLOCK_PREVIEW,
  themeCountLabel,
  weightLabel
} from "./theme-blocks";
import { missingFields, type RequiredField } from "@/shared/components/RequiredFieldsHint";
import {
  ApiError,
  apiClient,
  type BrandOut,
  type CatalogAlias,
  type Channel,
  type ClassificationTerm,
  type CitationTagGroup,
  type CompanyProfile,
  type Competitor,
  type FocusCategory,
  type FocusTerm,
  type KeyMessage,
  type PostSource,
  type Prompt,
  type AiModel,
  type EffectivePromptModel,
  type PromptRule,
  type PublisherGroup,
  type PublisherPerson,
  type PublisherTier,
  type Spokesperson,
  type TermCategory,
  type TermType,
  type Theme,
  type ThemeGroup,
  type Vehicle
} from "@/shared/api/api-client";
import { PROMPT_TASK_LABELS } from "@/shared/vocabulary/prompt-tasks";
import {
  DETERMINISTIC_SCOPES,
  DETERMINISTIC_SCOPE_LABELS,
  THEME_CLASSIFICATION_MODES,
  THEME_CLASSIFICATION_MODE_LABELS,
  themeModeTag,
  type DeterministicScope,
  type ThemeClassificationMode
} from "@/shared/vocabulary/theme-classification";

import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ThemeAssignmentPanel } from "@/features/settings/ThemeAssignmentPanel";
import { TermsEditor, TermChips } from "@/features/settings/TermsEditor";
import { formatInteger } from "@/shared/utils/format";
import { formatMoney } from "@/shared/utils/format-currency";
import { Pager } from "@/shared/components/Pager";
import { ListSearch } from "@/shared/components/ListSearch";
import { filterBySearch } from "@/shared/utils/text-search";
import { splitEntries, MULTI_ENTRY_HINT } from "@/shared/text/split-entries";
import { addSequentially, remainingToDraft } from "@/shared/text/add-sequentially";
import { splitLines } from "@/shared/text/split-lines";
import { Select } from "@/shared/components/Select";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { promptMarkers } from "@/shared/text/prompt-markers";
import { useConfirmLeave, useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";
import { CompanyLogoField } from "./CompanyLogoField";
import {
  VEHICLE_KINDS,
  VEHICLE_KIND_DEFAULT,
  VEHICLE_KIND_LABELS,
  type VehicleKind
} from "@/shared/vocabulary/vehicle-kinds";

/** Os termos de citação numa forma comparável: a ordem das categorias
 *  (que muda quando uma é regravada) e as vazias não contam. */
function citationSignature(groups: CitationTagGroup[]): string {
  return JSON.stringify(
    groups
      .filter(group => group.tags.length > 0)
      .map(group => [group.category, group.tags] as const)
      .sort(([a], [b]) => a.localeCompare(b))
  );
}

type SettingsPageProps = {
  /** Pro atalho do assistente de configuração (rota própria). */
  companyId?: string;
  companyName: string;
  token: string;
};

type TabId =
  | "geral"
  | "marcas"
  | "concorrentes"
  | "mensagens"
  | "temas"
  | "canais"
  | "veiculos"
  | "portavozes"
  | "publicadores-tipos"
  | "publicadores"
  | "importar-configuracao"
  | "premissas"
  | "termos-classificacao"
  | "foco"
  | "texto-prompt"
  | "modelo-ia";

interface SettingsTabDef {
  id: TabId;
  label: string;
  icon: React.ElementType;
  group: "empresa" | "monitoramento" | "ia" | "ferramentas";
  /** Palavra que acompanha a contagem: "3 temas", "1 tema". */
  unit?: [singular: string, plural: string];
  /** A segunda contagem da aba que junta duas listas ("12 temas · 87
   * subtemas"). Vem de `counts.subtemas`. */
  secondaryUnit?: [singular: string, plural: string];
  /** Texto fixo do selo, pras abas que não são lista nem formulário
   * (a importação por planilha). */
  staticStatus?: string;
}

// Os contadores desta lista eram TEXTO FIXO — "2 ativos", "2
// cadastrados", "Sem entradas". Mentiam nos dois sentidos: prometiam
// dois concorrentes numa empresa sem nenhum, e diziam "sem entradas"
// depois de você cadastrar dez. Agora vêm de `counts`, contados no
// servidor.
const TABS: SettingsTabDef[] = [
  // ── EMPRESA
  { id: "geral", label: "Dados da empresa", icon: Building2, group: "empresa" },
  { id: "marcas", label: "Marcas da empresa", icon: Tag, group: "empresa", unit: ["marca", "marcas"] },
  { id: "concorrentes", label: "Concorrentes", icon: Target, group: "empresa", unit: ["concorrente", "concorrentes"] },
  { id: "portavozes", label: "Porta-vozes", icon: Mic2, group: "empresa", unit: ["porta-voz", "porta-vozes"] },
  { id: "importar-configuracao", label: "Importar configuração", icon: FileSpreadsheet, group: "ferramentas", staticStatus: "Planilha" },

  // ── MONITORAMENTO
  { id: "mensagens", label: "Mensagem-chave", icon: BookOpen, group: "monitoramento", unit: ["mensagem", "mensagens"] },
  {
    id: "temas",
    label: "Temas e subtemas",
    icon: LayoutGrid,
    group: "monitoramento",
    unit: ["tema", "temas"],
    secondaryUnit: ["subtema", "subtemas"]
  },
  { id: "canais", label: "Canais monitorados", icon: Globe2, group: "monitoramento", unit: ["canal", "canais"] },
  { id: "veiculos", label: "Veículos", icon: Newspaper, group: "monitoramento", unit: ["veículo", "veículos"] },
  { id: "publicadores-tipos", label: "Porte do publicador", icon: Users, group: "monitoramento", unit: ["faixa", "faixas"] },
  { id: "publicadores", label: "Categorização de publicadores", icon: Users, group: "monitoramento", unit: ["publicador", "publicadores"] },

  // ── INTELIGÊNCIA ARTIFICIAL
  // A antiga "Configuração de prompt" (id `prompts`) virou cinco abas
  // (2026-10-31). O selo de contagem ficou só em "Texto do prompt": o
  // snapshot de `counts.prompts` conta as 13 linhas das duas fontes.
  { id: "premissas", label: "Premissas", icon: ListChecks, group: "ia" },
  { id: "termos-classificacao", label: "Termos de classificação", icon: Tags, group: "ia" },
  { id: "foco", label: "Foco", icon: Crosshair, group: "ia" },
  { id: "texto-prompt", label: "Texto do prompt", icon: MessageSquareQuote, group: "ia", unit: ["texto", "textos"] },
  { id: "modelo-ia", label: "Modelo de IA", icon: Cpu, group: "ia" }
];

/** As cinco abas que dividem o MESMO estado de configuração de prompt. */
const PROMPT_TAB_IDS = ["premissas", "termos-classificacao", "foco", "texto-prompt", "modelo-ia"] as const;
type PromptTabId = (typeof PROMPT_TAB_IDS)[number];
const isPromptTab = (tab: TabId): tab is PromptTabId =>
  (PROMPT_TAB_IDS as readonly string[]).includes(tab);

const PROMPT_TAB_COPY: Record<PromptTabId, { title: string; description: React.ReactNode }> = {
  premissas: {
    title: "Premissas",
    description: (
      <>
        O que a IA recebe pra classificar a polaridade de uma publicação desta marca. Muda
        por marca <strong>e</strong> por fonte — o que vale nas redes não vale na imprensa.
      </>
    )
  },
  "termos-classificacao": {
    title: "Termos de classificação",
    description: (
      <>
        Termos que decidem a polaridade sozinhos, sem chamar a IA. Mudam por marca{" "}
        <strong>e</strong> por fonte.
      </>
    )
  },
  foco: {
    title: "Foco",
    description: "O que diz à IA de quem a matéria fala. Muda por marca e por fonte."
  },
  "texto-prompt": {
    title: "Texto do prompt",
    description: "O texto enviado ao modelo em cada tarefa. Muda por fonte."
  },
  "modelo-ia": {
    title: "Modelo de IA",
    description: "Qual modelo de IA atende cada tarefa desta empresa."
  }
};

/** Id de aba vindo da URL: o `prompts` antigo (deep link salvo, rota
 * legada) abre Premissas. */
const resolveTabId = (requested: string | null): TabId | null => {
  if (requested === "prompts") return "premissas";
  return TABS.some(tab => tab.id === requested) ? (requested as TabId) : null;
};

/** O selo da navegação lateral, a partir da contagem real.
 *
 * `undefined` é um terceiro estado legítimo e não vira "0": significa
 * "ainda não sei", enquanto as contagens carregam. Mostrar zero antes
 * da resposta chegar seria a mesma mentira do texto fixo que isto veio
 * substituir, só que por menos tempo. */
function navStatus(
  tab: SettingsTabDef,
  count: number | undefined,
  secondaryCount?: number
): { dot: "ok" | "empty"; text: string } {
  if (tab.staticStatus) return { dot: "ok", text: tab.staticStatus };
  // "Dados da empresa" não é lista — ou a empresa existe, ou você não
  // estaria aqui.
  if (!tab.unit) return { dot: "ok", text: "Preenchido" };
  if (count === undefined) return { dot: "empty", text: "—" };
  if (tab.secondaryUnit) {
    if (secondaryCount === undefined) return { dot: "empty", text: "—" };
    if (count === 0 && secondaryCount === 0) return { dot: "empty", text: "Sem entradas" };
    return {
      dot: "ok",
      text:
        `${count} ${tab.unit[count === 1 ? 0 : 1]} · ` +
        `${secondaryCount} ${tab.secondaryUnit[secondaryCount === 1 ? 0 : 1]}`
    };
  }
  if (count === 0) return { dot: "empty", text: "Sem entradas" };
  return { dot: "ok", text: `${count} ${tab.unit[count === 1 ? 0 : 1]}` };
}

/** Rótulo humano das tarefas de classificação. As chaves são os
 * valores de `PromptType` no servidor (`core/vocabulary.py`). */

/** As quatro polaridades com faixa de numeração reservada.
 *
 * `term` é a categoria do termo determinístico; `rule` é o tipo da
 * premissa. São vocabulários DIFERENTES pro mesmo conceito porque um
 * mora em `classification_term.category` e o outro em
 * `prompt_rule.rule_type`, que o motor deriva do nome do marcador do
 * prompt (`$$$positivo$$$` → `POSITIVO`). */
const POLARITY_SECTIONS: {
  /** `null`: seção só de premissas (GLOBAL e NEUTRO não têm categoria de
   * termo — `TermCategory` só conhece as quatro polaridades e as duas
   * compostas). */
  term: TermCategory | null;
  rule: string;
  label: string;
  /** `true`: o tipo tem faixa reservada (`RULE_TYPE_RANGE_START`), logo
   * numeração contínua e teto de 15. `false` (GLOBAL, NEUTRO): entra no
   * prompt como lista com marcador, sem teto nem número. */
  numbered: boolean;
  /** Texto de apoio das seções que não são uma polaridade de verdade. */
  hint?: string;
}[] = [
  {
    term: null,
    rule: "GLOBAL",
    label: "Premissas gerais",
    numbered: false,
    hint: "Valem para todas as polaridades e entram no prompt antes das premissas de cada uma, como lista sem numeração."
  },
  { term: "positive", rule: "POSITIVO", label: "Positivo", numbered: true },
  {
    term: null,
    rule: "NEUTRO",
    label: "Neutro",
    numbered: false,
    hint: "Entram no prompt como lista sem numeração. Em 3 níveis o neutro é a resposta final; em 4 níveis é a primeira passada, que a segunda refina."
  },
  { term: "negative", rule: "NEGATIVO", label: "Negativo", numbered: true },
  { term: "favorable", rule: "FAVORAVEL", label: "Favorável", numbered: true },
  { term: "unfavorable", rule: "DESFAVORAVEL", label: "Desfavorável", numbered: true }
];

/** As duas compostas: só termo, sem premissa. Existem para a segunda
 * passada determinística, quando a IA devolve NEUTRO. */
const COMPOSITE_SECTIONS: { term: TermCategory; label: string }[] = [
  { term: "positive_favorable", label: "Positivo / Favorável" },
  { term: "negative_unfavorable", label: "Negativo / Desfavorável" }
];

const FOCUS_SECTIONS: { key: FocusCategory; label: string }[] = [
  { key: "client", label: "Foco cliente" },
  { key: "competitor", label: "Foco concorrente" }
];

/** As categorias de "Detecção de Citação no Título".
 *
 * Os nomes em CAIXA ALTA são os valores que o banco guarda desde o
 * schema legado (`dcitacao.categoria`), e o autofill por IA já mapeia
 * as sugestões dele nesses mesmos nomes — mudar aqui quebraria os dois
 * lados de uma vez. O rótulo bonito fica em `CITATION_LABELS`. */
const CITATION_CATEGORIES = [
  "APELIDOS E NOMES COMUNS",
  "SLOGANS",
  "PRODUTOS",
  "PROGRAMAS E INICIATIVAS",
  "CAMPANHAS PUBLICITARIAS"
];

const CITATION_LABELS: Record<string, string> = {
  "APELIDOS E NOMES COMUNS": "Apelidos e nomes comuns",
  SLOGANS: "Slogans",
  PRODUTOS: "Produtos",
  "PROGRAMAS E INICIATIVAS": "Programas e iniciativas",
  "CAMPANHAS PUBLICITARIAS": "Campanhas publicitárias"
};

/** A largura de cada faixa de numeração no servidor
 * (`core/vocabulary.py:MAX_RULES_PER_TYPE`). Espelhado aqui só para
 * desabilitar o botão antes do 409 — o servidor é quem manda. */
const MAX_RULES_PER_SECTION = 15;

/** O campo de acrescentar termo (foco, termos de polaridade): Enter ou
 *  o "+" mandam o que foi digitado; vários de uma vez, separados por `;`. */
function AddTermBox({
  value,
  onChange,
  onAdd,
  placeholder,
  disabled,
  label
}: {
  value: string;
  onChange: (value: string) => void;
  onAdd: () => void;
  placeholder: string;
  disabled: boolean;
  /** O nome acessível do "+" — "Adicionar Foco cliente". */
  label: string;
}) {
  return (
    <div className="inline-add-box">
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === "Enter" && (e.preventDefault(), onAdd())}
        placeholder={placeholder}
      />
      <button
        type="button"
        className="secondary-button"
        onClick={onAdd}
        disabled={disabled}
        aria-label={label}
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

/** Os termos já cadastrados, cada um com o "×" de remover. */
function RemovablePills({
  items,
  onRemove,
  onReactivate,
  onMove,
  moveLabel,
  empty,
  canEdit
}: {
  items: { id: number; term: string; is_active?: boolean }[];
  onRemove: (id: number) => void;
  /** Passa o termo ATIVO pro outro lado (foco cliente <-> concorrente). */
  onMove?: (id: number) => void;
  moveLabel?: string;
  /** Termo desativado (aparece com "Mostrar desativados") volta por aqui. */
  onReactivate?: (id: number) => void;
  /** O que dizer sem nenhum; sem isto a lista vazia não diz nada. */
  empty?: string;
  /** Abaixo de Especialista (2026-10-24) o termo fica e o × some. */
  canEdit: boolean;
}) {
  return (
    <div className="tag-pill-list">
      {items.map(item => item.is_active === false ? (
        <span key={item.id} className="tag-pill is-inactive">
          {item.term}
          <span className="badge-pill inactive">Desativado</span>
          {canEdit && onReactivate && (
            <button type="button" onClick={() => onReactivate(item.id)} aria-label={`Reativar ${item.term}`}>
              ↺
            </button>
          )}
        </span>
      ) : (
        <span key={item.id} className="tag-pill">
          {item.term}
          {canEdit && onMove && (
            <button
              type="button"
              className="tag-pill-move"
              onClick={() => onMove(item.id)}
              aria-label={`${moveLabel ?? "Mover"}: ${item.term}`}
              title={moveLabel}
            >
              <ArrowLeftRight size={12} aria-hidden="true" />
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={() => onRemove(item.id)} aria-label={`Remover ${item.term}`}>
              ×
            </button>
          )}
        </span>
      ))}
      {items.length === 0 && empty && <small>{empty}</small>}
    </div>
  );
}

/** Aviso legível de uma escrita parcial (não é falha de rede nem do servidor). */
class PromptWriteNotice extends Error {}

/** A identidade da linha de prompt da empresa: não há `id` estável (a
 *  linha efetiva é o geral ou a personalização), só tarefa × fonte × posição. */
function promptKey(prompt: Pick<Prompt, "task" | "source" | "position">): string {
  return `${prompt.task}:${prompt.source}:${prompt.position}`;
}

/** Rótulo do botão de premissa: diz quantas vão entrar (as linhas não
 *  vazias, limitadas ao que cabe na seção). */
function ruleButtonLabel(lines: number, room: number): string {
  const count = Math.min(lines, Math.max(room, 0));
  return count > 1 ? `Adicionar ${count} premissas` : "Adicionar premissa";
}

/** Filtro das seções de catálogo.
 *
 * Existe porque desativar NÃO apaga: o servidor carimba `deleted_on` e
 * a linha some da listagem padrão. Sem este filtro não haveria como
 * enxergar o que saiu de circulação — nem como reativar. */
/** As abas cujo cadastro existe pra empresa E pra cada concorrente. */
const CATALOG_OWNER_TABS: string[] = [
  "portavozes",
  "mensagens",
  "temas",
  "premissas",
  "termos-classificacao",
  "foco",
  "texto-prompt",
  "modelo-ia"
];

/**
 * "Cadastro de": a empresa ou um concorrente dela.
 *
 * Concorrente tem temas, grupos de temas, mensagens-chave e
 * porta-vozes PRÓPRIOS — é com eles que a esteira classifica as menções
 * dele. Canais, veículos e publicadores não entram: são uma lista só do
 * cliente, que vale pra todas as marcas. Sem concorrente cadastrado o
 * seletor nem aparece.
 *
 * `<Select>` (o dropdown da plataforma): a lista de concorrentes de um cliente é curta.
 */
function CatalogOwnerPicker({
  competitors,
  value,
  onChange
}: {
  competitors: Competitor[];
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  if (competitors.length === 0) return null;
  return (
    <label className="form-field-inline catalog-owner-picker">
      <span>Cadastro de</span>
      <Select
        value={value ?? ""}
        onChange={event => onChange(event.target.value ? Number(event.target.value) : null)}
      >
        <option value="">A empresa</option>
        {competitors.map(competitor => (
          <option key={competitor.id} value={competitor.id}>
            Concorrente: {competitor.name}
          </option>
        ))}
      </Select>
    </label>
  );
}

function InactiveFilter({
  checked,
  onChange
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="form-field-inline settings-switch">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      Mostrar desativados
    </label>
  );
}

/** Itens por página nas duas listas com envelope (Canais e Veículos).
 *
 * Elas são cards de uma coluna, então a página é o que cabe numa rolagem
 * curta — não o teto da rota (200). Com 14 mil veículos, o que decide o
 * conforto é a BUSCA; a página só precisa não ser um paredão. */
const CATALOG_PAGE_SIZE = 24;


/** Busca das listas grandes.
 *
 * Vai ao SERVIDOR, e isso é uma troca consciente: antes a filtragem era
 * em memória e instantânea, o que era melhor pro catálogo de 30 itens e
 * impossível pro de 14 mil. O `<form>` (e não o filtro a cada tecla)
 * existe pelo mesmo motivo — uma requisição por letra digitada é o que
 * transformaria a busca no problema que ela veio resolver. */
function CatalogSearch({
  value,
  onChange,
  onSubmit,
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
}) {
  return (
    <div className="catalog-search">
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === "Enter" && (e.preventDefault(), onSubmit())}
      />
      <button type="button" className="secondary-button" onClick={onSubmit}>
        <Search size={16} /> Buscar
      </button>
    </div>
  );
}

/** Os apelidos no CARD: só leitura.
 *
 * Editar é no formulário, e a separação não é estética — cada remoção
 * grava na hora, e um × solto no card apagaria um casamento de nome
 * (que é o que faz o post da clipadora achar o cadastro) com um clique
 * sem contexto. */
/** "SP · Sudeste · Brasil · R$ 1.200,00" — só o que estiver preenchido. */
function vehicleAttributesSummary(v: Vehicle): string {
  return [
    v.state,
    v.region,
    v.country,
    v.base_price === null ? null : formatMoney(v.base_price, "BRL")
  ]
    .filter((part): part is string => !!part)
    .join(" · ");
}

function AliasChips({ aliases }: { aliases: CatalogAlias[] }) {
  if (aliases.length === 0) return null;
  return (
    <div className="alias-chips">
      {aliases.map(alias => (
        <span key={alias.id} className="alias-chip">
          {alias.name}
        </span>
      ))}
    </div>
  );
}

/** Edição de apelidos, dentro do formulário e SÓ em modo edição.
 *
 * Cadastro novo não tem id, e apelido é um registro que aponta pro dono
 * — não há onde pendurá-lo antes do POST. Guardá-los em memória pra
 * gravar depois criaria um estado em que o canal salvou e os apelidos
 * não, que é pior do que pedir dois passos. */
function AliasEditor({
  aliases,
  draft,
  onDraft,
  onAdd,
  onRemove
}: {
  aliases: CatalogAlias[];
  draft: string;
  onDraft: (value: string) => void;
  onAdd: () => void;
  onRemove: (aliasId: number) => void;
}) {
  return (
    <div className="form-field mt-3">
      <span>Apelidos</span>
      <div className="inline-add-box">
        <input
          placeholder="Como a clipadora escreve este cadastro — vários separados por ;"
          value={draft}
          onChange={e => onDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), onAdd())}
        />
        <button type="button" className="secondary-button" onClick={onAdd}>
          <Plus size={16} /> Adicionar
        </button>
      </div>
      <small>
        Nome alternativo que resolve pra ESTE cadastro, herdando o peso, o
        tier e o "ignorar" dele. Gravado na hora, sem passar pelo "Salvar
        alterações". {MULTI_ENTRY_HINT}
      </small>
      {aliases.length > 0 && (
        <div className="tag-pill-list">
          {aliases.map(alias => (
            <span key={alias.id} className="tag-pill">
              {alias.name}
              <button
                type="button"
                onClick={() => onRemove(alias.id)}
                aria-label={`Remover apelido ${alias.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Uma data SÓ-DIA (`AAAA-MM-DD`, como `valid_until`) em pt-BR, sem
 *  passar por `Date`: `new Date("2026-12-31")` é meia-noite UTC, e em
 *  Brasília `toLocaleDateString` mostrava 30/12 — a classe do bug de
 *  `published_at` (teste de render, 2026-10-09). Os dígitos são a data. */
function dayLabel(day: string): string {
  const [year, month, dayOfMonth] = day.slice(0, 10).split("-");
  return year && month && dayOfMonth ? `${dayOfMonth}/${month}/${year}` : day;
}

/** Data de desativação em pt-BR, ou `null` se o item está ativo. */
function deactivatedAt(deletedOn: string | null): string | null {
  if (!deletedOn) return null;
  const data = new Date(deletedOn);
  return Number.isNaN(data.getTime()) ? null : data.toLocaleDateString("pt-BR");
}

/** Selo de estado + botão de desativar/reativar.
 *
 * O botão de desativar usa o ícone de lixeira porque é o gesto que o
 * usuário conhece, mas o texto é explícito: nada some. Um item de
 * catálogo desativado continua no banco e a menção já classificada
 * continua resolvendo o nome dele. */
function ActiveActions({
  isActive,
  deletedOn,
  onToggle,
  label,
  canEdit
}: {
  isActive: boolean;
  deletedOn: string | null;
  onToggle: (active: boolean) => void;
  label: string;
  /** Abaixo de Especialista (2026-10-24) o selo fica e o botão some. */
  canEdit: boolean;
}) {
  const desde = deactivatedAt(deletedOn);
  return (
    <>
      {/* Só o desativado ganha selo: "Ativo" em todo cartão repetia o
          óbvio e empurrava os botões pra lugares diferentes em cada aba. */}
      {!isActive && (
        <span className="badge-pill inactive">{desde ? `Desativado em ${desde}` : "Desativado"}</span>
      )}
      {!canEdit ? null : isActive ? (
        <button
          type="button"
          className="icon-btn-danger"
          onClick={() => onToggle(false)}
          title="Desativar — o item sai das próximas classificações, mas as menções antigas continuam com ele"
          aria-label={`Desativar ${label}`}
        >
          <Trash2 size={16} />
        </button>
      ) : (
        <button
          type="button"
          className="icon-btn"
          onClick={() => onToggle(true)}
          title="Reativar"
          aria-label={`Reativar ${label}`}
        >
          <Undo2 size={16} />
        </button>
      )}
    </>
  );
}

/**
 * Ligar/desligar um item de catálogo, para as dez abas que têm o
 * interruptor.
 *
 * Os dez `set*Active` eram byte a byte o mesmo bloco trocando quatro
 * identificadores: a rota de reativar, a de desativar, a recarga e o
 * setter de erro. É pouco código cada um, mas a ORDEM importa e estava
 * copiada dez vezes — limpar o erro ANTES (senão o erro velho fica na
 * tela durante a chamada nova), recarregar DEPOIS do sucesso (a lista
 * precisa refletir o que o servidor gravou, não o que a tela supôs), e
 * não recarregar quando falha (a lista continua correta, e recarregar
 * apagaria a mensagem que acabou de aparecer).
 *
 * **Reativar é PATCH e desativar é DELETE**, e não é assimetria à toa:
 * o `DELETE` da rota é o "desativar" do catálogo (nunca apaga), e
 * reativar é uma edição como outra qualquer. Desde 2026-09-02 desativar
 * o que já está desativado responde 409, então o erro que aparece aqui
 * também cobre o clique repetido.
 */
async function toggleCatalogActive({
  active,
  activate,
  deactivate,
  reload,
  setError,
  fallback
}: {
  active: boolean;
  activate: () => Promise<unknown>;
  deactivate: () => Promise<unknown>;
  reload: () => Promise<void>;
  setError: (message: string | null) => void;
  fallback: string;
}): Promise<void> {
  setError(null);
  try {
    await (active ? activate() : deactivate());
    await reload();
  } catch (error) {
    setError(errorMessage(error, fallback));
  }
}

/** Os formulários que abrem em pop-up, um por cadastro. */
type SettingsForm =
  | "brand"
  | "competitor"
  | "spokesperson"
  | "keyMessage"
  | "themeGroup"
  | "theme"
  | "channel"
  | "vehicle"
  | "tier"
  | "publisherGroup"
  | "publisherPerson";

export function SettingsPage({ companyId, companyName, token }: SettingsPageProps) {
  /**
   * A aba inicial pode vir da URL (`?tab=canais`), pra outras telas
   * mandarem a pessoa direto ao cadastro que falta — é o que faz o
   * "Cadastrar" da fila de correção resolver em um clique em vez de
   * abrir "Dados da empresa" e deixar a busca por conta dela.
   *
   * A aba continua em estado local depois disso: navegar entre abas não
   * empilha histórico, que é o comportamento que a tela já tinha.
   */
  const [searchParams] = useSearchParams();
  // O "Criar no cadastro" da fila de correção manda nome, marca e fonte
  // pela URL (`readSettingsPrefill`). Aplicado UMA vez, na primeira
  // entrada na aba de tema ou de mensagem — o formulário reseta a cada
  // troca de aba, e preencher de novo apagaria o que a pessoa digitou.
  const prefillRef = useRef(readSettingsPrefill(searchParams));
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const requested = searchParams.get("tab");
    // `grupos-temas` era a aba de temas, que se juntou à de subtemas
    // (2026-10-28): o link antigo abre a aba nova.
    if (requested === "grupos-temas") return "temas";
    return resolveTabId(requested) ?? "geral";
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Qual pop-up de cadastro/edição está aberto (2026-10-20). Um só pra
  // página: só uma aba aparece por vez, e o pop-up cobre a navegação.
  // É uma chave e não um booleano porque a aba Categorização de
  // publicadores tem DOIS formulários (grupo e pessoa). Quem fecha é o
  // reset do formulário de cada aba — salvar com sucesso, cancelar e
  // trocar de aba já passam por ele.
  const [openForm, setOpenForm] = useState<SettingsForm | null>(null);

  useEffect(() => {
    const content = document.querySelector(".content");
    if (content) {
      content.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [activeTab]);

  // Dados da empresa
  //
  // Os campos são os da spec (`Mapa de Domínio`, "Configurações da
  // Empresa"): nome, os dois logos, os dois botões e a detecção de
  // citação no título. Razão social, CNPJ, website e fuso horário saíram
  // — não estão na spec nem no schema, e o `GET` que os buscava era
  // justamente o 404 que punha "Not Found" no topo desta tela.
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [companyDetailError, setCompanyDetailError] = useState<string | null>(null);
  const [name, setName] = useState(companyName || "");
  const [hasCompetitorAnalysis, setHasCompetitorAnalysis] = useState(false);
  const [isGovernment, setIsGovernment] = useState(false);
  const [displaysCena, setDisplaysCena] = useState(true);
  const [publisherGroupCounts, setPublisherGroupCounts] = useState(false);
  // A escala vive no estado da PÁGINA (não só no perfil): a aba de
  // prompts também lê, e fora do modo dev o refreshProfile bate no
  // /auth/me morto — o CompanyContext não atualiza até F5. O valor
  // salvo aqui é a verdade da sessão pras duas abas.
  const [polarityScale, setPolarityScale] = useState<
    "four_level" | "three_level"
  >("four_level");
  // Do shell: o perfil do usuário (pra seção CENA ser só-admin) e o
  // refresh que faz menu/telas reagirem ao flag recém-salvo.
  const { profile: userProfile, refreshProfile } = useAppShell();
  // "Configurar o CENA da empresa" é do Máster, do Administrador e do
  // Especialista (proposta de IAM, 2026-10-22); até então só o topo via
  // estas seções. O servidor já aceitava o Especialista (`PATCH
  // /company-profile`); quem escondia era a tela.
  const canConfigureCena = hasRoleAtLeast(userProfile, "specialist");
  // Abaixo de Especialista Ajustes é só leitura (2026-10-24): o servidor
  // recusa toda escrita das abas com 403, então a tela esconde a ação em
  // vez de oferecê-la a quem só vai tomar o erro.
  const canEdit = hasRoleAtLeast(userProfile, "specialist");
  // O card "Configuração da empresa" no topo do conteúdo: carrega ao abrir
  // e é relido ao trocar de aba — você acaba de cadastrar algo na aba que
  // está saindo, e o item dela precisa refletir isso (o mesmo ponto de
  // recarga dos selos, ver `loadCounts`) — e ao fim de uma importação.
  const configChecklist = useCompanyChecklist(token);
  const reloadChecklist = configChecklist.reload;
  const [cenaWeightsOpen, setCenaWeightsOpen] = useState(false);
  const [colorLogo, setColorLogo] = useState<string | null>(null);
  const [grayLogo, setGrayLogo] = useState<string | null>(null);
  const [citationTags, setCitationTags] = useState<CitationTagGroup[]>([]);
  // Os termos como estão GRAVADOS — a base do "há alteração não salva"
  // (`companyDirty`). Muda junto com o servidor: carga, salvar e a
  // categoria que `setCitationCategory` grava na hora.
  const [savedCitationTags, setSavedCitationTags] = useState<CitationTagGroup[]>([]);
  const [autofilling, setAutofilling] = useState(false);
  const [autofillNotice, setAutofillNotice] = useState<string | null>(null);

  const loadCompanyDetail = async () => {
    setCompanyDetailError(null);
    try {
      const [perfil, tags] = await Promise.all([
        apiClient.companyProfile(token),
        apiClient.listCitationTags(token)
      ]);
      setProfile(perfil);
      setName(perfil.name);
      setHasCompetitorAnalysis(perfil.has_competitor_analysis);
      setIsGovernment(perfil.is_government);
      setDisplaysCena(perfil.displays_cena);
      setPolarityScale(perfil.polarity_scale);
      setPublisherGroupCounts(perfil.publisher_group_counts_toward_cena);
      setColorLogo(perfil.color_logo);
      setGrayLogo(perfil.gray_logo);
      setCitationTags(tags);
      setSavedCitationTags(tags);
    } catch (error) {
      setCompanyDetailError(errorMessage(error, "Não foi possível carregar os dados da empresa."));
    }
  };

  useEffect(() => {
    // A aba de prompts também carrega o perfil: é a ESCALA
    // (`polarityScale`) que decide quais seções de polaridade aparecem
    // lá — e fora do modo dev não há outro caminho de atualização (o
    // refreshProfile bate no /auth/me morto).
    if (activeTab !== "geral" && !isPromptTab(activeTab)) return;
    void loadCompanyDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const setCitationCategory = async (category: string, tags: string[]) => {
    setCompanyDetailError(null);
    try {
      if (tags.length === 0) {
        await apiClient.removeCitationTags(token, category);
      } else {
        await apiClient.setCitationTags(token, category, tags);
      }
      // Ressincroniza SÓ a categoria gravada. Trocar o estado inteiro
      // pela resposta do servidor apagava o que a pessoa tinha digitado
      // nas OUTRAS categorias e ainda não salvou — remover o último
      // termo de Produtos sumia com o slogan novo (teste de render,
      // 2026-10-09).
      const saved = (await apiClient.listCitationTags(token)).find(
        group => group.category === category
      );
      const resync = (prev: CitationTagGroup[]) => {
        const others = prev.filter(group => group.category !== category);
        return saved && saved.tags.length > 0 ? [...others, saved] : others;
      };
      setCitationTags(resync);
      setSavedCitationTags(resync);
    } catch (error) {
      setCompanyDetailError(errorMessage(error, "Não foi possível salvar os termos."));
    }
  };

  /** Preenchimento por IA a partir do nome público.
   *
   * O que ele devolve e o que fazemos com cada coisa:
   *   `is_government_entity`   → o botão de instituição governamental;
   *   apelidos/nomes comuns    → 'APELIDOS E NOMES COMUNS';
   *   slogans/produtos/…       → as demais categorias de citação.
   *
   * `legal_name` e `website` também vêm, e são DESCARTADOS: não há
   * coluna pra eles, e a spec não os pede. Guardar num campo invisível
   * seria pior do que ignorar.
   *
   * **Nada disso persiste sozinho** — preenche o formulário pra pessoa
   * revisar. Sugestão de modelo entrando direto no cadastro é como o
   * erro dele vira o cadastro do cliente. */
  const autofillFromAI = async () => {
    if (!name.trim()) return;
    setAutofilling(true);
    setCompanyDetailError(null);
    setAutofillNotice(null);
    try {
      const sugestao = await apiClient.autofillCompany(token, name.trim());
      setIsGovernment(sugestao.is_government_entity);
      const porCategoria: Record<string, string[]> = {
        "APELIDOS E NOMES COMUNS": [...sugestao.aliases, ...sugestao.common_names],
        SLOGANS: sugestao.slogans,
        PRODUTOS: sugestao.products,
        "PROGRAMAS E INICIATIVAS": sugestao.initiatives,
        "CAMPANHAS PUBLICITARIAS": sugestao.campaigns
      };
      // Junta com o que já existe em vez de substituir: a sugestão é
      // acréscimo, e apagar o que alguém cadastrou à mão seria a pior
      // forma possível de "ajudar".
      const mescladas = Object.entries(porCategoria).map(([category, sugeridos]) => {
        const atuais = citationTags.find(t => t.category === category)?.tags ?? [];
        const vistos = new Set(atuais.map(t => t.toLowerCase()));
        return {
          category,
          tags: [...atuais, ...sugeridos.filter(t => t.trim() && !vistos.has(t.toLowerCase()))]
        };
      });
      setCitationTags(mescladas.filter(g => g.tags.length > 0));
      setAutofillNotice(
        "Sugestões preenchidas abaixo. Revise antes de salvar — nada foi gravado ainda."
      );
    } catch (error) {
      setCompanyDetailError(
        errorMessage(error, "Não foi possível consultar a IA.")
      );
    } finally {
      setAutofilling(false);
    }
  };

  // Mostrar itens desativados. Um estado só pras seções de catálogo —
  // desativar não apaga (o servidor carimba `deleted_on`), e sem isto
  // não haveria como enxergar nem reativar o que saiu de circulação.
  // Fica aqui em cima porque TODOS os `load*` abaixo dependem dele.
  const [showInactive, setShowInactive] = useState(false);
  // A busca de cada lista que cabe em memória (2026-10-03), por aba.
  // Um mapa só, e não um estado por aba: trocar de aba não apaga o que
  // foi digitado nas outras. Canais e Veículos têm busca própria, no
  // servidor — são as duas listas que não cabem.
  const [listSearch, setListSearch] = useState<Record<string, string>>({});

  // Contagem por seção, pro selo da navegação lateral.
  //
  // Conta só o que está ATIVO — o selo responde "tem cadastro em uso
  // aqui?", não "quantas linhas existem no banco". Item desativado
  // continua no banco de propósito (a menção antiga precisa dele), mas
  // não conta como cadastro vivo.
  const [counts, setCounts] = useState<Partial<Record<TabId | "subtemas", number>>>({});

  const loadCounts = async () => {
    // UMA requisição (`GET /settings/counts`), desde 2026-10-07. Antes
    // esta função baixava NOVE listas inteiras mais duas chamadas
    // paginadas só pra escrever um número ao lado de cada aba — onze
    // requisições de uma vez, a cada troca de aba, que no online
    // enfileiravam atrás delas a chamada da tela que a pessoa queria
    // (pedido da dona: "as coisas deveriam carregar por tela, não tudo
    // de uma vez"). Falha deixa os selos como estavam: `navStatus`
    // mostra "—" pro que não tem número, em vez de inventar zero.
    try {
      const c = await apiClient.getSettingsCounts(token);
      setCounts({
        marcas: c.brands,
        concorrentes: c.competitors,
        portavozes: c.spokespersons,
        mensagens: c.key_messages,
        temas: c.theme_groups,
        subtemas: c.themes,
        "publicadores-tipos": c.publisher_tiers,
        publicadores: c.publisher_persons,
        "texto-prompt": c.prompts,
        canais: c.channels,
        veiculos: c.vehicles
      });
    } catch {
      // Selo é decoração; a aba continua funcionando sem ele.
    }
  };

  // Recarrega ao trocar de aba: você acabou de cadastrar algo na aba
  // que está saindo, e o selo dela precisa refletir isso. Enquanto você
  // está DENTRO de uma aba, o selo dela vem da lista já carregada (ver
  // `liveCounts` abaixo), então não há requisição por clique de salvar.
  useEffect(() => {
    void loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const checklistFirstRun = useRef(true);
  useEffect(() => {
    // A primeira passada é a carga ao montar, que o próprio hook já fez.
    if (checklistFirstRun.current) {
      checklistFirstRun.current = false;
      return;
    }
    void reloadChecklist();
  }, [activeTab, reloadChecklist]);

  // Concorrentes
  const [concorrentes, setConcorrentes] = useState<Competitor[]>([]);
  const [concorrentesLoading, setConcorrentesLoading] = useState(false);
  const [concorrentesError, setConcorrentesError] = useState<string | null>(null);
  const [creatingCompetitor, setCreatingCompetitor] = useState(false);
  const [editingCompetitorId, setEditingCompetitorId] = useState<number | null>(null);
  const [newCompetitor, setNewCompetitor] = useState("");
  const [newCompetitorTerms, setNewCompetitorTerms] = useState("");

  // ─── O ALVO do cadastro nas abas de catálogo (2026-10-03) ──────────
  // Temas, grupos de temas, mensagens-chave e porta-vozes existem pra
  // empresa E pra cada concorrente dela (cadastro próprio do
  // concorrente). `null` = a própria empresa. Uma escolha só, comum às
  // quatro abas: quem está montando o catálogo de um concorrente passa
  // de uma aba pra outra sem escolher de novo.
  const [catalogOwner, setCatalogOwner] = useState<number | null>(null);
  const [catalogCompetitors, setCatalogCompetitors] = useState<Competitor[]>([]);
  const [ownerBrands, setOwnerBrands] = useState<BrandOut[]>([]);
  const catalogApi = useMemo(() => apiClient.onCompetitor(catalogOwner), [catalogOwner]);
  const onCatalogTab = CATALOG_OWNER_TABS.includes(activeTab);

  useEffect(() => {
    if (!onCatalogTab) return;
    let alive = true;
    apiClient
      .listCompetitors(token)
      .then(rows => {
        if (alive) setCatalogCompetitors(rows);
      })
      .catch(() => {
        // Sem a lista, o seletor não aparece e as abas seguem na empresa.
      });
    return () => {
      alive = false;
    };
  }, [token, onCatalogTab]);

  useEffect(() => {
    if (catalogOwner === null) {
      setOwnerBrands([]);
      return;
    }
    let alive = true;
    apiClient
      .onCompetitor(catalogOwner)
      .listBrands(token)
      .then(rows => {
        if (alive) setOwnerBrands(rows);
      })
      .catch(() => {
        if (alive) setOwnerBrands([]);
      });
    return () => {
      alive = false;
    };
  }, [token, catalogOwner]);

  const loadCompetitors = async () => {
    setConcorrentesLoading(true);
    setConcorrentesError(null);
    try {
      setConcorrentes(await apiClient.listCompetitors(token, showInactive));
    } catch (error) {
      setConcorrentesError(errorMessage(error, "Não foi possível carregar os concorrentes."));
    } finally {
      setConcorrentesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "concorrentes") return;
    void loadCompetitors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive]);

  const resetCompetitorForm = () => {
    setOpenForm(null);
    setEditingCompetitorId(null);
    setNewCompetitor("");
    setNewCompetitorTerms("");
  };

  const openNewCompetitor = () => {
    resetCompetitorForm();
    setConcorrentesError(null);
    setOpenForm("competitor");
  };

  const startEditCompetitor = (c: Competitor) => {
    setConcorrentesError(null);
    setOpenForm("competitor");
    setEditingCompetitorId(c.id);
    setNewCompetitor(c.name);
    setNewCompetitorTerms(c.terms || "");
  };

  const competitorRequired: RequiredField[] = [
    { label: "Nome", filled: !!newCompetitor.trim() },
    { label: "Termos", filled: !!newCompetitorTerms.trim() },
  ];
  const saveCompetitor = async () => {
    // Termos são OBRIGATÓRIOS aqui, diferente da marca da própria
    // empresa: o matching de concorrente pula marca sem termos, então
    // um concorrente sem eles é cadastro que não faz nada.
    if (missingFields(competitorRequired).length > 0) return;
    setCreatingCompetitor(true);
    setConcorrentesError(null);
    try {
      const payload = { name: newCompetitor.trim(), terms: newCompetitorTerms.trim() };
      if (editingCompetitorId !== null) {
        await apiClient.updateCompetitor(token, editingCompetitorId, payload);
      } else {
        await apiClient.createCompetitor(token, payload);
      }
      resetCompetitorForm();
      await loadCompetitors();
    } catch (error) {
      setConcorrentesError(errorMessage(error, "Não foi possível salvar o concorrente."));
    } finally {
      setCreatingCompetitor(false);
    }
  };

  const setCompetitorActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        apiClient.updateCompetitor(token, id, { is_active: true }),
      deactivate: () => apiClient.deactivateCompetitor(token, id),
      reload: loadCompetitors,
      setError: setConcorrentesError,
      fallback: "Não foi possível alterar o concorrente."
    });

  // Porta-vozes
  const [spokespersons, setSpokespersons] = useState<Spokesperson[]>([]);
  const [brands, setBrands] = useState<BrandOut[]>([]);
  const [spokespersonsLoading, setSpokespersonsLoading] = useState(false);
  const [spokespersonsError, setSpokespersonsError] = useState<string | null>(null);
  const [creatingSpokesperson, setCreatingSpokesperson] = useState(false);
  const [editingSpokespersonId, setEditingSpokespersonId] = useState<number | null>(null);
  const [newSpokespersonName, setNewSpokespersonName] = useState("");
  const [newSpokespersonBrandIds, setNewSpokespersonBrandIds] = useState<number[]>([]);
  const [newSpokespersonCountsCena, setNewSpokespersonCountsCena] = useState(true);
  const [newSpokespersonValidUntil, setNewSpokespersonValidUntil] = useState("");
  const [newVariation, setNewVariation] = useState("");
  const [newSpokespersonVariations, setNewSpokespersonVariations] = useState<string[]>([]);

  const loadSpokespersons = async () => {
    setSpokespersonsLoading(true);
    setSpokespersonsError(null);
    try {
      setSpokespersons(await catalogApi.listSpokespersons(token, { includeInactive: showInactive }));
    } catch (error) {
      setSpokespersonsError(errorMessage(error, "Não foi possível carregar os porta-vozes."));
    } finally {
      setSpokespersonsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "portavozes") return;
    resetSpokespersonForm();
    void loadSpokespersons();
    if (brands.length === 0) void loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive, catalogOwner]);

  useEffect(() => {
    if ((["marcas", "temas", "mensagens"].includes(activeTab) || isPromptTab(activeTab)) && brands.length === 0) {
      void loadBrands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /** Nome da marca pelo id — as quatro listagens por marca (tema,
   * mensagem-chave, porta-voz, termo) vêm com `brand_id` cru, e mostrar
   * o número na tela não ajuda ninguém.
   *
   * Por isso `brands` carrega SEMPRE com desativadas (a marca saiu de
   * circulação, os filhos dela continuam listados) — quem filtra é
   * `activeBrands`, logo abaixo. Trocar a ordem faria todo tema de
   * marca desativada exibir "Marca 7". */
  const brandName = (id: number) =>
    brands.find(b => b.id === id)?.name ??
    ownerBrands.find(b => b.id === id)?.name ??
    `Marca ${id}`;

  /** As marcas que podem ser ESCOLHIDAS. Desativada não entra: o item
   * novo nasceria invisível (fora do matching e das listas), e o
   * servidor recusa com 409 de qualquer jeito — oferecer seria
   * prometer o que não vai dar certo. */
  const activeBrands = brands.filter(b => b.is_active);
  // As marcas que os formulários de catálogo oferecem: as da empresa,
  // ou as do concorrente quando ele é o alvo do cadastro.
  const pickerBrands =
    catalogOwner === null ? activeBrands : ownerBrands.filter(b => b.is_active);

  /** O que a ABA de marcas lista — respeita o "mostrar desativados".
   * Distinto de `activeBrands` (escolha) e de `brands` (resolvedor de
   * nome): são três perguntas diferentes sobre a mesma lista. */
  const visibleBrands = showInactive ? brands : activeBrands;

  // Marcas da Empresa
  const [newBrandName, setNewBrandName] = useState("");
  const [newBrandTerms, setNewBrandTerms] = useState("");
  const [newBrandIsMain, setNewBrandIsMain] = useState(false);
  const [creatingBrand, setCreatingBrand] = useState(false);
  const [brandsError, setBrandsError] = useState<string | null>(null);
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);

  const loadBrands = async () => {
    try {
      // Com desativadas SEMPRE — ver `brandName`.
      setBrands(await apiClient.listBrands(token, { includeInactive: true }));
    } catch (error) {
      setBrandsError(errorMessage(error, "Não foi possível carregar as marcas."));
    }
  };

  const resetBrandForm = () => {
    setOpenForm(null);
    setEditingBrandId(null);
    setNewBrandName("");
    setNewBrandTerms("");
    setNewBrandIsMain(false);
  };

  /** Desativa ou reativa a marca. Na principal nem chega aqui — o
   * botão está desabilitado —, mas se chegasse o servidor recusa com
   * 409 e a mensagem dele é a que aparece. */
  const toggleBrandActive = async (brand: BrandOut, active: boolean) => {
    setBrandsError(null);
    try {
      if (active) {
        await apiClient.updateBrand(token, brand.id, { isActive: true });
      } else {
        await apiClient.deactivateBrand(token, brand.id);
      }
      await loadBrands();
    } catch (error) {
      setBrandsError(
        error instanceof ApiError
          ? error.message
          : "Não foi possível alterar a marca."
      );
    }
  };

  const openNewBrand = () => {
    resetBrandForm();
    setBrandsError(null);
    setOpenForm("brand");
  };

  const startEditBrand = (brand: BrandOut) => {
    setBrandsError(null);
    setOpenForm("brand");
    setEditingBrandId(brand.id);
    setNewBrandName(brand.name);
    setNewBrandTerms(brand.terms || "");
    setNewBrandIsMain(brand.is_main_brand);
  };

  const brandRequired: RequiredField[] = [
    { label: "Nome da marca", filled: !!newBrandName.trim() },
  ];
  const addBrand = async () => {
    if (missingFields(brandRequired).length > 0) return;
    setCreatingBrand(true);
    setBrandsError(null);
    try {
      const payload = {
        name: newBrandName.trim(),
        // `null` LIMPA os termos; `undefined` não mexe. O `PATCH` é
        // parcial de verdade no servidor (`model_fields_set`), então a
        // diferença entre os dois importa.
        terms: newBrandTerms.trim() || null,
        is_main_brand: newBrandIsMain
      };
      if (editingBrandId !== null) {
        await apiClient.updateBrand(token, editingBrandId, payload);
      } else {
        await apiClient.createBrand(token, payload);
      }
      resetBrandForm();
      // Recarrega em vez de mexer na lista em memória: marcar uma marca
      // como principal REBAIXA a anterior no servidor, e a linha
      // rebaixada não vem na resposta.
      await loadBrands();
    } catch (error) {
      setBrandsError(errorMessage(error, "Não foi possível salvar a marca."));
    } finally {
      setCreatingBrand(false);
    }
  };

  // Grupos de Temas Monitorados
  const [themeGroups, setThemeGroups] = useState<ThemeGroup[]>([]);
  const [themeGroupsError, setThemeGroupsError] = useState<string | null>(null);
  const [newThemeGroupName, setNewThemeGroupName] = useState("");
  const [newThemeGroupWeight, setNewThemeGroupWeight] = useState("");
  const [creatingThemeGroup, setCreatingThemeGroup] = useState(false);
  const [editingThemeGroupId, setEditingThemeGroupId] = useState<number | null>(null);
  // Temas criados junto com o grupo. `null` nas marcas = "ainda não mexeu":
  // com UMA marca só no picker ela já nasce marcada; com várias, nenhuma.
  const [newGroupThemeNames, setNewGroupThemeNames] = useState("");
  const [newGroupThemeBrandIds, setNewGroupThemeBrandIds] = useState<number[] | null>(null);
  const [themeGroupNotice, setThemeGroupNotice] = useState<string | null>(null);

  const loadThemeGroups = async () => {
    try {
      setThemeGroups(await catalogApi.listThemeGroups(token, showInactive));
    } catch (error) {
      setThemeGroupsError(errorMessage(error, "Não foi possível carregar os temas."));
    }
  };

  const resetThemeGroupForm = () => {
    setOpenForm(null);
    setEditingThemeGroupId(null);
    setNewThemeGroupName("");
    setNewThemeGroupWeight("");
    setNewGroupThemeNames("");
    setNewGroupThemeBrandIds(null);
  };

  const openNewThemeGroup = () => {
    resetThemeGroupForm();
    setThemeGroupsError(null);
    setOpenForm("themeGroup");
  };

  const startEditThemeGroup = (g: ThemeGroup) => {
    setThemeGroupsError(null);
    setOpenForm("themeGroup");
    setEditingThemeGroupId(g.id);
    setNewThemeGroupName(g.name);
    setNewThemeGroupWeight(g.weight === null ? "" : String(g.weight));
  };

  // Os temas só existem na CRIAÇÃO; na edição acrescentar tema a grupo
  // existente continua sendo a aba Temas.
  const groupThemeNames = editingThemeGroupId === null ? splitEntries(newGroupThemeNames) : [];
  const groupThemeBrandIds =
    newGroupThemeBrandIds ?? (pickerBrands.length === 1 ? [pickerBrands[0].id] : []);
  const groupThemeCount = groupThemeNames.length * groupThemeBrandIds.length;
  const themeGroupRequired: RequiredField[] = [
    { label: "Nome", filled: !!newThemeGroupName.trim() },
    // Só cobra marca quando há subtema a criar: tema sem subtemas segue
    // exigindo apenas o nome.
    { label: "Marcas dos subtemas", filled: groupThemeNames.length === 0 || groupThemeBrandIds.length > 0 },
  ];
  const addThemeGroup = async () => {
    if (missingFields(themeGroupRequired).length > 0) return;
    setCreatingThemeGroup(true);
    setThemeGroupsError(null);
    setThemeGroupNotice(null);
    const themeFailures: string[] = [];
    let created: ThemeGroup | null = null;
    try {
      const payload = {
        name: newThemeGroupName.trim(),
        weight: newThemeGroupWeight.trim() === "" ? null : Number(newThemeGroupWeight)
      };
      if (editingThemeGroupId !== null) {
        await catalogApi.updateThemeGroup(token, editingThemeGroupId, payload);
      } else {
        created = await catalogApi.createThemeGroup(token, payload);
      }
      // Os temas do grupo, um por nome e por marca, em sequência como o
      // `addTheme`. Falha de um não para os outros: o grupo e o que já
      // entrou ficam, e a mensagem final diz o que faltou.
      if (created && groupThemeNames.length > 0) {
        for (const name of groupThemeNames) {
          for (const brandId of groupThemeBrandIds) {
            try {
              await catalogApi.createTheme(token, {
                brand_id: brandId,
                theme_group_id: created.id,
                name,
                terms: null,
                classification_mode: "both",
                deterministic_scope: "both",
                detection_assumptions: null,
                non_detection_assumptions: null
              });
            } catch (error) {
              const brand = brands.find(b => b.id === brandId)?.name ?? `marca ${brandId}`;
              themeFailures.push(`${name} (${brand}): ${errorMessage(error, "não foi possível criar")}`);
            }
          }
        }
      }
      resetThemeGroupForm();
      // A recarga vem ANTES de gravar a falha: a recarga não limpa o erro
      // de propósito, e gravar depois garante que a recusa não some.
      await loadThemeGroups();
      // Os subtemas criados junto moram na outra lista.
      if (groupThemeNames.length > 0) await loadThemes();
      if (themeFailures.length > 0) {
        setThemeGroupsError(
          `Tema criado. ${themeFailures.length} subtema(s) não entraram: ${themeFailures.join("; ")}`
        );
      } else if (groupThemeCount > 0) {
        setThemeGroupNotice(`Tema e ${groupThemeCount} ${groupThemeCount === 1 ? "subtema criado" : "subtemas criados"}`);
      }
    } catch (error) {
      setThemeGroupsError(errorMessage(error, "Não foi possível salvar o tema."));
    } finally {
      setCreatingThemeGroup(false);
    }
  };

  const setThemeGroupActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        catalogApi.updateThemeGroup(token, id, { is_active: true }),
      deactivate: () => catalogApi.deactivateThemeGroup(token, id),
      reload: loadThemeGroups,
      setError: setThemeGroupsError,
      fallback: "Não foi possível alterar o tema."
    });

  // Temas Monitorados
  const [themes, setThemes] = useState<Theme[]>([]);
  const [themesError, setThemesError] = useState<string | null>(null);
  const [newThemeName, setNewThemeName] = useState("");
  const [newThemeBrandIds, setNewThemeBrandIds] = useState<number[]>([]);
  const [newThemeGroupId, setNewThemeGroupId] = useState("");
  const [newThemeTerms, setNewThemeTerms] = useState("");
  const [newThemeDetection, setNewThemeDetection] = useState("");
  const [newThemeNonDetection, setNewThemeNonDetection] = useState("");
  // Só determinístico / só IA / os dois, e (nos dois) a fonte do termo.
  const [newThemeMode, setNewThemeMode] = useState<ThemeClassificationMode>("both");
  const [newThemeScope, setNewThemeScope] = useState<DeterministicScope>("both");
  const [creatingTheme, setCreatingTheme] = useState(false);
  const [editingThemeId, setEditingThemeId] = useState<number | null>(null);
  // A lista agrupada por tema: o filtro de marca, quais blocos a pessoa
  // abriu (um mapa pra lista parada e outro enquanto há filtro, que abre
  // sozinha o que tem resultado — o segundo zera a cada filtro novo) e
  // de quais temas ela pediu "ver todos".
  const [themeBrandFilter, setThemeBrandFilter] = useState<number | null>(null);
  const [openIdle, setOpenIdle] = useState<Record<string, boolean>>({});
  const [openFiltered, setOpenFiltered] = useState<Record<string, boolean>>({});
  const [showAllOf, setShowAllOf] = useState<Record<string, boolean>>({});
  // Quantos publicadores/veículos atribuem cada tema — só o selo da lista.
  const [themeRuleCounts, setThemeRuleCounts] = useState<Record<string, number>>({});

  const loadThemeRuleCounts = async () => {
    try {
      setThemeRuleCounts(await catalogApi.countThemeAssignmentTargets(token));
    } catch {
      // O selo é enfeite: sem ele a lista continua certa.
    }
  };

  const loadThemes = async () => {
    void loadThemeRuleCounts();
    try {
      setThemes(await catalogApi.listThemes(token, { includeInactive: showInactive }));
    } catch (error) {
      setThemesError(errorMessage(error, "Não foi possível carregar os subtemas."));
    }
  };

  useEffect(() => {
    if (activeTab !== "temas") return;
    resetThemeForm();
    resetThemeGroupForm();
    setThemeGroupNotice(null);
    // Outro dono do cadastro tem outras marcas: o filtro de marca não vale.
    setThemeBrandFilter(null);
    setOpenFiltered({});
    const prefill = prefillRef.current;
    // O deep link `?novo=` (fila de correção) só abre o pop-up pra quem
    // pode cadastrar; abaixo de Especialista é consumido e ignorado
    // (2026-10-24).
    if (prefill) {
      prefillRef.current = null;
      if (canEdit) {
        setNewThemeName(prefill.name);
        if (prefill.brandId !== null) setNewThemeBrandIds([prefill.brandId]);
        setOpenForm("theme");
      }
    }
    void loadThemes();
    void loadThemeGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive, catalogOwner]);

  // A importação por planilha escreve nas duas listas sem passar pelos
  // formulários; quando a execução termina o painel avisa por aqui. Um
  // contador (e não o callback chamando as cargas direto) porque o painel
  // precisa de uma função ESTÁVEL: com uma nova a cada render, o efeito
  // dele que compara "a execução ativa sumiu" avisaria a cada render.
  const [themesRefresh, setThemesRefresh] = useState(0);
  const onThemesImported = useCallback(() => setThemesRefresh(current => current + 1), []);
  useEffect(() => {
    if (activeTab !== "temas" || themesRefresh === 0) return;
    void loadThemes();
    void loadThemeGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themesRefresh]);

  // "Baixar planilha": os temas e os subtemas (duas abas) da configuração.
  const [downloadingThemes, setDownloadingThemes] = useState(false);
  const downloadThemes = async () => {
    setDownloadingThemes(true);
    setThemesError(null);
    try {
      downloadBlob(
        await apiClient.downloadCompanyConfig(token, undefined, {
          sheets: ["theme_groups", "themes"]
        }),
        configFilename(companyName, "temas")
      );
    } catch (error) {
      setThemesError(errorMessage(error, "Não foi possível baixar os temas e subtemas."));
    } finally {
      setDownloadingThemes(false);
    }
  };

  const resetThemeForm = () => {
    setOpenForm(null);
    setEditingThemeId(null);
    setNewThemeName("");
    setNewThemeBrandIds([]);
    setNewThemeGroupId("");
    setNewThemeTerms("");
    setNewThemeDetection("");
    setNewThemeNonDetection("");
    setNewThemeMode("both");
    setNewThemeScope("both");
  };

  const openNewTheme = () => {
    resetThemeForm();
    setThemesError(null);
    setOpenForm("theme");
  };

  /** "+ Subtema" no bloco do tema: o pop-up de novo subtema já com o tema. */
  const openNewThemeIn = (groupId: number) => {
    resetThemeForm();
    setThemesError(null);
    setNewThemeGroupId(String(groupId));
    setOpenForm("theme");
  };

  const startEditTheme = (t: Theme) => {
    setThemesError(null);
    setOpenForm("theme");
    setEditingThemeId(t.id);
    setNewThemeName(t.name);
    setNewThemeBrandIds([t.brand_id]);
    setNewThemeGroupId(String(t.theme_group_id));
    setNewThemeTerms(t.terms || "");
    setNewThemeDetection(t.detection_assumptions || "");
    setNewThemeNonDetection(t.non_detection_assumptions || "");
    setNewThemeMode(t.classification_mode);
    setNewThemeScope(t.deterministic_scope);
  };

  const themeRequired: RequiredField[] = [
    { label: "Nome do subtema", filled: !!newThemeName.trim() },
    { label: "Marcas", filled: newThemeBrandIds.length > 0 },
    { label: "Tema do subtema", filled: !!newThemeGroupId },
    ...(newThemeMode === "deterministic"
      ? [{ label: "Termos (só determinístico)", filled: !!newThemeTerms.trim() }]
      : []),
  ];
  const addTheme = async () => {
    if (missingFields(themeRequired).length > 0) return;
    setCreatingTheme(true);
    setThemesError(null);
    try {
      const comum = {
        theme_group_id: Number(newThemeGroupId),
        name: newThemeName.trim(),
        terms: newThemeTerms.trim() || null,
        classification_mode: newThemeMode,
        // Fora do modo "os dois" a fonte não decide nada; o servidor grava
        // `both` de qualquer jeito, e mandar o mesmo evita a dúvida.
        deterministic_scope: newThemeMode === "both" ? newThemeScope : "both",
        detection_assumptions: newThemeDetection.trim() || null,
        non_detection_assumptions: newThemeNonDetection.trim() || null
      };
      if (editingThemeId !== null) {
        await catalogApi.updateTheme(token, editingThemeId, {
          ...comum,
          brand_id: newThemeBrandIds[0]
        });
      } else {
        // Uma linha POR MARCA — o servidor cria um tema de cada vez, e
        // é assim que o domínio manda: "um tema X aplicado a duas
        // marcas vira dois temas X". Sequencial de propósito: se a
        // segunda falhar (nome repetido na marca, por exemplo), a
        // primeira já criada aparece no recarregamento e a mensagem de
        // erro diz o que faltou.
        for (const brandId of newThemeBrandIds) {
          await catalogApi.createTheme(token, { ...comum, brand_id: brandId });
        }
      }
      resetThemeForm();
      await loadThemes();
    } catch (error) {
      setThemesError(errorMessage(error, "Não foi possível salvar o subtema."));
      await loadThemes();
    } finally {
      setCreatingTheme(false);
    }
  };

  const setThemeActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        catalogApi.updateTheme(token, id, { is_active: true }),
      deactivate: () => catalogApi.deactivateTheme(token, id),
      reload: loadThemes,
      setError: setThemesError,
      fallback: "Não foi possível alterar o subtema."
    });

  // Contador de "o catálogo mudou por fora". A importação por planilha
  // escreve nas DUAS tabelas sem passar pelos formulários desta tela, e
  // quem avisa é o painel de import quando a execução termina. Um
  // contador (e não chamar `loadChannels` direto de lá) porque o
  // callback precisa ser estável entre renders — passar uma função que
  // captura `channelPage`/`channelQuery` recarregaria a página errada.
  const [catalogRefresh, setCatalogRefresh] = useState(0);
  const onCatalogImported = useCallback(
    () => setCatalogRefresh(current => current + 1),
    []
  );

  // Canais Monitorados
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelSource, setNewChannelSource] = useState<"network" | "press">("network");
  const [newChannelTier, setNewChannelTier] = useState("");
  const [newChannelWeight, setNewChannelWeight] = useState("");
  const [newChannelIgnoreTerms, setNewChannelIgnoreTerms] = useState(false);
  const [newChannelIgnored, setNewChannelIgnored] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<number | null>(null);
  // Busca/página desta aba. `channelTotal` é o do FILTRO, não do
  // catálogo, e é ele que a paginação e o rodapé "1–24 de N" leem.
  const [channelSearch, setChannelSearch] = useState("");
  const [channelQuery, setChannelQuery] = useState("");
  const [channelPage, setChannelPage] = useState(1);
  const [channelTotal, setChannelTotal] = useState(0);
  // Os apelidos do canal em edição, e o campo de digitar um novo. A
  // lista vive aqui (e não dentro do form) porque cada operação devolve
  // o canal inteiro — a tela nunca monta a lista por conta.
  const [channelAliases, setChannelAliases] = useState<CatalogAlias[]>([]);
  const [newChannelAlias, setNewChannelAlias] = useState("");
  // O botão "Criar canais padrão" (2026-10-28): ocupado enquanto o
  // servidor cria, e a frase do que aconteceu depois.
  const [standardChannelsBusy, setStandardChannelsBusy] = useState(false);
  const [channelsNotice, setChannelsNotice] = useState<string | null>(null);

  const loadChannels = async () => {
    try {
      const page = await apiClient.listChannels(token, {
        q: channelQuery,
        includeInactive: showInactive,
        page: channelPage,
        size: CATALOG_PAGE_SIZE
      });
      setChannels(page.items);
      setChannelTotal(page.total);
    } catch (error) {
      setChannelsError(errorMessage(error, "Não foi possível carregar os canais."));
    }
  };

  useEffect(() => {
    if (activeTab !== "canais") return;
    void loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive, channelQuery, channelPage, catalogRefresh]);

  /** Aplica a busca. Volta pra página 1 de propósito: procurar estando
   *  na página 7 de outro filtro pediria uma página que talvez nem
   *  exista no resultado novo, e a lista voltaria vazia. */
  const searchChannels = () => {
    setChannelPage(1);
    setChannelQuery(channelSearch);
  };

  const resetChannelForm = () => {
    setOpenForm(null);
    setEditingChannelId(null);
    setNewChannelName("");
    setNewChannelSource("network");
    setNewChannelTier("");
    setNewChannelWeight("");
    setNewChannelIgnoreTerms(false);
    setNewChannelIgnored(false);
    setChannelAliases([]);
    setNewChannelAlias("");
  };

  const openNewChannel = () => {
    resetChannelForm();
    setChannelsError(null);
    setChannelsNotice(null);
    setOpenForm("channel");
  };

  /** Os canais de rede da base que faltam. A recarga vem ANTES do aviso
   *  (regra de 2026-10-09): uma recarga que limpasse a mensagem depois
   *  apagaria o que a pessoa precisa ler. */
  const createStandardChannels = async () => {
    setStandardChannelsBusy(true);
    setChannelsError(null);
    setChannelsNotice(null);
    try {
      const result = await apiClient.createStandardNetworkChannels(token);
      await loadChannels();
      setChannelsNotice(standardChannelsNotice(result));
    } catch (error) {
      setChannelsError(errorMessage(error, "Não foi possível criar os canais padrão."));
    } finally {
      setStandardChannelsBusy(false);
    }
  };

  const startEditChannel = (c: Channel) => {
    setChannelsError(null);
    setOpenForm("channel");
    setEditingChannelId(c.id);
    setNewChannelName(c.name);
    setNewChannelSource(c.post_source);
    setNewChannelTier(c.tier || "");
    // `?? ""` e não `|| ""`: peso `0` é a empresa dizendo "este canal
    // não pontua", e com `||` o campo abriria vazio e o save o apagaria.
    setNewChannelWeight(c.weight === null || c.weight === undefined ? "" : String(c.weight));
    setNewChannelIgnoreTerms(c.ignore_term_verification);
    setNewChannelIgnored(c.ignored);
    setChannelAliases(c.aliases);
    setNewChannelAlias("");
  };

  /** Apelido é gravado NA HORA, fora do "Salvar alterações" do
   *  formulário — e por isso só aparece em modo edição: um canal que
   *  ainda não existe não tem id pra pendurar o apelido. Misturar as
   *  duas coisas exigiria segurar apelidos em memória e gravá-los depois
   *  do POST, com um estado intermediário em que metade salvou. */
  const addChannelAlias = async () => {
    const apelidos = splitEntries(newChannelAlias);
    if (editingChannelId === null || apelidos.length === 0) return;
    setChannelsError(null);
    // Um por vez e na ordem (a API tem cerca de taxa). Se um falhar, o
    // campo fica só com o que não entrou — o que já gravou não é reenviado.
    const { added, remaining, error } = await addSequentially(apelidos, async apelido => {
      const updated = await apiClient.addChannelAlias(token, editingChannelId, apelido);
      setChannelAliases(updated.aliases);
    });
    setNewChannelAlias(remainingToDraft(remaining));
    if (error) {
      setChannelsError(errorMessage(error, "Não foi possível adicionar o apelido."));
    }
    if (added.length > 0) await loadChannels();
  };

  const removeChannelAlias = async (aliasId: number) => {
    if (editingChannelId === null) return;
    setChannelsError(null);
    try {
      const updated = await apiClient.removeChannelAlias(
        token,
        editingChannelId,
        aliasId
      );
      setChannelAliases(updated.aliases);
      await loadChannels();
    } catch (error) {
      setChannelsError(errorMessage(error, "Não foi possível remover o apelido."));
    }
  };

  const channelRequired: RequiredField[] = [
    { label: "Nome", filled: !!newChannelName.trim() },
  ];
  const addChannel = async () => {
    if (missingFields(channelRequired).length > 0) return;
    // Vazio = "usa o peso padrão da plataforma"; `0` = "não pontua".
    // O idioma `|| null` do resto do formulário serve pra texto e
    // MATARIA o zero aqui; e `Number("abc")` é `NaN`, que o
    // `JSON.stringify` serializa como `null` — ou seja, digitar lixo
    // apagaria o peso sem erro nenhum.
    const rawWeight = newChannelWeight.trim();
    const weight = rawWeight === "" ? null : Number(rawWeight);
    if (weight !== null && (!Number.isFinite(weight) || weight < 0)) {
      setChannelsError("O peso do canal precisa ser um número maior ou igual a zero.");
      return;
    }
    setCreatingChannel(true);
    setChannelsError(null);
    try {
      const payload = {
        name: newChannelName.trim(),
        post_source: newChannelSource,
        tier: newChannelTier.trim() || null,
        // Imprensa pontua por tier de VEÍCULO, não por peso de canal —
        // mandar peso aqui gravaria um número inerte.
        weight: newChannelSource === "press" ? null : weight,
        ignore_term_verification: newChannelIgnoreTerms,
        ignored: newChannelIgnored
      };
      if (editingChannelId !== null) {
        await apiClient.updateChannel(token, editingChannelId, payload);
      } else {
        await apiClient.createChannel(token, payload);
      }
      resetChannelForm();
      await loadChannels();
    } catch (error) {
      setChannelsError(errorMessage(error, "Não foi possível salvar o canal."));
    } finally {
      setCreatingChannel(false);
    }
  };

  const setChannelActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        apiClient.updateChannel(token, id, { is_active: true }),
      deactivate: () => apiClient.deactivateChannel(token, id),
      reload: loadChannels,
      setError: setChannelsError,
      fallback: "Não foi possível alterar o canal."
    });

  // Veículos (imprensa/TV/rádio)
  //
  // Tabela SEPARADA de canal, e não um `post_source` dele: canal com
  // `post_source='press'` é herança do schema legado. Veículo tem
  // `tier_key` (que alimenta o `p_veiculo` do CENA) e `content_optional`
  // (paywall), que canal não tem.
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);
  const [newVehicleName, setNewVehicleName] = useState("");
  const [newVehicleKind, setNewVehicleKind] = useState<VehicleKind>(VEHICLE_KIND_DEFAULT);
  const [newVehicleTier, setNewVehicleTier] = useState("");
  const [newVehicleContentOptional, setNewVehicleContentOptional] = useState(false);
  const [newVehicleIgnored, setNewVehicleIgnored] = useState(false);
  const [newVehicleIgnoreTerms, setNewVehicleIgnoreTerms] = useState(false);
  // UF, região, país e preço base: texto no formulário, `null` no fio
  // quando vazio (nunca string vazia).
  const [newVehicleState, setNewVehicleState] = useState("");
  const [newVehicleRegion, setNewVehicleRegion] = useState("");
  const [newVehicleCountry, setNewVehicleCountry] = useState("");
  const [newVehicleBasePrice, setNewVehicleBasePrice] = useState("");
  const [creatingVehicle, setCreatingVehicle] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState<number | null>(null);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehicleTotal, setVehicleTotal] = useState(0);
  const [vehicleAliases, setVehicleAliases] = useState<CatalogAlias[]>([]);
  const [newVehicleAlias, setNewVehicleAlias] = useState("");

  const loadVehicles = async () => {
    try {
      const page = await apiClient.listVehicles(token, {
        q: vehicleQuery,
        includeInactive: showInactive,
        page: vehiclePage,
        size: CATALOG_PAGE_SIZE
      });
      setVehicles(page.items);
      setVehicleTotal(page.total);
    } catch (error) {
      setVehiclesError(errorMessage(error, "Não foi possível carregar os veículos."));
    }
  };

  useEffect(() => {
    if (activeTab !== "veiculos") return;
    void loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive, vehicleQuery, vehiclePage, catalogRefresh]);

  /** Ver `searchChannels`. */
  const searchVehicles = () => {
    setVehiclePage(1);
    setVehicleQuery(vehicleSearch);
  };

  const resetVehicleForm = () => {
    setOpenForm(null);
    setEditingVehicleId(null);
    setNewVehicleName("");
    setNewVehicleKind(VEHICLE_KIND_DEFAULT);
    setNewVehicleTier("");
    setNewVehicleContentOptional(false);
    setNewVehicleIgnored(false);
    setNewVehicleIgnoreTerms(false);
    setNewVehicleState("");
    setNewVehicleRegion("");
    setNewVehicleCountry("");
    setNewVehicleBasePrice("");
    setVehicleAliases([]);
    setNewVehicleAlias("");
  };

  const openNewVehicle = () => {
    resetVehicleForm();
    setVehiclesError(null);
    setOpenForm("vehicle");
  };

  const startEditVehicle = (v: Vehicle) => {
    setVehiclesError(null);
    setOpenForm("vehicle");
    setEditingVehicleId(v.id);
    setNewVehicleName(v.name);
    setNewVehicleKind(v.vehicle_kind);
    setNewVehicleTier(v.tier_key || "");
    setNewVehicleContentOptional(v.content_optional);
    setNewVehicleIgnored(v.ignored);
    setNewVehicleIgnoreTerms(v.ignore_term_verification);
    setNewVehicleState(v.state ?? "");
    setNewVehicleRegion(v.region ?? "");
    setNewVehicleCountry(v.country ?? "");
    setNewVehicleBasePrice(v.base_price === null ? "" : String(v.base_price));
    setVehicleAliases(v.aliases);
    setNewVehicleAlias("");
  };

  /** Ver `addChannelAlias` — mesma regra, mesmo motivo. */
  const addVehicleAlias = async () => {
    const apelidos = splitEntries(newVehicleAlias);
    if (editingVehicleId === null || apelidos.length === 0) return;
    setVehiclesError(null);
    const { added, remaining, error } = await addSequentially(apelidos, async apelido => {
      const updated = await apiClient.addVehicleAlias(token, editingVehicleId, apelido);
      setVehicleAliases(updated.aliases);
    });
    setNewVehicleAlias(remainingToDraft(remaining));
    if (error) {
      setVehiclesError(errorMessage(error, "Não foi possível adicionar o apelido."));
    }
    if (added.length > 0) await loadVehicles();
  };

  const removeVehicleAlias = async (aliasId: number) => {
    if (editingVehicleId === null) return;
    setVehiclesError(null);
    try {
      const updated = await apiClient.removeVehicleAlias(
        token,
        editingVehicleId,
        aliasId
      );
      setVehicleAliases(updated.aliases);
      await loadVehicles();
    } catch (error) {
      setVehiclesError(errorMessage(error, "Não foi possível remover o apelido."));
    }
  };

  const vehicleRequired: RequiredField[] = [
    { label: "Nome", filled: !!newVehicleName.trim() },
  ];
  const addVehicle = async () => {
    if (missingFields(vehicleRequired).length > 0) return;
    setCreatingVehicle(true);
    setVehiclesError(null);
    try {
      const payload = {
        name: newVehicleName.trim(),
        vehicle_kind: newVehicleKind,
        tier_key: newVehicleTier.trim() || null,
        content_optional: newVehicleContentOptional,
        ignored: newVehicleIgnored,
        ignore_term_verification: newVehicleIgnoreTerms,
        state: newVehicleState.trim() || null,
        region: newVehicleRegion.trim() || null,
        country: newVehicleCountry.trim() || null,
        base_price: newVehicleBasePrice.trim() ? Number(newVehicleBasePrice) : null
      };
      if (editingVehicleId !== null) {
        await apiClient.updateVehicle(token, editingVehicleId, payload);
      } else {
        await apiClient.createVehicle(token, payload);
      }
      resetVehicleForm();
      await loadVehicles();
    } catch (error) {
      setVehiclesError(errorMessage(error, "Não foi possível salvar o veículo."));
    } finally {
      setCreatingVehicle(false);
    }
  };

  const setVehicleActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        apiClient.updateVehicle(token, id, { is_active: true }),
      deactivate: () => apiClient.deactivateVehicle(token, id),
      reload: loadVehicles,
      setError: setVehiclesError,
      fallback: "Não foi possível alterar o veículo."
    });

  // Porte do publicador — as faixas de seguidores que viram `p_publi`.
  //
  // Uma regra aqui não é preferência de tela, é o que o cálculo exige:
  // faixas não podem se sobrepor. O motor usa a primeira que casar,
  // então sobreposição faria a nota depender da ordem de cadastro. O
  // servidor recusa com 409.
  //
  // A faixa de QUEDA é a marcada com `is_default`: é onde cai a
  // publicação de rede sem número de seguidores (ou com zero) e a que
  // não casou range nenhum. Uma por empresa; marcar outra rebaixa a
  // anterior no servidor. Nada obriga a ter uma — sem ela essas
  // publicações só ficam com `p_publi` zero. A tela avisa; não trava.
  const [publisherTiers, setPublisherTiers] = useState<PublisherTier[]>([]);
  const [tiersError, setTiersError] = useState<string | null>(null);
  const [newTierName, setNewTierName] = useState("");
  const [newTierWeight, setNewTierWeight] = useState("");
  const [newTierMin, setNewTierMin] = useState("");
  const [newTierMax, setNewTierMax] = useState("");
  const [newTierDefault, setNewTierDefault] = useState(false);
  const [creatingTier, setCreatingTier] = useState(false);
  const [editingTierId, setEditingTierId] = useState<number | null>(null);

  const loadPublisherTiers = async () => {
    try {
      setPublisherTiers(
        await apiClient.listPublisherTiers(token, { includeInactive: showInactive })
      );
    } catch (error) {
      setTiersError(errorMessage(error, "Não foi possível carregar as faixas."));
    }
  };

  useEffect(() => {
    if (activeTab !== "publicadores-tipos") return;
    void loadPublisherTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive]);

  const resetTierForm = () => {
    setOpenForm(null);
    setEditingTierId(null);
    setNewTierName("");
    setNewTierWeight("");
    setNewTierMin("");
    setNewTierMax("");
    setNewTierDefault(false);
  };

  const openNewTier = () => {
    resetTierForm();
    setTiersError(null);
    setOpenForm("tier");
  };

  const startEditTier = (tier: PublisherTier) => {
    setTiersError(null);
    setOpenForm("tier");
    setEditingTierId(tier.id);
    setNewTierName(tier.name);
    setNewTierWeight(String(tier.weight));
    setNewTierMin(tier.min_count === null ? "" : String(tier.min_count));
    setNewTierMax(tier.max_count === null ? "" : String(tier.max_count));
    setNewTierDefault(tier.is_default);
  };

  const tierRequired: RequiredField[] = [
    { label: "Nome", filled: !!newTierName.trim() },
    { label: "Peso", filled: !!newTierWeight.trim() },
  ];
  const addPublisherTier = async () => {
    if (missingFields(tierRequired).length > 0) return;
    setCreatingTier(true);
    setTiersError(null);
    try {
      const payload = {
        name: newTierName.trim(),
        weight: Number(newTierWeight),
        min_count: newTierMin.trim() === "" ? null : Number(newTierMin),
        max_count: newTierMax.trim() === "" ? null : Number(newTierMax),
        is_default: newTierDefault
      };
      if (editingTierId !== null) {
        await apiClient.updatePublisherTier(token, editingTierId, payload);
      } else {
        await apiClient.createPublisherTier(token, payload);
      }
      resetTierForm();
      await loadPublisherTiers();
    } catch (error) {
      setTiersError(errorMessage(error, "Não foi possível salvar a faixa."));
    } finally {
      setCreatingTier(false);
    }
  };

  const setTierActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        apiClient.updatePublisherTier(token, id, { is_active: true }),
      deactivate: () => apiClient.deactivatePublisherTier(token, id),
      reload: loadPublisherTiers,
      setError: setTiersError,
      fallback: "Não foi possível alterar a faixa."
    });

  // Categorização de publicadores — grupos e pessoas, dois painéis na
  // mesma aba. O grupo é recurso próprio porque tem PESO (entrada do
  // CENA); a pessoa é um agregado (nome + perfis + grupos numa
  // requisição só).
  const [publisherGroups, setPublisherGroups] = useState<PublisherGroup[]>([]);
  const [publisherPersons, setPublisherPersons] = useState<PublisherPerson[]>([]);
  const [publishersError, setPublishersError] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupWeight, setNewGroupWeight] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [newPersonName, setNewPersonName] = useState("");
  const [newPersonProfiles, setNewPersonProfiles] = useState<
    Array<{ platform: string; profile_url: string }>
  >([]);
  const [newPersonGroupIds, setNewPersonGroupIds] = useState<number[]>([]);
  const [creatingPerson, setCreatingPerson] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<number | null>(null);

  const loadPublishers = async () => {
    try {
      const [grupos, pessoas] = await Promise.all([
        apiClient.listPublisherGroups(token, { includeInactive: showInactive }),
        apiClient.listPublisherPersons(token, { includeInactive: showInactive })
      ]);
      setPublisherGroups(grupos);
      setPublisherPersons(pessoas);
    } catch (error) {
      setPublishersError(errorMessage(error, "Não foi possível carregar os publicadores."));
    }
  };

  // O select de plataforma do publicador. Lista PRÓPRIA, e não a
  // `channels` da aba Canais: aquela é uma PÁGINA (e pode estar filtrada
  // por uma busca), então usá-la aqui esconderia opções sem aviso. São
  // só os canais de rede, que são poucos por natureza — a escala de 14
  // mil linhas é do lado dos veículos.
  const [channelOptions, setChannelOptions] = useState<Channel[]>([]);

  const loadChannelOptions = async () => {
    try {
      const page = await apiClient.listChannels(token, {
        postSource: "network",
        size: 200
      });
      setChannelOptions(page.items);
    } catch { /* o select fica vazio; o painel já reporta erro de carga */ }
  };

  // A importação por planilha escreve em pessoas e vínculos sem passar
  // pelos formulários: quando uma execução termina, o painel avisa e a
  // aba recarrega as duas listas. Contador + efeito (e não chamar
  // `loadPublishers` do callback) pelo mesmo motivo de `catalogRefresh`:
  // o callback tem que ser estável entre renders.
  const [publishersRefresh, setPublishersRefresh] = useState(0);
  // A importação da configuração inteira escreve em todas as listas ao
  // mesmo tempo: quando uma execução termina, só os SELOS da navegação
  // precisam recarregar na hora (cada aba relê a própria lista ao ser
  // aberta). Contador + efeito, como `catalogRefresh`, porque o callback
  // do painel tem que ser estável.
  const [configRefresh, setConfigRefresh] = useState(0);
  const onConfigImported = useCallback(
    () => setConfigRefresh(current => current + 1),
    []
  );
  useEffect(() => {
    if (configRefresh === 0) return;
    void loadCounts();
    void reloadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configRefresh]);

  // O atalho de premissas, na aba Configuração de prompt: quando uma
  // execução termina, a aba relê as listas dela (premissas, termos, foco).
  // Contador + efeito pelo mesmo motivo de `configRefresh` — o callback do
  // painel tem que ser estável — e o efeito lê a marca e a fonte do
  // momento, que é onde a pessoa está olhando.
  const [rulesRefresh, setRulesRefresh] = useState(0);
  const onRulesImported = useCallback(
    () => setRulesRefresh(current => current + 1),
    []
  );

  const onPublishersImported = useCallback(
    () => setPublishersRefresh(current => current + 1),
    []
  );
  useEffect(() => {
    if (publishersRefresh === 0 || activeTab !== "publicadores") return;
    void loadPublishers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishersRefresh]);

  useEffect(() => {
    if (activeTab !== "publicadores") return;
    void loadPublishers();
    // A lista de canais alimenta o select de plataforma: o valor tem
    // que casar com o `Canal` que vem da planilha, e texto livre
    // deixaria cadastrar grafia que nunca casa.
    if (!channelOptions.length) void loadChannelOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive]);

  const resetGroupForm = () => {
    setOpenForm(null);
    setEditingGroupId(null);
    setNewGroupName("");
    setNewGroupWeight("");
  };

  const openNewGroup = () => {
    resetGroupForm();
    setPublishersError(null);
    setOpenForm("publisherGroup");
  };

  const startEditGroup = (group: PublisherGroup) => {
    setPublishersError(null);
    setOpenForm("publisherGroup");
    setEditingGroupId(group.id);
    setNewGroupName(group.name);
    setNewGroupWeight(group.weight === null ? "" : String(group.weight));
  };

  const publisherGroupRequired: RequiredField[] = [
    { label: "Nome", filled: !!newGroupName.trim() },
  ];
  const addPublisherGroup = async () => {
    if (missingFields(publisherGroupRequired).length > 0) return;
    setCreatingGroup(true);
    setPublishersError(null);
    try {
      // Peso vazio vira `null`, e `null` SIGNIFICA "só rotula, não
      // pontua" — diferente de zero.
      const payload = {
        name: newGroupName.trim(),
        weight: newGroupWeight.trim() === "" ? null : Number(newGroupWeight)
      };
      if (editingGroupId !== null) {
        await apiClient.updatePublisherGroup(token, editingGroupId, payload);
      } else {
        await apiClient.createPublisherGroup(token, payload);
      }
      resetGroupForm();
      await loadPublishers();
    } catch (error) {
      setPublishersError(errorMessage(error, "Não foi possível salvar o grupo."));
    } finally {
      setCreatingGroup(false);
    }
  };

  const setGroupActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        apiClient.updatePublisherGroup(token, id, { is_active: true }),
      deactivate: () => apiClient.deactivatePublisherGroup(token, id),
      reload: loadPublishers,
      setError: setPublishersError,
      fallback: "Não foi possível alterar o grupo."
    });

  const resetPersonForm = () => {
    setOpenForm(null);
    setEditingPersonId(null);
    setNewPersonName("");
    setNewPersonProfiles([]);
    setNewPersonGroupIds([]);
  };

  const openNewPerson = () => {
    resetPersonForm();
    setPublishersError(null);
    setOpenForm("publisherPerson");
  };

  const startEditPerson = (person: PublisherPerson) => {
    setPublishersError(null);
    setOpenForm("publisherPerson");
    setEditingPersonId(person.id);
    setNewPersonName(person.name);
    setNewPersonProfiles(
      person.profiles.map(p => ({ platform: p.platform, profile_url: p.profile_url }))
    );
    setNewPersonGroupIds([...person.group_ids]);
  };

  const personRequired: RequiredField[] = [
    { label: "Nome", filled: !!newPersonName.trim() },
  ];
  const addPublisherPerson = async () => {
    if (missingFields(personRequired).length > 0) return;
    setCreatingPerson(true);
    setPublishersError(null);
    try {
      // Perfis e grupos vão SEMPRE juntos — o servidor grava as três
      // tabelas numa transação, então não existe meia-pessoa.
      const payload = {
        name: newPersonName.trim(),
        profiles: newPersonProfiles.filter(p => p.platform && p.profile_url.trim()),
        group_ids: newPersonGroupIds
      };
      if (editingPersonId !== null) {
        await apiClient.updatePublisherPerson(token, editingPersonId, payload);
      } else {
        await apiClient.createPublisherPerson(token, payload);
      }
      resetPersonForm();
      await loadPublishers();
    } catch (error) {
      setPublishersError(errorMessage(error, "Não foi possível salvar o publicador."));
    } finally {
      setCreatingPerson(false);
    }
  };

  const setPersonActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        apiClient.updatePublisherPerson(token, id, { is_active: true }),
      deactivate: () => apiClient.deactivatePublisherPerson(token, id),
      reload: loadPublishers,
      setError: setPublishersError,
      fallback: "Não foi possível alterar o publicador."
    });

  // Clipadoras continua FORA daqui de propósito: o backend dela não
  // existe (a tabela nem foi criada), e nenhum dos 5 scrapers usa
  // credencial hoje. Um formulário que erra ao carregar e ao salvar não
  // é incompleto — é um que não existe, com aparência de existir. O
  // painel no lugar diz o que a seção vai fazer; vira formulário quando
  // houver o que gravar.

  // Mensagem-Chave
  const [keyMessages, setKeyMessages] = useState<KeyMessage[]>([]);
  const [keyMessagesError, setKeyMessagesError] = useState<string | null>(null);
  const [newKeyMessageBrandId, setNewKeyMessageBrandId] = useState("");
  const [newKeyMessageSource, setNewKeyMessageSource] = useState<"press" | "network">("network");
  const [newKeyMessageText, setNewKeyMessageText] = useState("");
  const [newKeyMessageRules, setNewKeyMessageRules] = useState("");
  const [newKeyMessageExclusions, setNewKeyMessageExclusions] = useState("");
  const [creatingKeyMessage, setCreatingKeyMessage] = useState(false);
  const [editingKeyMessageId, setEditingKeyMessageId] = useState<number | null>(null);

  const loadKeyMessages = async () => {
    try {
      setKeyMessages(await catalogApi.listKeyMessages(token, { includeInactive: showInactive }));
    } catch (error) {
      setKeyMessagesError(errorMessage(error, "Não foi possível carregar as mensagens-chave."));
    }
  };

  useEffect(() => {
    if (activeTab !== "mensagens") return;
    resetKeyMessageForm();
    const prefill = prefillRef.current;
    if (prefill) {
      prefillRef.current = null;
      if (canEdit) {
        setNewKeyMessageText(prefill.name);
        if (prefill.brandId !== null) setNewKeyMessageBrandId(String(prefill.brandId));
        if (prefill.source !== null) setNewKeyMessageSource(prefill.source);
        setOpenForm("keyMessage");
      }
    }
    void loadKeyMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showInactive, catalogOwner]);

  const resetKeyMessageForm = () => {
    setOpenForm(null);
    setEditingKeyMessageId(null);
    setNewKeyMessageBrandId("");
    setNewKeyMessageSource("network");
    setNewKeyMessageText("");
    setNewKeyMessageRules("");
    setNewKeyMessageExclusions("");
  };

  const openNewKeyMessage = () => {
    resetKeyMessageForm();
    setKeyMessagesError(null);
    setOpenForm("keyMessage");
  };

  const startEditKeyMessage = (m: KeyMessage) => {
    setKeyMessagesError(null);
    setOpenForm("keyMessage");
    setEditingKeyMessageId(m.id);
    setNewKeyMessageBrandId(String(m.brand_id));
    setNewKeyMessageSource(m.data_source);
    setNewKeyMessageText(m.message);
    setNewKeyMessageRules(m.rules);
    setNewKeyMessageExclusions(m.exclusion_rules || "");
  };

  const keyMessageRequired: RequiredField[] = [
    { label: "Mensagem", filled: !!newKeyMessageText.trim() },
    { label: "Marca", filled: !!newKeyMessageBrandId },
    { label: "Critério de aplicação", filled: !!newKeyMessageRules.trim() },
  ];
  const addKeyMessage = async () => {
    // `rules` é obrigatório: é o critério que vai DENTRO do prompt
    // ("usa essa tag quando…"). Sem ele a IA escolhe a mensagem-chave
    // por conta própria.
    if (missingFields(keyMessageRequired).length > 0) return;
    setCreatingKeyMessage(true);
    setKeyMessagesError(null);
    try {
      const payload = {
        brand_id: Number(newKeyMessageBrandId),
        data_source: newKeyMessageSource,
        message: newKeyMessageText.trim(),
        rules: newKeyMessageRules.trim(),
        exclusion_rules: newKeyMessageExclusions.trim() || null
      };
      if (editingKeyMessageId !== null) {
        await catalogApi.updateKeyMessage(token, editingKeyMessageId, payload);
      } else {
        await catalogApi.createKeyMessage(token, payload);
      }
      resetKeyMessageForm();
      await loadKeyMessages();
    } catch (error) {
      setKeyMessagesError(errorMessage(error, "Não foi possível salvar a mensagem-chave."));
    } finally {
      setCreatingKeyMessage(false);
    }
  };

  const setKeyMessageActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        catalogApi.updateKeyMessage(token, id, { is_active: true }),
      deactivate: () => catalogApi.deactivateKeyMessage(token, id),
      reload: loadKeyMessages,
      setError: setKeyMessagesError,
      fallback: "Não foi possível alterar a mensagem-chave."
    });

  // ── Configuração de prompt ─────────────────────────────────────
  //
  // A aba edita TRÊS coisas diferentes, e a distinção é o que ela
  // precisa ensinar em vez de esconder:
  //
  //   Termos      → decidem a polaridade SEM chamar a IA. É atalho:
  //                 conta ocorrência no post/autor/veículo e pronto.
  //   Premissas   → texto que vai DENTRO do prompt, numerado, é o
  //                 critério que a IA usa e cita de volta.
  //   Foco        → texto que diz à IA de QUEM a matéria fala.
  //
  // Tudo é escopado por marca E por fonte (redes/imprensa), porque a
  // regra muda nos dois eixos.
  const promptTask = "polarity_intensity";
  const [promptBrandId, setPromptBrandId] = useState<number | null>(null);
  const [promptSource, setPromptSource] = useState<PostSource>("network");
  const [focusTerms, setFocusTerms] = useState<FocusTerm[]>([]);
  const [classificationTerms, setClassificationTerms] = useState<ClassificationTerm[]>([]);
  const [promptRules, setPromptRules] = useState<PromptRule[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptModels, setPromptModels] = useState<EffectivePromptModel[]>([]);
  // O cardápio que a administração cadastrou: só ativos e com credencial
  // do provedor. O campo era texto livre até 2026-09-01, e dava pra
  // digitar um modelo inexistente — o erro só aparecia quando o upload
  // falhava.
  const [aiModelOptions, setAiModelOptions] = useState<AiModel[]>([]);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [promptNotice, setPromptNotice] = useState<string[]>([]);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  // Marcas EXTRAS que recebem a premissa ou o termo de foco novo, além
  // da marca da aba (que sempre recebe). O servidor grava uma marca por
  // pedido, então o envio é um POST por marca — o padrão dos subtemas.
  const [promptExtraBrandIds, setPromptExtraBrandIds] = useState<number[]>([]);
  // O pop-up "Trazer do cliente, invertido" da aba Foco.
  const [invertOpen, setInvertOpen] = useState(false);

  // Trocar a escala pode sumir com a seção aberta (favorável sem dado,
  // compostas) — o estado apontaria pra um toggle que não renderiza
  // mais. Nada quebra, mas reabrir a seção "fantasma" ao voltar de
  // escala seria surpresa; zera e pronto.
  useEffect(() => {
    setExpandedSection(null);
  }, [polarityScale]);
  // Um rascunho por (seção × campo) — as seções são muitas e um estado
  // por campo viraria trinta `useState`.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [promptBusy, setPromptBusy] = useState(false);
  // Linhas em que a pessoa apertou "Personalizar" e ainda não salvou:
  // a edição liga, partindo do texto geral. Chave = `promptKey`.
  const [customizing, setCustomizing] = useState<Record<string, boolean>>({});
  // A linha cujo "Voltar ao geral" espera confirmação.
  const [confirmResetKey, setConfirmResetKey] = useState<string | null>(null);
  // A premissa aberta pra edição no próprio card — uma por vez, porque
  // o Salvar recarrega a lista inteira.
  const [editingRule, setEditingRule] = useState<{ id: number; text: string } | null>(null);

  const setDraftValue =(chave: string, valor: string) =>
    setDraft(prev => ({ ...prev, [chave]: valor }));

  // O texto e a temperatura de uma linha, com o rascunho por cima do efetivo.
  const promptDraftOf = (prompt: Prompt) => ({
    header: draft[`prompt:${promptKey(prompt)}:header`] ?? prompt.header,
    temperature: draft[`prompt:${promptKey(prompt)}:temperature`] ?? String(prompt.temperature)
  });

  const loadPromptConfig = async (brandId: number, source: PostSource) => {
    setPromptError(null);
    try {
      const [focos, termos, regras, textos, modelos, opcoes] = await Promise.all([
        // O interruptor "Mostrar desativados" vale pras três listas: sem
        // ele a premissa desativada sumia e a pessoa achava que a
        // tinha perdido (o servidor só a devolve com `include_inactive`).
        catalogApi.listFocusTerms(token, { brandId, source, includeInactive: showInactive }),
        catalogApi.listClassificationTerms(token, { brandId, source, includeInactive: showInactive }),
        catalogApi.listPromptRules(token, { brandId, task: promptTask, source, includeInactive: showInactive }),
        catalogApi.listPrompts(token, { source }),
        apiClient.listPromptModels(token),
        apiClient.listAiModelOptions(token)
      ]);
      setFocusTerms(focos);
      setClassificationTerms(termos);
      setPromptRules(regras);
      setPrompts(textos);
      setPromptModels(modelos);
      setAiModelOptions(opcoes);
    } catch (error) {
      setPromptError(errorMessage(error, "Não foi possível carregar a configuração de prompt."));
    }
  };

  useEffect(() => {
    if (!isPromptTab(activeTab)) return;
    // A marca tem que ser do ALVO do cadastro: a da empresa, ou a do
    // concorrente escolhido em "Cadastro de". Trocar o alvo deixa a
    // marca anterior fora da lista, e aí escolhe-se a primeira do novo.
    if (promptBrandId === null || !pickerBrands.some(b => b.id === promptBrandId)) {
      if (pickerBrands.length > 0) setPromptBrandId(pickerBrands[0].id);
      return;
    }
    void loadPromptConfig(promptBrandId, promptSource);
    // `isPromptTab(activeTab)` e não `activeTab`: trocar entre as cinco abas de IA não pode refazer a carga (o estado é um só); só entrar e sair delas conta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPromptTab(activeTab), promptBrandId, promptSource, brands, ownerBrands, catalogOwner, showInactive]);

  // As marcas extras valem pra marca e pro alvo em que foram marcadas:
  // trocar qualquer um dos dois zera, senão o envio seguinte iria pra
  // uma marca que a pessoa já não vê marcada.
  useEffect(() => {
    setPromptExtraBrandIds([]);
  }, [promptBrandId, catalogOwner]);

  useEffect(() => {
    if (rulesRefresh === 0 || !isPromptTab(activeTab) || promptBrandId === null) return;
    void loadPromptConfig(promptBrandId, promptSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesRefresh]);

  /** Envolve uma escrita da aba: erro vira mensagem legível e a lista
   * recarrega sempre — inclusive quando falha, porque um `POST` que
   * estourou o teto de 15 pode ter deixado outros já gravados. */
  const runPromptWrite = async (acao: () => Promise<void>, seFalhar: string): Promise<boolean> => {
    if (promptBrandId === null) return false;
    setPromptBusy(true);
    setPromptError(null);
    // A recusa é gravada DEPOIS da recarga: `loadPromptConfig` começa
    // limpando o erro, e gravá-la antes fazia toda recusa da aba (o 409
    // do teto de premissas, o 422 de marcador desconhecido) sumir no
    // mesmo tick — o servidor recusava e a tela parecia ter aceitado
    // (teste de render, 2026-10-09).
    let failure: string | null = null;
    try {
      await acao();
    } catch (error) {
      failure = error instanceof ApiError || error instanceof PromptWriteNotice ? error.message : seFalhar;
    } finally {
      await loadPromptConfig(promptBrandId, promptSource);
      if (failure !== null) setPromptError(failure);
      setPromptBusy(false);
    }
    return failure === null;
  };

  /** As marcas que recebem a escrita nova: a da aba mais as extras
   * marcadas que ainda são do alvo atual. */
  const promptTargetBrandIds = (): number[] => {
    if (promptBrandId === null) return [];
    const extras = promptExtraBrandIds.filter(
      id => id !== promptBrandId && pickerBrands.some(b => b.id === id)
    );
    return [promptBrandId, ...extras];
  };

  /** Grava os itens em CADA marca, uma de cada vez e em sequência. Nunca
   * lança: devolve o que não entrou (`remaining`, a união entre as
   * marcas, na ordem digitada) e o `failure` pronto pra subir. Com uma
   * marca só o erro é o do próprio servidor; com várias, a falha de
   * uma não impede as outras e a mensagem lista por marca o que não
   * entrou — o que já entrou fica gravado e aparece na recarga. */
  const addToBrands = async (
    items: string[],
    create: (brandId: number, item: string) => Promise<unknown>
  ): Promise<{ added: string[]; remaining: string[]; failure: unknown | null }> => {
    const brandIds = promptTargetBrandIds();
    if (brandIds.length === 1) {
      const result = await addSequentially(items, item => create(brandIds[0], item));
      return { added: result.added, remaining: result.remaining, failure: result.error };
    }
    const failures: { brandId: number; remaining: string[]; message: string }[] = [];
    let added: string[] = [];
    for (const brandId of brandIds) {
      const result = await addSequentially(items, item => create(brandId, item));
      if (brandId === brandIds[0]) added = result.added;
      if (result.error) {
        failures.push({
          brandId,
          remaining: result.remaining,
          message: result.error instanceof ApiError ? result.error.message : "não foi possível gravar."
        });
      }
    }
    const missing = new Set(failures.flatMap(f => f.remaining));
    const remaining = items.filter(item => missing.has(item));
    if (failures.length === 0) return { added, remaining, failure: null };
    return {
      added,
      remaining,
      failure: new PromptWriteNotice(
        `Não entrou em ${failures.length === 1 ? "uma marca" : `${failures.length} marcas`}: ` +
          failures.map(f => `${brandName(f.brandId)} — ${f.message}`).join(" · ")
      )
    };
  };

  const addFocusTerm = (category: FocusCategory) => {
    const chave = `focus:${category}`;
    const termos = splitEntries(draft[chave] || "");
    if (termos.length === 0 || promptBrandId === null) return;
    void runPromptWrite(async () => {
      const { remaining, failure } = await addToBrands(termos, (brandId, termo) =>
        catalogApi.createFocusTerm(token, {
          brand_id: brandId,
          category,
          source: promptSource,
          term: termo
        })
      );
      setDraftValue(chave, remainingToDraft(remaining));
      if (failure) throw failure;
    }, "Não foi possível salvar o foco.");
  };

  /** Inverte um termo de foco: cliente <-> concorrente (item 32, 2026-11-01).
   * A categoria é a única coisa que muda; o servidor já aceitava no PATCH. */
  const moveFocusTerm = (id: number, category: FocusCategory) => {
    void runPromptWrite(
      async () => { await catalogApi.updateFocusTerm(token, id, { category }); },
      "Não foi possível mover o foco."
    );
  };

  /** Cria os termos trazidos do cliente (já invertidos), um por vez e em
   * sequência. Falha de um não impede os seguintes; um 429 PARA o laço (os
   * pedidos seguintes só empurrariam a liberação) e diz quantos faltaram.
   * A lista recarrega ANTES de a mensagem ser gravada. */
  const applyInvertedFocus = async (items: InvertedTerm[]) => {
    if (promptBrandId === null || items.length === 0) return;
    setPromptBusy(true);
    setPromptError(null);
    setPromptNotice([]);
    let created = 0;
    const failures: string[] = [];
    let stoppedAt: number | null = null;
    for (const [index, item] of items.entries()) {
      try {
        await catalogApi.createFocusTerm(token, {
          brand_id: promptBrandId,
          category: item.category,
          source: promptSource,
          term: item.term
        });
        created += 1;
      } catch (error) {
        if (error instanceof ApiError && error.status === 429) {
          stoppedAt = index;
          break;
        }
        failures.push(`${item.term} — ${error instanceof ApiError ? error.message : "não foi possível gravar."}`);
      }
    }
    setInvertOpen(false);
    await loadPromptConfig(promptBrandId, promptSource);
    const problems: string[] = [];
    if (failures.length > 0) {
      problems.push(`${failures.length === 1 ? "1 termo não entrou" : `${failures.length} termos não entraram`}: ${failures.join(" · ")}`);
    }
    if (stoppedAt !== null) {
      problems.push(
        `Limite de requisições atingido: faltaram ${items.length - stoppedAt} termo(s). Aplique de novo — os que já entraram aparecem como já cadastrados.`
      );
    }
    if (problems.length > 0) setPromptError(problems.join(" "));
    if (created > 0) {
      setPromptNotice([created === 1 ? "1 termo criado." : `${created} termos criados.`]);
    }
    setPromptBusy(false);
  };

  const addClassificationTerm = (category: TermCategory, termType: TermType) => {
    const chave = `term:${category}:${termType}`;
    const termos = splitEntries(draft[chave] || "");
    if (termos.length === 0 || promptBrandId === null) return;
    void runPromptWrite(async () => {
      const { remaining, error } = await addSequentially(termos, termo =>
        catalogApi.createClassificationTerm(token, {
          brand_id: promptBrandId,
          term_type: termType,
          category,
          source: promptSource,
          term: termo
        })
      );
      setDraftValue(chave, remainingToDraft(remaining));
      if (error) throw error;
    }, "Não foi possível salvar o termo.");
  };

  const addPromptRule = (ruleType: string) => {
    const chave = `rule:${ruleType}`;
    const linhas = splitLines(draft[chave] || "");
    if (linhas.length === 0 || promptBrandId === null) return;
    // Só as ATIVAS contam pro teto (a desativada aparece na lista com
    // "Mostrar desativados" e não ocupa vaga no prompt).
    // GLOBAL e NEUTRO não têm faixa reservada: sem teto.
    const numbered = POLARITY_SECTIONS.find(secao => secao.rule === ruleType)?.numbered ?? true;
    const room = numbered
      ? MAX_RULES_PER_SECTION - promptRules.filter(r => r.rule_type === ruleType && r.is_active).length
      : Infinity;
    if (room <= 0) return;
    // Nunca manda uma linha a mais pro servidor tomar 409: envia só as
    // que cabem e devolve o resto ao campo.
    const enviar = linhas.slice(0, room);
    const excedente = linhas.slice(room);
    void runPromptWrite(async () => {
      // `position` não vai no payload: o servidor deriva da faixa
      // reservada do tipo. Mandar daqui permitiria gravar número fora
      // da faixa, e aí o motor descarta a polaridade em silêncio.
      const { added, remaining, failure } = await addToBrands(enviar, (brandId, texto) =>
        catalogApi.createPromptRule(token, {
          brand_id: brandId,
          task: promptTask,
          rule_type: ruleType,
          source: promptSource,
          text: texto
        })
      );
      setDraftValue(chave, [...remaining, ...excedente].join("\n"));
      if (failure) throw failure;
      if (excedente.length > 0) {
        throw new PromptWriteNotice(
          `Entraram ${added.length} ${added.length === 1 ? "premissa" : "premissas"}; esta seção aceita ${MAX_RULES_PER_SECTION} e sobraram ${excedente.length} no campo.`
        );
      }
    }, "Não foi possível salvar a premissa.");
  };

  /** Troca só o TEXTO: o número fica, porque a IA o cita de volta e a
   * menção já classificada aponta pra ele. Quebra de linha vira espaço
   * — no prompt cada premissa é uma linha numerada, e uma quebra no
   * meio pareceria duas. Recusado, o campo continua aberto com o texto. */
  const savePromptRuleText = (rule: PromptRule) => {
    if (editingRule === null || editingRule.id !== rule.id) return;
    const text = editingRule.text.replace(/\s*\r?\n\s*/g, " ").trim();
    if (!text) return;
    if (text === rule.text) {
      setEditingRule(null);
      return;
    }
    void runPromptWrite(async () => {
      await catalogApi.updatePromptRule(token, rule.id, { text });
    }, "Não foi possível salvar a premissa.").then(ok => {
      if (ok) setEditingRule(null);
    });
  };

  /** Larga o rascunho e o "Personalizar" de uma linha (depois de gravar,
   * ou de a linha voltar ao geral). */
  const forgetPromptDraft = (prompt: Prompt) => {
    const key = promptKey(prompt);
    setDraft(prev => {
      const { [`prompt:${key}:header`]: _h, [`prompt:${key}:temperature`]: _t, ...resto } = prev;
      return resto;
    });
    setCustomizing(prev => {
      const { [key]: _c, ...resto } = prev;
      return resto;
    });
  };

  /** Personaliza a linha INTEIRA (PUT): o que a tela edita é o texto e a
   * temperatura; cabeçalho extra, molde de saída e mensagem de sistema
   * seguem como a linha efetiva os tem. Texto igual ao geral volta com
   * `origin: "default"` — a linha não virou exceção, e a tela diz. */
  const putPrompt = async (prompt: Prompt, header: string, temperature: number) => {
    const salvo = await catalogApi.customizePrompt(token, prompt.task, prompt.source, prompt.position, {
      header,
      extra_header: prompt.extra_header,
      output_template: prompt.output_template,
      temperature,
      system_message: prompt.system_message
    });
    forgetPromptDraft(prompt);
    setPromptNotice(
      salvo.origin === "default"
        ? ["O texto ficou igual ao geral: a linha voltou a seguir o geral."]
        : []
    );
  };

  const savePromptText = (prompt: Prompt) => {
    const { header, temperature } = promptDraftOf(prompt);
    void runPromptWrite(
      () => putPrompt(prompt, header, Number(temperature)),
      "Não foi possível salvar o texto do prompt."
    );
  };

  /** "Vi que o geral mudou e mantenho o meu": reenvia a personalização
   * como está, o que só atualiza a data de referência. */
  const keepPromptCustomization = (prompt: Prompt) => {
    void runPromptWrite(
      () => putPrompt(prompt, prompt.header, prompt.temperature),
      "Não foi possível manter a personalização."
    );
  };

  const resetPromptToGeneral = (prompt: Prompt) => {
    void runPromptWrite(async () => {
      await catalogApi.resetPrompt(token, prompt.task, prompt.source, prompt.position);
      forgetPromptDraft(prompt);
      setPromptNotice([]);
    }, "Não foi possível voltar a linha ao geral.");
  };

  /**
   * Escolhe o modelo da tarefa, ou volta ao padrão da instalação.
   *
   * Valor vazio no seletor significa "usar o padrão", e isso é um
   * DELETE do override — não um PUT com string vazia. A distinção
   * importa: sem apagar a linha, a empresa continuaria presa ao modelo
   * escolhido mesmo depois de a administração trocar o padrão.
   */
  const savePromptModel = (task: string, modelId: string) => {
    void runPromptWrite(async () => {
      if (modelId) await apiClient.setPromptModel(token, task, Number(modelId));
      else await apiClient.clearPromptModel(token, task);
    }, "Não foi possível salvar o modelo.");
  };

  // As duas chaves da empresa (2026-09-14): o Máster edita sempre;
  // abaixo dele, só com a chave ligada. O servidor responde 403 de
  // qualquer jeito — aqui é o que a tela OFERECE, pra ninguém digitar um
  // prompt inteiro e descobrir no Salvar que não podia.
  const promptsLocked =
    !isMaster(userProfile) && !(profile?.can_edit_prompts ?? false);
  const modelsLocked =
    !isMaster(userProfile) && !(profile?.can_edit_models ?? false);

  const saveAiEditingAccess = (changes: {
    can_edit_prompts?: boolean;
    can_edit_models?: boolean;
  }) => {
    void runPromptWrite(async () => {
      const salvo = await apiClient.setAiEditingAccess(token, changes);
      setProfile(salvo);
    }, "Não foi possível alterar a liberação.");
  };

  /** `true` se gravou — o "Salvar e sair" do aviso de saída depende disso. */
  const handleSave = async (e?: FormEvent): Promise<boolean> => {
    if (e) e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);
    setCompanyDetailError(null);
    try {
      const salvo = await apiClient.updateCompanyProfile(token, {
        name: name.trim(),
        has_competitor_analysis: hasCompetitorAnalysis,
        is_government: isGovernment,
        displays_cena: displaysCena,
        publisher_group_counts_toward_cena: publisherGroupCounts,
        polarity_scale: polarityScale,
        color_logo: colorLogo,
        gray_logo: grayLogo
      });
      setProfile(salvo);
      // O flag já mudou no servidor — o shell precisa recarregar o /me
      // pra menu/card/filtro reagirem sem F5. ANTES do laço de citação
      // (uma falha ali não pode pular isto), e NUNCA aguardado dentro
      // deste try: em modo não-dev o refresh bate no /auth/me morto e
      // rejeita, e um save BEM-SUCEDIDO mostraria erro.
      void refreshProfile().catch(() => {});
      // As categorias de citação são gravadas uma a uma: cada `PUT`
      // substitui a lista da categoria, e mandar as cinco de uma vez
      // exigiria uma rota de lote que nada mais usaria.
      for (const grupo of citationTags) {
        await apiClient.setCitationTags(token, grupo.category, grupo.tags);
      }
      setSavedCitationTags(citationTags);
      setAutofillNotice(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      return true;
    } catch (error) {
      setCompanyDetailError(errorMessage(error, "Não foi possível salvar as alterações."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleNewSpokespersonBrand = (brandId: number) => {
    setNewSpokespersonBrandIds(prev =>
      prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]
    );
  };

  const addVariation = () => {
    const novas = splitEntries(newVariation);
    if (novas.length === 0) return;
    setNewSpokespersonVariations(prev => [...prev, ...novas.filter(v => !prev.includes(v))]);
    setNewVariation("");
  };

  const removeVariation = (index: number) => {
    setNewSpokespersonVariations(prev => prev.filter((_, i) => i !== index));
  };

  const resetSpokespersonForm = () => {
    setOpenForm(null);
    setEditingSpokespersonId(null);
    setNewSpokespersonName("");
    setNewSpokespersonBrandIds([]);
    setNewSpokespersonCountsCena(true);
    setNewSpokespersonValidUntil("");
    setNewSpokespersonVariations([]);
  };

  const openNewSpokesperson = () => {
    resetSpokespersonForm();
    setSpokespersonsError(null);
    setOpenForm("spokesperson");
  };

  const startEditSpokesperson = (p: Spokesperson) => {
    setSpokespersonsError(null);
    setOpenForm("spokesperson");
    setEditingSpokespersonId(p.id);
    setNewSpokespersonName(p.name);
    setNewSpokespersonBrandIds([p.brand_id]);
    setNewSpokespersonCountsCena(p.counts_toward_spokesperson_score);
    setNewSpokespersonValidUntil(p.valid_until || "");
    // `;` é a forma que o banco guarda e que o `elt/` divide na borda.
    setNewSpokespersonVariations(
      p.name_variations ? splitEntries(p.name_variations) : []
    );
  };

  const spokespersonRequired: RequiredField[] = [
    { label: "Nome", filled: !!newSpokespersonName.trim() },
    { label: "Marcas", filled: newSpokespersonBrandIds.length > 0 },
  ];
  const addSpokesperson = async () => {
    if (missingFields(spokespersonRequired).length > 0) return;
    setCreatingSpokesperson(true);
    setSpokespersonsError(null);
    try {
      const comum = {
        name: newSpokespersonName.trim(),
        name_variations: newSpokespersonVariations.join(";") || null,
        counts_toward_spokesperson_score: newSpokespersonCountsCena,
        // `valid_until` é a saída do porta-voz da empresa: depois dessa
        // data ele para de contar, mas as menções antigas continuam
        // atribuídas a ele. Vazio = sem data de saída.
        valid_until: newSpokespersonValidUntil || null
      };
      if (editingSpokespersonId !== null) {
        await catalogApi.updateSpokesperson(token, editingSpokespersonId, {
          ...comum,
          brand_id: newSpokespersonBrandIds[0]
        });
      } else {
        // Uma linha por marca, igual a tema: "se o porta-voz X foi
        // aplicado para 3 marcas, ele vai constar 3 vezes na listagem".
        for (const brandId of newSpokespersonBrandIds) {
          await catalogApi.createSpokesperson(token, { ...comum, brand_id: brandId });
        }
      }
      resetSpokespersonForm();
      await loadSpokespersons();
    } catch (error) {
      // Recarrega ANTES de gravar o erro: `loadSpokespersons` começa
      // limpando o erro, e na ordem inversa o 409 da segunda marca
      // sumia da tela com a primeira já criada (teste de render,
      // 2026-10-09). A recarga é necessária — o laço cria uma linha por
      // marca, e as que entraram antes da recusa já existem.
      await loadSpokespersons();
      setSpokespersonsError(errorMessage(error, "Não foi possível salvar o porta-voz."));
    } finally {
      setCreatingSpokesperson(false);
    }
  };

  const setSpokespersonActive = (id: number, active: boolean) =>
    toggleCatalogActive({
      active,
      activate: () =>
        catalogApi.updateSpokesperson(token, id, { is_active: true }),
      deactivate: () => catalogApi.deactivateSpokesperson(token, id),
      reload: loadSpokespersons,
      setError: setSpokespersonsError,
      fallback: "Não foi possível alterar o porta-voz."
    });

  /** A contagem da aba ABERTA vem da lista que ela já carregou, não do
   * snapshot: você acabou de adicionar um tema e o selo tem que mudar
   * na hora, sem uma segunda ida ao servidor.
   *
   * `filter(is_active)` porque as listas da aba podem estar com
   * "Mostrar desativados" ligado, e o selo conta cadastro vivo. */
  const vivos = <T extends { is_active: boolean }>(linhas: T[]) =>
    linhas.filter(linha => linha.is_active).length;

  const liveCounts: Partial<Record<TabId | "subtemas", number>> = {
    marcas: brands.length,
    concorrentes: vivos(concorrentes),
    portavozes: vivos(spokespersons),
    mensagens: vivos(keyMessages),
    temas: vivos(themeGroups),
    subtemas: vivos(themes),
    // Canais e veículos vêm do `total` do servidor, não do `vivos()` da
    // lista: a lista é uma PÁGINA, e contá-la diria "24" pra um catálogo
    // de 14 mil. Com busca ou "mostrar desativados" ligados o `total` é
    // do filtro e não do catálogo, então aí o selo volta ao snapshot —
    // é o único valor que ainda responde à pergunta que ele faz
    // ("quantos cadastros vivos existem aqui?").
    canais:
      showInactive || channelQuery.trim() ? counts.canais : channelTotal,
    veiculos:
      showInactive || vehicleQuery.trim() ? counts.veiculos : vehicleTotal,
    "publicadores-tipos": vivos(publisherTiers),
    publicadores: vivos(publisherPersons)
    // `texto-prompt` fica DE FORA de propósito: a lista da aba é filtrada
    // pela fonte selecionada (só redes ou só imprensa), então usá-la
    // aqui faria o selo cair de 13 pra 6 no instante em que você
    // abrisse a aba. O snapshot conta as duas fontes, que é o número
    // que o selo promete.
  };

  const countFor = (id: TabId | "subtemas") =>
    activeTab === (id === "subtemas" ? "temas" : id) ? liveCounts[id] : counts[id];

  // ── Busca nas listas (ver `listSearch`) ──────────────────────────
  // Em memória e a cada tecla: estas listas chegam inteiras. Cada uma
  // diz em QUE campos procura — o nome, e o que mais a pessoa lembra do
  // cadastro (o termo, a marca, o grupo).
  const searchOf = (key: string) => listSearch[key] ?? "";
  const setSearchOf = (key: string) => (value: string) =>
    setListSearch(current => ({ ...current, [key]: value }));
  const shownBrands = filterBySearch(visibleBrands, searchOf("marcas"), b => [
    b.name,
    b.terms
  ]);
  const shownCompetitors = filterBySearch(
    concorrentes,
    searchOf("concorrentes"),
    c => [c.name, c.terms]
  );
  const shownSpokespersons = filterBySearch(
    spokespersons,
    searchOf("portavozes"),
    sp => [sp.name, sp.name_variations, brandName(sp.brand_id)]
  );
  const shownKeyMessages = filterBySearch(
    keyMessages,
    searchOf("mensagens"),
    km => [km.message, km.rules, km.exclusion_rules, brandName(km.brand_id)]
  );
  // Temas e subtemas: um bloco por tema, com a busca e o filtro de marca
  // aplicados dentro dele (`theme-blocks.ts`).
  const themeFilters = { search: searchOf("temas"), brandId: themeBrandFilter };
  const themeFiltering = isFiltering(themeFilters);
  const themeBlocks = buildThemeBlocks(themeGroups, themes, themeFilters, brandName);
  const allThemeBlocks = themeFiltering
    ? buildThemeBlocks(themeGroups, themes, { search: "", brandId: null }, brandName)
    : themeBlocks;
  const shownPublisherTiers = filterBySearch(
    publisherTiers,
    searchOf("publicadores-tipos"),
    tier => [tier.name]
  );
  const shownPublisherGroups = filterBySearch(
    publisherGroups,
    searchOf("publicadores-grupos"),
    group => [group.name]
  );
  const shownPublisherPersons = filterBySearch(
    publisherPersons,
    searchOf("publicadores"),
    person => [
      person.name,
      ...person.profiles.map(profile => profile.profile_url),
      ...person.group_ids.map(
        id => publisherGroups.find(group => group.id === id)?.name
      )
    ]
  );

  // ── Alteração não salva (2026-10-26) ──────────────────────────────
  // Sair da tela ou trocar de aba com algo por gravar pergunta antes
  // (`shared/unsaved-changes`). Só conta a aba ABERTA: o estado das
  // outras fica na página, mas é recarregado do servidor ao voltar.
  const confirmLeave = useConfirmLeave();
  const companyDirty =
    activeTab === "geral" &&
    canEdit &&
    profile !== null &&
    (name.trim() !== profile.name ||
      hasCompetitorAnalysis !== profile.has_competitor_analysis ||
      isGovernment !== profile.is_government ||
      displaysCena !== profile.displays_cena ||
      publisherGroupCounts !== profile.publisher_group_counts_toward_cena ||
      polarityScale !== profile.polarity_scale ||
      colorLogo !== profile.color_logo ||
      grayLogo !== profile.gray_logo ||
      citationSignature(citationTags) !== citationSignature(savedCitationTags));
  useUnsavedChanges(companyDirty, handleSave, "Dados da empresa");

  // Os textos de prompt editados e não salvos (cada um tem o seu "Salvar
  // texto"; sair salva todos).
  // Linha que segue o geral só entra aqui depois de "Personalizar" E de
  // o texto mudar: sem mudança não há o que salvar (e salvar igual ao
  // geral nem cria exceção).
  const dirtyPrompts =
    activeTab === "texto-prompt" && canEdit && !promptsLocked
      ? prompts.filter(prompt => {
          const { header, temperature } = promptDraftOf(prompt);
          return header !== prompt.header || Number(temperature) !== prompt.temperature;
        })
      : [];
  useUnsavedChanges(dirtyPrompts.length > 0, () =>
    runPromptWrite(async () => {
      for (const prompt of dirtyPrompts) {
        const { header, temperature } = promptDraftOf(prompt);
        await putPrompt(prompt, header, Number(temperature));
      }
    }, "Não foi possível salvar o texto do prompt."),
    dirtyPrompts.length === 1 ? "um texto de prompt" : "textos de prompt"
  );

  // ── As cinco abas de IA (2026-10-31) ────────────────────────────
  // Premissas, Termos de classificação, Foco, Texto do prompt e Modelo de
  // IA saíram de UMA aba ("Configuração de prompt"). O ESTADO continua um
  // só (marca, fonte, tarefa, "Cadastro de", inativos, marcas extras, as
  // listas de `loadPromptConfig`): trocar de aba não perde a escolha. Só o
  // desenho se repartiu — os seletores de alvo viram um sub-render
  // reaproveitado no topo de cada aba que precisa deles.
  const renderPromptTarget = (show: { brand: boolean; extras: boolean }) => (
    <div className="prompt-context">
      <span className="prompt-context-label">Editando</span>
      <div className="grid-fields-2">
        {show.brand && (
          <label className="form-field">
            <span>Marca</span>
            <Select
              value={promptBrandId ?? ""}
              onChange={e => setPromptBrandId(Number(e.target.value))}
            >
              {pickerBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </label>
        )}
        <label className="form-field">
          <span>Fonte</span>
          <Select
            value={promptSource}
            onChange={e => setPromptSource(e.target.value as PostSource)}
          >
            <option value="network">Redes sociais</option>
            <option value="press">Imprensa</option>
          </Select>
        </label>
      </div>
      {show.extras && canEdit && pickerBrands.length > 1 && (
        <div className="form-field">
          <span>Premissas e foco novos valem para</span>
          <div className="brand-picker">
            {pickerBrands.map(b => (
              <label key={b.id} className="brand-picker-item">
                <input
                  type="checkbox"
                  checked={b.id === promptBrandId || promptExtraBrandIds.includes(b.id)}
                  disabled={b.id === promptBrandId || promptBusy}
                  onChange={() =>
                    setPromptExtraBrandIds(prev =>
                      prev.includes(b.id) ? prev.filter(id => id !== b.id) : [...prev, b.id]
                    )
                  }
                />
                {b.name}
              </label>
            ))}
          </div>
          <small>
            A marca desta página sempre recebe. Marcar outras grava a mesma premissa
            ou o mesmo termo de foco em cada uma — uma gravação por marca.
          </small>
        </div>
      )}
      <InactiveFilter checked={showInactive} onChange={setShowInactive} />
    </div>
  );

  /** Premissas ("premises") e Termos ("terms") dividem as mesmas seções
   * de polaridade; cada aba desenha só a sua metade. */
  const renderPolaritySections = (part: "premises" | "terms") => POLARITY_SECTIONS.map(secao => {
              const termCategory = secao.term;
              // Premissas e Termos são duas abas (2026-10-31): a seção
              // desenha só a metade da aba aberta. Neutro e Premissas
              // gerais não têm termos, então não aparecem na aba de termos.
              if (part === "terms" && secao.term === null) return null;
              const termosDaSecao = (tipo: TermType) =>
                secao.term === null
                  ? []
                  : classificationTerms.filter(t => t.category === secao.term && t.term_type === tipo);
              const regras = promptRules
                .filter(r => r.rule_type === secao.rule)
                .sort((a, b) => a.position - b.position);
              // Fora da escala (favorável/desfavorável no modo 3): a
              // seção só aparece se tem DADO — órfã visível com selo,
              // pra quem migrou de escala poder limpar (decisão da
              // dona). O motor ignora esses cadastros no modo 3; sem
              // dado, a seção some. NEUTRO não ganha seção nunca: é
              // residual, sem termos nem premissas.
              const foraDaEscala =
                polarityScale === "three_level" &&
                (secao.term === "favorable" || secao.term === "unfavorable");
              const temTermos =
                secao.term !== null && classificationTerms.some(t => t.category === secao.term);
              const temDado = part === "premises" ? regras.length > 0 : temTermos;
              if (foraDaEscala && !temDado) return null;
              const tipoDeAutor: TermType = promptSource === "press" ? "vehicle" : "publisher";
              const rotuloDeAutor = promptSource === "press" ? "Veículos" : "Publicadores";
              const aberta = expandedSection === secao.rule;
              const proprios = termosDaSecao("proprietary_terms");
              // Só as ativas ocupam vaga no prompt e contam no teto; as
              // desativadas só aparecem com "Mostrar desativados".
              const regrasAtivas = regras.filter(r => r.is_active);
              // Tipo sem faixa reservada (GLOBAL, NEUTRO) não tem teto.
              const cheia = secao.numbered && regrasAtivas.length >= MAX_RULES_PER_SECTION;
              const doAutor = termosDaSecao(tipoDeAutor);
              return (
                <div className={`prompt-polarity${aberta ? " is-open" : ""}`} data-tone={secao.term ?? secao.rule.toLowerCase()} key={secao.rule}>
                  <button
                    type="button"
                    className={`category-toggle ${aberta ? "is-open" : ""}`}
                    onClick={() => setExpandedSection(prev => prev === secao.rule ? null : secao.rule)}
                    aria-expanded={aberta}
                  >
                    <span className="prompt-polarity-title">
                      <span className="prompt-polarity-dot" aria-hidden="true" />
                      {secao.label}
                    </span>
                    <span className="prompt-polarity-summary">
                      {part === "terms" && (
                        <>
                          {proprios.filter(t => t.is_active).length} {proprios.filter(t => t.is_active).length === 1 ? "termo" : "termos"}
                        </>
                      )}
                      {part === "premises" && (
                        <>
                          {regrasAtivas.length} {regrasAtivas.length === 1 ? "premissa" : "premissas"}
                        </>
                      )}
                    </span>
                    {foraDaEscala && (
                      <span className="badge-pill inactive">
                        não usada nesta escala
                      </span>
                    )}
                    <ChevronRight size={16} className={aberta ? "rotated" : ""} />
                  </button>
                  {aberta && foraDaEscala && (
                    <p className="section-hint">
                      Esta empresa está na escala de 3 níveis: o motor{" "}
                      <strong>ignora</strong> estes termos e premissas ao
                      classificar. Eles continuam guardados — remova o que
                      não quiser manter, ou volte a escala de 4 níveis em
                      "Dados da empresa" pra reativá-los.
                    </p>
                  )}
                  {aberta && (
                    <div className={`category-body${part === "premises" || secao.term === null ? " is-single" : ""}`}>
                      {part === "terms" && termCategory !== null && (
                      <div className="prompt-polarity-col">
                        <h4>Termos</h4>
                        <p className="section-hint">
                          Batendo o termo, a publicação já sai como{" "}
                          {secao.label.toLowerCase()} — sem IA. Palavra inteira, sem
                          diferenciar maiúscula — mas <strong>COM acento</strong>: cadastre{" "}
                          <code>saúde</code> e <code>saude</code> se quiser os dois. Sem
                          expressão booleana, sem regex.
                        </p>
                        <div className="form-field prompt-term-field">
                          <span>Termos proprietários</span>
                          {canEdit && (
                            <AddTermBox
                              value={draft[`term:${termCategory}:proprietary_terms`] || ""}
                              onChange={value => setDraftValue(`term:${termCategory}:proprietary_terms`, value)}
                              onAdd={() => addClassificationTerm(termCategory, "proprietary_terms")}
                              placeholder="Termo — vários separados por ;"
                              disabled={promptBusy}
                              label="Adicionar termo proprietário"
                            />
                          )}
                          <small>Procurado no texto da publicação e no título. {MULTI_ENTRY_HINT}</small>
                          <RemovablePills
 canEdit={canEdit}
                            items={proprios}
                            onRemove={id => runPromptWrite(
                              async () => { await catalogApi.deactivateClassificationTerm(token, id); },
                              "Não foi possível remover o termo."
                            )}
                            onReactivate={id => runPromptWrite(
                              async () => { await catalogApi.updateClassificationTerm(token, id, { is_active: true }); },
                              "Não foi possível reativar o termo."
                            )}
                          />
                        </div>
                        {/* Inerte desde 2026-08-25: fica recolhido, como
                            histórico, em vez de dividir a coluna com o campo
                            que vale. */}
                        <details className="prompt-legacy">
                          <summary>
                            Termos de {rotuloDeAutor.toLowerCase()} <span>inativo · {doAutor.length}</span>
                          </summary>
                          <div className="form-field prompt-term-field">
                            <span>{rotuloDeAutor}</span>
                            {canEdit && (
                              <AddTermBox
                                value={draft[`term:${termCategory}:${tipoDeAutor}`] || ""}
                                onChange={value => setDraftValue(`term:${termCategory}:${tipoDeAutor}`, value)}
                                onAdd={() => addClassificationTerm(termCategory, tipoDeAutor)}
                                placeholder="Termo — vários separados por ;"
                                disabled={promptBusy}
                                label={`Adicionar ${rotuloDeAutor}`}
                              />
                            )}
                            <small>
                              <strong>Inerte desde 2026-08-25</strong>: quem publicou não decide
                              mais a polaridade — só o texto do post e o título. Estes termos ficam
                              guardados como histórico, mas o motor não os consulta.{" "}
                              {MULTI_ENTRY_HINT}
                            </small>
                            <RemovablePills
 canEdit={canEdit}
                              items={doAutor}
                              onRemove={id => runPromptWrite(
                                async () => { await catalogApi.deactivateClassificationTerm(token, id); },
                                "Não foi possível remover o termo."
                              )}
                              onReactivate={id => runPromptWrite(
                                async () => { await catalogApi.updateClassificationTerm(token, id, { is_active: true }); },
                                "Não foi possível reativar o termo."
                              )}
                            />
                          </div>
                        </details>
                      </div>
                      )}

                      {part === "premises" && (
                      <div className="prompt-polarity-col">
                        <h4>
                          Premissas
                          {secao.numbered && (
                            <>
                              {" "}
                              <span className={`prompt-rule-count${cheia ? " is-full" : ""}`}>
                                {regrasAtivas.length}/{MAX_RULES_PER_SECTION}
                              </span>
                            </>
                          )}
                        </h4>
                        {secao.hint && <p className="section-hint">{secao.hint}</p>}
                        <div className="list-cards-grid">
                          {regras.map(r => canEdit && editingRule?.id === r.id ? (
                            <div key={r.id} className="item-card prompt-rule-editing">
                              <div className="item-card-info">
                                <span className="rule-number">{secao.numbered ? r.position : "•"}</span>
                                <div className="form-field">
                                  <textarea
                                    rows={3}
                                    autoFocus
                                    value={editingRule.text}
                                    onChange={e => setEditingRule({ id: r.id, text: e.target.value })}
                                    onKeyDown={e => {
                                      // Enter salva (premissa é uma linha só); Esc desiste.
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        savePromptRuleText(r);
                                      } else if (e.key === "Escape") {
                                        setEditingRule(null);
                                      }
                                    }}
                                    disabled={promptBusy}
                                    aria-label={`Texto da premissa ${r.position}`}
                                  />
                                </div>
                              </div>
                              <div className="item-card-actions">
                                <button
                                  type="button"
                                  className="icon-btn"
                                  onClick={() => savePromptRuleText(r)}
                                  disabled={promptBusy || !editingRule.text.trim()}
                                  aria-label={`Salvar premissa ${r.position}`}
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  onClick={() => setEditingRule(null)}
                                  disabled={promptBusy}
                                  aria-label={`Cancelar edição da premissa ${r.position}`}
                                >
                                  <Undo2 size={16} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div key={r.id} className={`item-card${r.is_active ? "" : " is-inactive"}`}>
                              <div className="item-card-info">
                                <span className="rule-number">{secao.numbered ? r.position : "•"}</span>
                                <div><span>{r.text}</span></div>
                              </div>
                              {!canEdit && !r.is_active && (
                                <div className="item-card-actions">
                                  <span className="badge-pill inactive">Desativada</span>
                                </div>
                              )}
                              {canEdit && !r.is_active && (
                                <div className="item-card-actions">
                                  <span className="badge-pill inactive">Desativada</span>
                                  <button
                                    type="button"
                                    className="icon-btn"
                                    onClick={() => runPromptWrite(
                                      async () => { await catalogApi.updatePromptRule(token, r.id, { is_active: true }); },
                                      "Não foi possível reativar a premissa."
                                    )}
                                    title="Reativar"
                                    aria-label={`Reativar premissa ${r.position}`}
                                    disabled={promptBusy}
                                  >
                                    <Undo2 size={16} />
                                  </button>
                                </div>
                              )}
                              {canEdit && r.is_active && (
                                <div className="item-card-actions">
                                  <button
                                    type="button"
                                    className="icon-btn"
                                    onClick={() => setEditingRule({ id: r.id, text: r.text })}
                                    disabled={promptBusy}
                                    aria-label={`Editar premissa ${r.position}`}
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    className="icon-btn-danger"
                                    onClick={() => runPromptWrite(
                                      async () => { await catalogApi.deactivatePromptRule(token, r.id); },
                                      "Não foi possível remover a premissa."
                                    )}
                                    aria-label={`Remover premissa ${r.position}`}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                          {regras.length === 0 && <p>Nenhuma premissa cadastrada.</p>}
                          {regras.length > 0 && regrasAtivas.length === 0 && <p>Nenhuma premissa ativa.</p>}
                        </div>
                        {canEdit && (
                          <>
                          <label className="form-field mt-3">
                            <span>Nova premissa{secao.numbered ? ` (${regrasAtivas.length}/${MAX_RULES_PER_SECTION})` : ""}</span>
                            <textarea
                              rows={3}
                              value={draft[`rule:${secao.rule}`] || ""}
                              onChange={e => setDraftValue(`rule:${secao.rule}`, e.target.value)}
                              placeholder="Ex: menção que destaca conquista da marca"
                            />
                            <small>
                              Uma premissa por linha — cole várias de uma vez.
                              {secao.numbered
                                ? ` O número ao lado de cada premissa é o que a IA cita ao responder (e o que a página de Justificativas mostra), por isso o limite existe: acima de ${MAX_RULES_PER_SECTION} o número sai da faixa desta seção e o sistema descarta a classificação.`
                                : " Esta seção não tem número nem limite: cada linha entra no prompt como item da lista."}
                            </small>
                          </label>
                          <div className="edit-form-actions">
                            <button
                              type="button"
                              className="secondary-button mt-3"
                              onClick={() => addPromptRule(secao.rule)}
                              disabled={promptBusy || cheia || !(draft[`rule:${secao.rule}`] || "").trim()}
                            >
                              <Plus size={16} /> {ruleButtonLabel(splitLines(draft[`rule:${secao.rule}`] || "").length, secao.numbered ? MAX_RULES_PER_SECTION - regrasAtivas.length : Infinity)}
                            </button>
                          </div>
                          </>
                        )}
                      </div>
                      )}
                    </div>
                  )}
                </div>
              );
            });

  return (
    <div className="ajustes-layout company-settings-glass">
      {/* ── Sub-Menu Lateral da Tela de Ajustes ── */}
      <aside className="ajustes-sidebar">
        <div className="ajustes-sidebar-header">
          <div className="company-badge-icon">
            <Building2 size={16} />
          </div>
          <div>
            <strong>{name}</strong>
            <span>Ajustes da operação</span>
          </div>
        </div>

        <nav className="ajustes-nav">
          {/* Ferramentas (2026-10-14): o que age sobre a configuração
              INTEIRA fica junto e em cima, cada um dizendo o que faz — o
              assistente monta com a IA; baixar e importar são a ida e a
              volta da mesma planilha. */}
          <div className="ajustes-tools" role="group" aria-label="Ferramentas de configuração">
            {companyId && canEdit && (
              <Link className="ajustes-tool is-primary" to={`/empresas/${companyId}/configuracoes/assistente`}>
                <span className="ajustes-tool-icon" aria-hidden="true"><WandSparkles size={16} /></span>
                <span className="ajustes-tool-copy">
                  <strong>Assistente de configuração</strong>
                  <small>A IA monta o cadastro com você, passo a passo</small>
                </span>
              </Link>
            )}
            <div className="ajustes-tool-pair">
              {/* Exportar é de Técnico na API (`GET /company-config/export`):
                  o Visualizador não vê o botão (2026-10-24). */}
              {canOperate(userProfile) && (
                <ConfigExportButton token={token} companyName={companyName} />
              )}
              {canEdit && TABS.filter(t => t.group === "ferramentas").map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={`ajustes-tool${activeTab === tab.id ? " active" : ""}`}
                  onClick={() => confirmLeave(() => setActiveTab(tab.id))}
                  title="Aplicar uma planilha de configuração editada"
                >
                  <span className="ajustes-tool-icon" aria-hidden="true"><Upload size={15} /></span>
                  <span className="ajustes-tool-copy">
                    <strong>{tab.label}</strong>
                    <small>Aplica uma planilha editada</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="ajustes-nav-section-title">EMPRESA</div>
          {TABS.filter(t => t.group === "empresa").map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            const selo = navStatus(tab, countFor(tab.id), countFor("subtemas"));
            return (
              <button
                key={tab.id}
                type="button"
                className={`ajustes-nav-item ${active ? "active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => confirmLeave(() => setActiveTab(tab.id))}
              >
                <Icon size={16} />
                <span className="ajustes-nav-item-body">
                  <span className="ajustes-nav-item-label">{tab.label}</span>
                  <span className="ajustes-nav-item-meta">
                    <span className={`status-dot-indicator status-${selo.dot}`} />
                    <small className="nav-badge-status">{selo.text}</small>
                  </span>
                </span>
              </button>
            );
          })}

          <div className="ajustes-nav-section-title">MONITORAMENTO</div>
          {TABS.filter(t => t.group === "monitoramento").map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            const selo = navStatus(tab, countFor(tab.id), countFor("subtemas"));
            return (
              <button
                key={tab.id}
                type="button"
                className={`ajustes-nav-item ${active ? "active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => confirmLeave(() => setActiveTab(tab.id))}
              >
                <Icon size={16} />
                <span className="ajustes-nav-item-body">
                  <span className="ajustes-nav-item-label">{tab.label}</span>
                  <span className="ajustes-nav-item-meta">
                    <span className={`status-dot-indicator status-${selo.dot}`} />
                    <small className="nav-badge-status">{selo.text}</small>
                  </span>
                </span>
              </button>
            );
          })}

          <div className="ajustes-nav-section-title">INTELIGÊNCIA ARTIFICIAL</div>
          {TABS.filter(t => t.group === "ia").map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            const selo = navStatus(tab, countFor(tab.id), countFor("subtemas"));
            return (
              <button
                key={tab.id}
                type="button"
                className={`ajustes-nav-item ${active ? "active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => confirmLeave(() => setActiveTab(tab.id))}
              >
                <Icon size={16} />
                <span className="ajustes-nav-item-body">
                  <span className="ajustes-nav-item-label">{tab.label}</span>
                  <span className="ajustes-nav-item-meta">
                    <span className={`status-dot-indicator status-${selo.dot}`} />
                    <small className="nav-badge-status">{selo.text}</small>
                  </span>
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Painel de Conteúdo dos Ajustes ── */}
      <main className="ajustes-content">
        {/* Sempre visível, acima das abas. Quem só lê (Visualizador, Técnico)
            vê o estado e o "Resolver", sem o botão de criar canais. */}
        <CompanyChecklistCard
          checklist={configChecklist.checklist}
          loading={configChecklist.loading}
          error={configChecklist.error}
          onRetry={() => void reloadChecklist()}
          settingsHref={tab => `?tab=${tab}`}
          onOpenTab={tab => {
            if (TABS.some(candidate => candidate.id === tab)) {
              confirmLeave(() => setActiveTab(tab as TabId));
            }
          }}
          onStandardChannels={canEdit ? configChecklist.createStandardChannels : undefined}
          companyId={companyId}
        />
        {/* TAB: Dados da empresa */}
        {activeTab === "geral" && (
          // O `form` fica POR FORA do painel e sem caixa: o cartão é o
          // `fieldset` lá dentro, que trava os campos abaixo de Especialista.
          // Com o `fieldset` entre o cartão e as seções (2026-10-24), as
          // regras `.panel-section-group > …` e `.company-settings > …`
          // deixaram de casar e a tela perdeu padding, divisórias e as duas
          // colunas (2026-10-26).
          <form onSubmit={handleSave} style={{ display: "contents" }}>
          <div className="ajustes-panel">
            {companyDetailError && <div className="login-error">{companyDetailError}</div>}
            {autofillNotice && <div className="panel-notice"><p>{autofillNotice}</p></div>}
            {/* Sem o perfil em mãos NÃO há formulário: ele nascia com os
                padrões do `useState` (escala de 4 níveis, CENA ligado, sem
                logo) e "Salvar" habilitado, e quem salvasse depois de uma
                piscada de rede gravava os padrões por cima do perfil real
                (teste de render, 2026-10-09). Falha de carga é erro com
                "tentar de novo", nunca um formulário inventado. */}
            {!profile ? (
              companyDetailError ? (
                <ApiState kind="error" onRetry={() => void loadCompanyDetail()}>
                  Os dados da empresa não carregaram.
                </ApiState>
              ) : (
                <LoaderCircle className="spin" size={20} />
              )
            ) : (
              // Só leitura abaixo de Especialista (2026-10-24): campos
              // travados, sem salvar.
                <fieldset disabled={!canEdit} className="panel-section-group company-settings">
                  <header className="panel-header">
                    <div>
                      <h2>Dados da empresa</h2>
                      <p>
                        O cadastro básico do cliente: nome, identidade visual e o que o
                        sistema usa para reconhecer citações à empresa.
                      </p>
                    </div>
                  </header>

                  <nav className="prompt-index company-settings-index" aria-label="Ir para a parte do cadastro">
                    <a href="#empresa-identificacao">Identificação</a>
                    <a href="#empresa-visual">Identidade visual</a>
                    <a href="#empresa-monitoramento">Monitoramento</a>
                    {canConfigureCena && <a href="#empresa-cena">Índice e escala</a>}
                    <a href="#empresa-citacao">Citação no título</a>
                  </nav>

                  <div className="panel-section ajustes-block" id="empresa-identificacao">
                    <h3><Building2 size={17} aria-hidden="true" />Identificação</h3>
                    <div className="company-name-row">
                      <label className="form-field">
                        <span>Nome da empresa <b aria-hidden="true">*</b></span>
                        <input value={name} onChange={e => setName(e.target.value)} required maxLength={180} />
                      </label>
                      {canEdit && (
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={autofillFromAI}
                          disabled={autofilling || !name.trim()}
                        >
                          {autofilling ? <LoaderCircle className="spin" size={16} /> : <WandSparkles size={16} />}
                          Preencher com IA
                        </button>
                      )}
                    </div>
                    <small className="settings-card-help">
                      A partir do nome, a IA sugere apelidos, slogans, produtos, iniciativas e
                      campanhas para a detecção de citação, e indica se é instituição
                      governamental. <strong>Nada é gravado automaticamente</strong> — as
                      sugestões preenchem os campos abaixo para você revisar antes de salvar.
                    </small>
                  </div>

                  <div className="panel-section ajustes-block" id="empresa-visual">
                    <h3><ImageIcon size={17} aria-hidden="true" />Identidade visual</h3>
                    {/* Uma logo só (2026-10-16): a em cores é a que as telas mostram.
                        Escolher uma nova grava na em cores e limpa a em cinza que
                        tenha sobrado; remover limpa as duas. */}
                    <CompanyLogoField
                      value={colorLogo ?? grayLogo}
                      onChange={value => {
                        setColorLogo(value);
                        setGrayLogo(null);
                      }}
                      onError={setCompanyDetailError}
                    />
                  </div>

                  <div className="panel-section ajustes-block" id="empresa-monitoramento">
                    <h3><Target size={17} aria-hidden="true" />Comportamento do monitoramento</h3>
                    <div className="setting-rows">
                      <label className="form-field-inline setting-row">
                        <input
                          type="checkbox"
                          checked={hasCompetitorAnalysis}
                          onChange={e => setHasCompetitorAnalysis(e.target.checked)}
                        />
                        <span className="setting-row-copy">
                          <strong>Esta empresa tem análise de concorrentes</strong>
                        </span>
                      </label>
                      <label className="form-field-inline setting-row">
                        <input
                          type="checkbox"
                          checked={isGovernment}
                          onChange={e => setIsGovernment(e.target.checked)}
                        />
                        <span className="setting-row-copy">
                          <strong>É instituição governamental</strong>
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Especialista ou acima (2026-10-22; antes, só o topo). */}
                  {canConfigureCena && (
                  <div className="panel-section ajustes-block" id="empresa-cena">
                    <h3><Gauge size={17} aria-hidden="true" />CENA</h3>
                    <p className="section-hint">
                      O índice é <strong>sempre calculado</strong> — estas opções mudam
                      o que aparece nas telas e o que entra na pontuação, não o cálculo
                      em si.
                    </p>
                    <div className="setting-rows">
                      <label className="form-field-inline setting-row">
                        <input
                          type="checkbox"
                          checked={displaysCena}
                          onChange={e => setDisplaysCena(e.target.checked)}
                        />
                        <span className="setting-row-copy">
                          <strong>Exibir o índice CENA</strong>
                          <small>
                            Desligar oculta o índice das telas de menções, visão geral e o
                            amostra do menu. Os números continuam sendo calculados e voltam
                            ao religar.
                          </small>
                        </span>
                      </label>
                      <label className="form-field-inline setting-row">
                        <input
                          type="checkbox"
                          checked={publisherGroupCounts}
                          disabled={!displaysCena}
                          onChange={e => setPublisherGroupCounts(e.target.checked)}
                        />
                        <span className="setting-row-copy">
                          <strong>Considerar grupo de publicador na pontuação</strong>
                          <small>
                            Quando ligado, o peso do grupo cadastrado compete com a faixa de
                            seguidores — vale o maior. Com a exibição do CENA desligada a
                            <strong> edição</strong> fica bloqueada, mas o valor atual continua
                            valendo no cálculo.
                          </small>
                        </span>
                      </label>
                    </div>
                  </div>
                  )}

                  {/* Seção PRÓPRIA, e não dentro da "CENA": o hint dali
                      jura que as opções "não mudam o cálculo em si" — a
                      escala muda o motor E, via peso do neutro, se a
                      menção ganha nota. Mesmo gate da seção do CENA. */}
                  {canConfigureCena && (
                  <div className="panel-section ajustes-block">
                    <h3><SlidersHorizontal size={17} aria-hidden="true" />Escala de sentimento</h3>
                    <p className="section-hint">
                      Em quantos níveis a classificação desta empresa trabalha.
                      Vale para uploads e correções <strong>daqui pra frente</strong> —
                      menção já classificada mantém o rótulo que tem.
                    </p>
                    <label className="form-field">
                      <span>Escala</span>
                      <Select
                        value={polarityScale}
                        onChange={e =>
                          setPolarityScale(
                            e.target.value as "four_level" | "three_level"
                          )
                        }
                      >
                        <option value="four_level">
                          4 níveis — Positivo, Favorável, Desfavorável, Negativo
                        </option>
                        <option value="three_level">
                          3 níveis — Positivo, Neutro, Negativo
                        </option>
                      </Select>
                      <small>
                        Na escala de 4 níveis, "neutro" não é resposta final: a IA
                        desempata pra favorável ou desfavorável, e um neutro que
                        sobrar vira aviso no upload. Na de 3, o neutro é resposta
                        legítima — e, enquanto o peso dele não for definido no CENA,
                        menção neutra fica <strong>sem nota</strong> (não 5,0).
                      </small>
                    </label>
                  </div>
                  )}

                  {canConfigureCena && (
                    // Os pesos só carregam quando a seção abre: abrir a aba não
                    // pede nada além do que ela mostra (teste de abertura).
                    <details
                      className="panel-section ajustes-block company-cena-weights"
                      onToggle={event => setCenaWeightsOpen(event.currentTarget.open)}
                    >
                      <summary>
                        <h3><SlidersHorizontal size={17} aria-hidden="true" />Pesos do CENA desta empresa</h3>
                      </summary>
                      {cenaWeightsOpen && <CompanyCenaWeights token={token} canEdit={canConfigureCena} />}
                    </details>
                  )}

                  <div className="panel-section ajustes-block" id="empresa-citacao">
                    <h3><Quote size={17} aria-hidden="true" />Detecção de citação no título</h3>
                    <p className="section-hint">
                      Termos que, aparecendo no <strong>título</strong> de uma publicação,
                      pontuam na categoria "Citação no Título" do CENA. É determinístico: o
                      termo casa ou não casa, sem IA no meio — então tudo que precisa pontuar
                      tem que estar aqui.
                    </p>
                    <p className="section-hint">
                      Cada termo é uma lista, não uma expressão: casa como texto literal dentro
                      do título, sem diferenciar maiúscula nem acento, e basta um bater. Entre
                      barras (<code>/.../</code>) vira expressão regular. Sem AND/OR/NOT; aspas
                      e <code>=</code> não têm efeito aqui.
                    </p>
                    <div className="citation-grid">
                    {CITATION_CATEGORIES.map(categoria => {
                      const grupo = citationTags.find(t => t.category === categoria);
                      const termos = grupo?.tags ?? [];
                      const chave = `citacao:${categoria}`;
                      const rotulo = CITATION_LABELS[categoria] ?? categoria;
                      const adicionar = () => {
                        // `;` é o separador de armazenamento: o servidor
                        // recusa tag que o contenha, então quebrar aqui
                        // é obrigatório, não só conveniência.
                        const novos = splitEntries(draft[chave] || "");
                        if (novos.length === 0) return;
                        setCitationTags(prev => {
                          const atuais = prev.find(t => t.category === categoria)?.tags ?? [];
                          const resto = prev.filter(t => t.category !== categoria);
                          return [
                            ...resto,
                            {
                              category: categoria,
                              tags: [...atuais, ...novos.filter(n => !atuais.includes(n))]
                            }
                          ];
                        });
                        setDraftValue(chave, "");
                      };
                      return (
                        <div className="form-field citation-card" key={categoria}>
                          <span>
                            {rotulo}{" "}
                            <small className="citation-count">{termos.length}</small>
                          </span>
                          {canEdit && (
                            <AddTermBox
                              value={draft[chave] || ""}
                              onChange={value => setDraftValue(chave, value)}
                              onAdd={adicionar}
                              placeholder="Digite um termo e tecle Enter — vários separados por ;"
                              disabled={false}
                              label={`Adicionar a ${rotulo}`}
                            />
                          )}
                          <div className="tag-pill-list">
                            {termos.map(termo => (
                              <span key={termo} className="tag-pill">
                                {termo}
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const restantes = termos.filter(t => t !== termo);
                                      setCitationTags(prev => {
                                        const resto = prev.filter(t => t.category !== categoria);
                                        return restantes.length
                                          ? [...resto, { category: categoria, tags: restantes }]
                                          : resto;
                                      });
                                      if (grupo && restantes.length === 0) {
                                        void setCitationCategory(categoria, []);
                                      }
                                    }}
                                    aria-label={`Remover ${termo}`}
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                            {termos.length === 0 && <small>Nenhum termo.</small>}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                    <small className="settings-card-help">{MULTI_ENTRY_HINT}</small>
                  </div>

                  {canEdit && (
                    <div className="admin-savebar company-settings-savebar">
                      <span className="admin-savebar-status">
                        Nome, logos, opções e termos de citação só valem depois de salvar.
                      </span>
                      <div className="admin-savebar-actions">
                        <button type="submit" className="primary-button" disabled={saving}>
                          {saving ? <LoaderCircle className="spin" size={16} /> : saveSuccess ? <Check size={16} /> : <Save size={16} />}
                          {saving ? "Salvando..." : saveSuccess ? "Salvo com sucesso!" : "Salvar alterações"}
                        </button>
                      </div>
                    </div>
                  )}
                </fieldset>
            )}
          </div>
          </form>
        )}

        {/* TAB: Marcas da Empresa */}
        {activeTab === "marcas" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Marcas da empresa</h2>
                <p>Gerencie marcas principais, marcas secundárias e identidades visuais da empresa.</p>
              </div>
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewBrand}>
                    <Plus size={16} /> Nova marca
                  </button>
                )}
              </div>
            </header>

            {brandsError && openForm !== "brand" && <div className="login-error">{brandsError}</div>}

            {openForm === "brand" && (
              <SettingsFormDialog
                eyebrow="MARCAS"
                title={editingBrandId !== null ? "Editar marca" : "Nova marca"}
                editing={editingBrandId !== null}
                values={{ newBrandName, newBrandTerms, newBrandIsMain }}
                busy={creatingBrand}
                required={brandRequired}
                saveLabel={editingBrandId !== null ? "Salvar alterações" : "Cadastrar marca"}
                error={brandsError}
                onSave={() => void addBrand()}
                onClose={resetBrandForm}
              >
              <label className="form-field">
                <span>Nome da marca <b aria-hidden="true">*</b></span>
                <input value={newBrandName} onChange={e => setNewBrandName(e.target.value)} placeholder="Marca" />
              </label>
              <label className="form-field mt-3">
                <span>Termos</span>
                <TermsEditor
                  value={newBrandTerms}
                  onChange={setNewBrandTerms}
                  placeholder={'"marca alfa" OR alfa NOT "alfa romeu" — Enter ou ; fecha a expressão'}
                />
                <small>
                  Uma expressão por chip — basta UMA casar para a publicação ser desta marca.
                  Cada expressão aceita <code>AND</code>, <code>OR</code>, <code>NOT</code> e
                  aspas para termos compostos; o servidor valida cada uma antes de salvar.
                  Sem aspas casa em qualquer lugar (<code>safra</code> acha "SafraPay"); com
                  aspas casa a palavra inteira, sem ligar para maiúscula nem acento; com{" "}
                  <code>=</code> antes das aspas (<code>="Corsa"</code>) casa só este texto
                  exato — maiúscula e acento contam.
                </small>
              </label>
              <label className="form-field-inline mt-3">
                <input type="checkbox" checked={newBrandIsMain} onChange={e => setNewBrandIsMain(e.target.checked)} />
                Marca principal — só uma por empresa; marcar esta rebaixa a atual automaticamente
              </label>
              </SettingsFormDialog>
            )}

            <div className="panel-section">
              <h3>Marcas registradas ({visibleBrands.length})</h3>
              <ListSearch
                value={searchOf("marcas")}
                onChange={setSearchOf("marcas")}
                placeholder="Buscar marca por nome ou termo"
                shown={shownBrands.length}
                total={visibleBrands.length}
              />
              <div className="list-cards-grid">
                {shownBrands.map(brand => (
                  <div key={brand.id} className="item-card">
                    <div className="item-card-info">
                      <Tag size={20} className="item-card-icon" />
                      <div>
                        <strong>{brand.name}{brand.is_main_brand ? " (marca principal)" : ""}</strong>
                        <TermChips terms={brand.terms} />
                      </div>
                    </div>
                    <div className="item-card-actions">
                      {canEdit && (
                        <button type="button" className="icon-btn" onClick={() => startEditBrand(brand)} aria-label={`Editar ${brand.name}`}><Pencil size={16} /></button>
                      )}
                      {!canEdit ? null : brand.is_main_brand ? (
                        /* A principal não desativa — o servidor recusa
                           com 409. Desabilitado com o motivo é mais
                           honesto que esconder: a pessoa vê que a ação
                           existe e por que não vale aqui. */
                        <button
                          type="button"
                          className="icon-btn-danger"
                          disabled
                          title="A marca principal não pode ser desativada — promova outra marca a principal antes"
                          aria-label={`Desativar ${brand.name} (indisponível: é a marca principal)`}
                        >
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <ActiveActions
                          canEdit={canEdit}
                          isActive={brand.is_active}
                          deletedOn={brand.deleted_on}
                          label={brand.name}
                          onToggle={active => void toggleBrandActive(brand, active)}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {visibleBrands.length === 0 && <p>Nenhuma marca cadastrada.</p>}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Concorrentes */}
        {activeTab === "concorrentes" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Concorrentes</h2>
                <p>
                  Empresas e marcas concorrentes monitoradas para benchmarking de mercado.
                  Cada concorrente vira uma empresa própria no sistema, sem usuário
                  nenhum vinculado — é o que permite um concorrente ter o mesmo nome de
                  um cliente sem os dois se misturarem.
                </p>
              </div>
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewCompetitor}>
                    <Plus size={16} /> Novo concorrente
                  </button>
                )}
              </div>
            </header>

            {concorrentesError && openForm !== "competitor" && <div className="login-error">{concorrentesError}</div>}

            {openForm === "competitor" && (
              <SettingsFormDialog
                eyebrow="CONCORRENTES"
                title={editingCompetitorId !== null ? "Editar concorrente" : "Novo concorrente"}
                editing={editingCompetitorId !== null}
                values={{ newCompetitor, newCompetitorTerms }}
                busy={creatingCompetitor}
                required={competitorRequired}
                saveLabel={editingCompetitorId !== null ? "Salvar alterações" : "Adicionar concorrente"}
                error={concorrentesError}
                onSave={() => void saveCompetitor()}
                onClose={resetCompetitorForm}
              >
              <label className="form-field">
                <span>Nome <b aria-hidden="true">*</b></span>
                <input
                  placeholder="Nome do concorrente"
                  value={newCompetitor}
                  onChange={e => setNewCompetitor(e.target.value)}
                />
              </label>
              <label className="form-field mt-3">
                <span>Termos <b aria-hidden="true">*</b></span>
                <TermsEditor
                  placeholder={'"concorrente x" OR concorrentex — Enter ou ; fecha a expressão'}
                  value={newCompetitorTerms}
                  onChange={setNewCompetitorTerms}
                />
                <small>
                  Obrigatório, diferente das marcas da própria empresa: a identificação de
                  concorrente pula quem não tem termos, então um concorrente sem eles fica
                  cadastrado sem nunca capturar nada. Mesmo formato das marcas: uma expressão
                  por chip, cada uma com <code>AND</code>, <code>OR</code>, <code>NOT</code> e
                  aspas; <code>="Texto"</code> exige o texto exato, com maiúscula e acento.
                </small>
              </label>
              </SettingsFormDialog>
            )}

            <div className="panel-section">
              <h3>Concorrentes monitorados ({concorrentes.length})</h3>
              <ListSearch
                value={searchOf("concorrentes")}
                onChange={setSearchOf("concorrentes")}
                placeholder="Buscar concorrente por nome ou termo"
                shown={shownCompetitors.length}
                total={concorrentes.length}
              />
              {concorrentesLoading && concorrentes.length === 0 ? (
                <LoaderCircle className="spin" size={20} />
              ) : (
                <div className="list-cards-grid">
                  {shownCompetitors.map(item => (
                    <div key={item.id} className={`item-card${item.is_active ? "" : " is-inactive"}`}>
                      <div className="item-card-info">
                        <Target size={20} className="item-card-icon" />
                        <div>
                          <strong>{item.name}</strong>
                          <TermChips terms={item.terms} />
                        </div>
                      </div>
                      <div className="item-card-actions">
                        <ActiveActions
                          canEdit={canEdit}
                          isActive={item.is_active}
                          deletedOn={item.deleted_on}
                          onToggle={active => setCompetitorActive(item.id, active)}
                          label={item.name}
                        />
                        {canEdit && (
                          <button type="button" className="icon-btn" onClick={() => startEditCompetitor(item)} aria-label={`Editar ${item.name}`}>
                            <Pencil size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {concorrentes.length === 0 && <p>Nenhum concorrente monitorado.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Porta-vozes */}
        {activeTab === "portavozes" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Porta-vozes</h2>
                <p>
                  Executivos e porta-vozes oficiais da empresa acompanhados em citações.
                  O cadastro é por MARCA: um porta-voz aplicado a três marcas aparece
                  três vezes na listagem, uma para cada.
                </p>
              </div>
              <CatalogOwnerPicker
                competitors={catalogCompetitors}
                value={catalogOwner}
                onChange={setCatalogOwner}
              />
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewSpokesperson}>
                    <Plus size={16} /> Novo porta-voz
                  </button>
                )}
              </div>
            </header>

            {spokespersonsError && openForm !== "spokesperson" && <div className="login-error">{spokespersonsError}</div>}

            {openForm === "spokesperson" && (
              <SettingsFormDialog
                eyebrow="PORTA-VOZES"
                title={editingSpokespersonId !== null ? "Editar porta-voz" : "Novo porta-voz"}
                editing={editingSpokespersonId !== null}
                values={{
                  newSpokespersonName,
                  newSpokespersonBrandIds,
                  newSpokespersonCountsCena,
                  newSpokespersonValidUntil,
                  newSpokespersonVariations,
                  newVariation
                }}
                busy={creatingSpokesperson}
                required={spokespersonRequired}
                saveLabel={editingSpokespersonId !== null ? "Salvar alterações" : "Cadastrar porta-voz"}
                error={spokespersonsError}
                onSave={() => void addSpokesperson()}
                onClose={resetSpokespersonForm}
              >
                <label className="form-field">
                  <span>Nome <b aria-hidden="true">*</b></span>
                  <input
                    placeholder="Ex: Carlos Eduardo Silva"
                    value={newSpokespersonName}
                    onChange={e => setNewSpokespersonName(e.target.value)}
                  />
                  <small>O próprio nome também é procurado no texto da publicação, junto com as variações.</small>
                </label>

                <div className="grid-fields-2 mt-3">
                  <div className="form-field">
                    <span>Marcas <b aria-hidden="true">*</b></span>
                    <div className="brand-picker">
                      {pickerBrands.length === 0 && <small>Nenhuma marca cadastrada.</small>}
                      {pickerBrands.map(brand => (
                        <label key={brand.id} className="brand-picker-item">
                          <input
                            type="checkbox"
                            checked={newSpokespersonBrandIds.includes(brand.id)}
                            disabled={editingSpokespersonId !== null && !newSpokespersonBrandIds.includes(brand.id)}
                            onChange={() => toggleNewSpokespersonBrand(brand.id)}
                          />
                          {brand.name}
                        </label>
                      ))}
                    </div>
                    {editingSpokespersonId !== null && (
                      <small>
                        Na edição a marca é uma só — cada linha da listagem é o vínculo com
                        UMA marca. Pra aplicar a outra, cadastre de novo escolhendo ela.
                      </small>
                    )}
                  </div>
                  <div className="form-field">
                    <span>Validade e pontuação</span>
                    <label className="form-field-inline">
                      <input
                        type="checkbox"
                        checked={newSpokespersonCountsCena}
                        onChange={e => setNewSpokespersonCountsCena(e.target.checked)}
                      />
                      Conta na pontuação de porta-voz (CENA)
                    </label>
                    <input
                      type="date"
                      className="mt-3"
                      value={newSpokespersonValidUntil}
                      onChange={e => setNewSpokespersonValidUntil(e.target.value)}
                    />
                    <small>
                      Data de saída da empresa. Depois dela o porta-voz para de contar, mas
                      as menções antigas continuam atribuídas a ele. Vazio = sem saída.
                    </small>
                  </div>
                </div>

                <div className="form-field mt-3">
                  <span>Variações</span>
                  <div className="inline-add-box">
                    <input
                      placeholder="Digite uma variação e adicione — vários separados por ;"
                      value={newVariation}
                      onChange={e => setNewVariation(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addVariation())}
                    />
                    <button type="button" className="secondary-button" onClick={addVariation}>
                      <Plus size={16} /> Adicionar
                    </button>
                  </div>
                  <small>{MULTI_ENTRY_HINT}</small>
                  <small>
                    Uma lista, não uma expressão: cada variação casa como texto literal dentro
                    da publicação, sem diferenciar maiúscula nem acento; basta uma bater. Entre
                    barras (<code>/.../</code>) vira expressão regular. Aspas e <code>=</code>{" "}
                    não têm efeito aqui.
                  </small>
                  {newSpokespersonVariations.length > 0 && (
                    <div className="tag-pill-list">
                      {newSpokespersonVariations.map((variation, index) => (
                        <span key={`${variation}-${index}`} className="tag-pill">
                          {variation}
                          <button type="button" onClick={() => removeVariation(index)} aria-label={`Remover ${variation}`}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

              </SettingsFormDialog>
            )}

            <div className="panel-section">
              <h3>Porta-vozes cadastrados ({spokespersons.length})</h3>
              <ListSearch
                value={searchOf("portavozes")}
                onChange={setSearchOf("portavozes")}
                placeholder="Buscar porta-voz por nome, variação ou marca"
                shown={shownSpokespersons.length}
                total={spokespersons.length}
              />
              {spokespersonsLoading && spokespersons.length === 0 ? (
                <LoaderCircle className="spin" size={20} />
              ) : (
                <div className="list-cards-grid">
                  {shownSpokespersons.map(p => (
                    <div key={p.id} className={`item-card${p.is_active ? "" : " is-inactive"}`}>
                      <div className="item-card-info">
                        <Mic2 size={20} className="item-card-icon" />
                        <div>
                          <strong>{p.name}{p.counts_toward_spokesperson_score ? " (CENA)" : ""}</strong>
                          <span>
                            {brandName(p.brand_id)}
                            {p.name_variations ? ` · ${p.name_variations.split(";").join(", ")}` : ""}
                            {p.valid_until ? ` · até ${dayLabel(p.valid_until)}` : ""}
                          </span>
                        </div>
                      </div>
                      <div className="item-card-actions">
                        <ActiveActions
                          canEdit={canEdit}
                          isActive={p.is_active}
                          deletedOn={p.deleted_on}
                          onToggle={active => setSpokespersonActive(p.id, active)}
                          label={p.name}
                        />
                        {canEdit && (
                          <button type="button" className="icon-btn" onClick={() => startEditSpokesperson(p)} aria-label={`Editar ${p.name}`}>
                            <Pencil size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {spokespersons.length === 0 && <p>Nenhum porta-voz cadastrado.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Mensagem-Chave */}
        {activeTab === "mensagens" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Mensagem-chave</h2>
                <p>
                  Configure as mensagens-chave usadas na classificação de menções.
                  Uma por marca — a mesma mensagem aplicada a duas marcas vira duas
                  linhas.
                </p>
              </div>
              <CatalogOwnerPicker
                competitors={catalogCompetitors}
                value={catalogOwner}
                onChange={setCatalogOwner}
              />
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewKeyMessage}>
                    <Plus size={16} /> Nova mensagem-chave
                  </button>
                )}
              </div>
            </header>
            {keyMessagesError && openForm !== "keyMessage" && <div className="login-error">{keyMessagesError}</div>}
            {openForm === "keyMessage" && (
              <SettingsFormDialog
                eyebrow="MENSAGEM-CHAVE"
                title={editingKeyMessageId !== null ? "Editar mensagem-chave" : "Nova mensagem-chave"}
                editing={editingKeyMessageId !== null}
                values={{
                  newKeyMessageBrandId,
                  newKeyMessageSource,
                  newKeyMessageText,
                  newKeyMessageRules,
                  newKeyMessageExclusions
                }}
                busy={creatingKeyMessage}
                required={keyMessageRequired}
                saveLabel={editingKeyMessageId !== null ? "Salvar alterações" : "Adicionar mensagem-chave"}
                error={keyMessagesError}
                onSave={() => void addKeyMessage()}
                onClose={resetKeyMessageForm}
              >
              <div className="grid-fields-2">
                <label className="form-field">
                  <span>Marca <b aria-hidden="true">*</b></span>
                  <Select value={newKeyMessageBrandId} onChange={e => setNewKeyMessageBrandId(e.target.value)}>
                    <option value="">Selecione uma marca</option>
                    {pickerBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </Select>
                </label>
                <label className="form-field">
                  <span>Fonte de dados</span>
                  <Select value={newKeyMessageSource} onChange={e => setNewKeyMessageSource(e.target.value as "press" | "network")}>
                    <option value="network">Redes sociais</option>
                    <option value="press">Imprensa</option>
                  </Select>
                </label>
              </div>
              <label className="form-field mt-3">
                <span>Mensagem <b aria-hidden="true">*</b></span>
                <textarea rows={3} value={newKeyMessageText} onChange={e => setNewKeyMessageText(e.target.value)} />
              </label>
              <label className="form-field mt-3">
                <span>Critério de aplicação <b aria-hidden="true">*</b></span>
                <textarea rows={3} value={newKeyMessageRules} onChange={e => setNewKeyMessageRules(e.target.value)} />
                <small>
                  Texto livre que vai DENTRO do prompt ("use esta mensagem quando…").
                  Obrigatório: sem critério, a IA escolhe a mensagem-chave por conta
                  própria.
                </small>
                <small>
                  Texto para a IA, não expressão: cada critério separado por <code>;</code> vira
                  uma linha do prompt, e é a IA que julga se a mensagem se aplica.
                </small>
              </label>
              <label className="form-field mt-3">
                <span>Critério de exclusão</span>
                <textarea rows={2} value={newKeyMessageExclusions} onChange={e => setNewKeyMessageExclusions(e.target.value)} />
                <small>
                  Quando NÃO usar esta mensagem. Opcional. Também é texto para a IA: cada
                  critério separado por <code>;</code> vira uma linha do prompt.
                </small>
              </label>
              </SettingsFormDialog>
            )}
            <div className="panel-section">
              <h3>Mensagens cadastradas ({keyMessages.length})</h3>
              <ListSearch
                value={searchOf("mensagens")}
                onChange={setSearchOf("mensagens")}
                placeholder="Buscar mensagem por texto, regra ou marca"
                shown={shownKeyMessages.length}
                total={keyMessages.length}
              />
              <div className="list-cards-grid">
                {shownKeyMessages.map(km => (
                  <div key={km.id} className={`item-card${km.is_active ? "" : " is-inactive"}`}>
                    <div className="item-card-info">
                      <BookOpen size={20} className="item-card-icon" />
                      <div>
                        <strong>{brandName(km.brand_id)} · {km.data_source === "press" ? "Imprensa" : "Redes sociais"}</strong>
                        <span>{km.message}</span>
                      </div>
                    </div>
                    <div className="item-card-actions">
                      <ActiveActions
                          canEdit={canEdit}
                        isActive={km.is_active}
                        deletedOn={km.deleted_on}
                        onToggle={active => setKeyMessageActive(km.id, active)}
                        label={`mensagem-chave de ${brandName(km.brand_id)}`}
                      />
                      {canEdit && (
                        <button type="button" className="icon-btn" onClick={() => startEditKeyMessage(km)} aria-label={`Editar mensagem-chave de ${brandName(km.brand_id)}`}><Pencil size={16} /></button>
                      )}
                    </div>
                  </div>
                ))}
                {keyMessages.length === 0 && <p>Nenhuma mensagem-chave cadastrada.</p>}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Temas e subtemas (o grupo de tema e o tema do código) */}
        {activeTab === "temas" && (
          <div className="ajustes-panel themes-panel">
            <header className="panel-header">
              <div>
                <h2>Temas e subtemas</h2>
                <p>Cada tema agrupa os subtemas que a classificação procura.</p>
              </div>
              <CatalogOwnerPicker
                competitors={catalogCompetitors}
                value={catalogOwner}
                onChange={setCatalogOwner}
              />
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {/* Baixar é de Técnico (a exportação da configuração); a
                    escrita é de Especialista. */}
                {canOperate(userProfile) && (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={downloadingThemes}
                    onClick={() => void downloadThemes()}
                  >
                    {downloadingThemes ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
                    Baixar planilha
                  </button>
                )}
                {canEdit && (
                  <CatalogImportButton
                    token={token}
                    companyName={companyName}
                    kind="themes"
                    onFinished={onThemesImported}
                  />
                )}
                {canEdit && (
                  <button type="button" className="secondary-button" onClick={openNewTheme}>
                    <Plus size={16} /> Novo subtema
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewThemeGroup}>
                    <Plus size={16} /> Novo tema
                  </button>
                )}
              </div>
            </header>
            {themeGroupsError && openForm !== "themeGroup" && <div className="login-error">{themeGroupsError}</div>}
            {themesError && openForm !== "theme" && <div className="login-error">{themesError}</div>}
            {themeGroupNotice && <div className="panel-notice"><p>{themeGroupNotice}</p></div>}
            {openForm === "themeGroup" && (
              <SettingsFormDialog
                eyebrow="TEMAS"
                title={editingThemeGroupId !== null ? "Editar tema" : "Novo tema"}
                editing={editingThemeGroupId !== null}
                values={{
                  newThemeGroupName,
                  newThemeGroupWeight,
                  newGroupThemeNames,
                  groupThemeBrandIds
                }}
                busy={creatingThemeGroup}
                required={themeGroupRequired}
                saveLabel={
                  editingThemeGroupId !== null
                    ? "Salvar alterações"
                    : groupThemeCount > 0
                      ? `Adicionar tema e ${groupThemeCount} ${groupThemeCount === 1 ? "subtema" : "subtemas"}`
                      : "Adicionar tema"
                }
                error={themeGroupsError}
                wide
                onSave={() => void addThemeGroup()}
                onClose={resetThemeGroupForm}
              >
              <div className="grid-fields-2">
                <label className="form-field">
                  <span>Nome <b aria-hidden="true">*</b></span>
                  <input placeholder="Nome do tema" value={newThemeGroupName} onChange={e => setNewThemeGroupName(e.target.value)} />
                </label>
                <label className="form-field">
                  <span>Peso</span>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Sem peso"
                    value={newThemeGroupWeight}
                    onChange={e => setNewThemeGroupWeight(e.target.value)}
                  />
                  <small>Multiplicador do tema no cálculo. Vazio = sem peso próprio.</small>
                </label>
              </div>
              {editingThemeGroupId === null && (
                <div className="grid-fields-2 mt-3">
                  <label className="form-field">
                    <span>Subtemas deste tema</span>
                    <TermsEditor
                      value={newGroupThemeNames}
                      onChange={value => { setNewGroupThemeNames(value); setThemeGroupNotice(null); }}
                      placeholder="Nome do subtema"
                    />
                    <small>Um subtema por nome, em cada marca marcada. Termos e premissas você cadastra depois, editando o subtema.</small>
                  </label>
                  <div className="form-field">
                    <span>Marcas dos subtemas</span>
                    <div className="brand-picker">
                      {pickerBrands.length === 0 && <small>Nenhuma marca cadastrada.</small>}
                      {pickerBrands.map(b => (
                        <label key={b.id} className="brand-picker-item">
                          <input
                            type="checkbox"
                            checked={groupThemeBrandIds.includes(b.id)}
                            onChange={() => setNewGroupThemeBrandIds(
                              groupThemeBrandIds.includes(b.id)
                                ? groupThemeBrandIds.filter(id => id !== b.id)
                                : [...groupThemeBrandIds, b.id]
                            )}
                          />
                          {b.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              </SettingsFormDialog>
            )}
            {openForm === "theme" && (
              <SettingsFormDialog
                eyebrow="SUBTEMAS"
                title={editingThemeId !== null ? "Editar subtema" : "Novo subtema"}
                editing={editingThemeId !== null}
                values={{
                  newThemeName,
                  newThemeBrandIds,
                  newThemeGroupId,
                  newThemeTerms,
                  newThemeDetection,
                  newThemeNonDetection,
                  newThemeMode,
                  newThemeScope
                }}
                busy={creatingTheme}
                required={themeRequired}
                saveLabel={editingThemeId !== null ? "Salvar alterações" : "Adicionar subtema"}
                error={themesError}
                wide
                onSave={() => void addTheme()}
                onClose={resetThemeForm}
              >
              <label className="form-field">
                <span>Nome do subtema <b aria-hidden="true">*</b></span>
                <input value={newThemeName} onChange={e => setNewThemeName(e.target.value)} />
              </label>
              <div className="grid-fields-2 mt-3">
                <div className="form-field">
                  <span>Marcas <b aria-hidden="true">*</b></span>
                  <div className="brand-picker">
                    {pickerBrands.length === 0 && <small>Nenhuma marca cadastrada.</small>}
                    {pickerBrands.map(b => (
                      <label key={b.id} className="brand-picker-item">
                        <input
                          type="checkbox"
                          checked={newThemeBrandIds.includes(b.id)}
                          disabled={editingThemeId !== null && !newThemeBrandIds.includes(b.id)}
                          onChange={() => setNewThemeBrandIds(prev => prev.includes(b.id) ? prev.filter(id => id !== b.id) : [...prev, b.id])}
                        />
                        {b.name}
                      </label>
                    ))}
                  </div>
                  {editingThemeId !== null
                    ? <small>Cada linha da listagem é o subtema de UMA marca. Pra aplicar a outra, cadastre de novo escolhendo ela.</small>
                    : <small>Marcar várias cria um subtema para cada — é assim que o sistema separa por marca.</small>}
                </div>
                <label className="form-field">
                  <span>Tema do subtema <b aria-hidden="true">*</b></span>
                  <Select value={newThemeGroupId} onChange={e => setNewThemeGroupId(e.target.value)}>
                    <option value="">Selecione um tema</option>
                    {themeGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </Select>
                </label>
              </div>
              <div className="grid-fields-2 mt-3">
                <label className="form-field">
                  <span>Classificação</span>
                  <Select
                    value={newThemeMode}
                    onChange={e => setNewThemeMode(e.target.value as ThemeClassificationMode)}
                  >
                    {THEME_CLASSIFICATION_MODES.map(mode => (
                      <option key={mode} value={mode}>{THEME_CLASSIFICATION_MODE_LABELS[mode]}</option>
                    ))}
                  </Select>
                </label>
                {newThemeMode === "both" && (
                  <label className="form-field">
                    <span>Determinístico em</span>
                    <Select
                      value={newThemeScope}
                      onChange={e => setNewThemeScope(e.target.value as DeterministicScope)}
                    >
                      {DETERMINISTIC_SCOPES.map(scope => (
                        <option key={scope} value={scope}>{DETERMINISTIC_SCOPE_LABELS[scope]}</option>
                      ))}
                    </Select>
                  </label>
                )}
              </div>
              <p className="section-hint mt-2" data-testid="theme-mode-hint">
                {newThemeMode === "deterministic" &&
                  "Só pelos termos, em imprensa e redes: se um termo casar, o subtema entra; se nenhum casar, o subtema fica de fora — a IA nunca o avalia."}
                {newThemeMode === "ai" &&
                  "Só pela IA: os termos não são conferidos (os que já estão gravados ficam guardados, mas não valem)."}
                {newThemeMode === "both" &&
                  "Os termos são conferidos primeiro, na fonte escolhida: se um casar, o subtema entra direto, sem IA. Se nenhum casar — ou se o post for da outra fonte —, a IA decide."}
                {newThemeMode !== "ai" &&
                  " O termo vale sozinho: as premissas (inclusive as de \"não identificar\") só vão pro prompt da IA e nunca tiram um subtema que casou pelo termo."}
              </p>
              <label className="form-field mt-3">
                <span>Termos{newThemeMode === "deterministic" && <b aria-hidden="true"> *</b>}</span>
                <TermsEditor
                  value={newThemeTerms}
                  onChange={setNewThemeTerms}
                  disabled={newThemeMode === "ai"}
                  placeholder={'"greve" OR paralisação NOT "greve de fome" — Enter ou ; fecha a expressão'}
                />
                <small>
                  Igual às marcas: uma expressão por chip, basta uma casar; cada uma com{" "}
                  <code>AND</code>, <code>OR</code>, <code>NOT</code> e aspas (<code>="Texto"</code>{" "}
                  exige o texto exato, com maiúscula e acento). A exclusão fica AQUI dentro, com{" "}
                  <code>NOT</code> — não existe mais um campo separado pra ela.
                </small>
              </label>
              <label className="form-field mt-3">
                <span>Identificar SUBTEMA quando</span>
                <textarea rows={2} value={newThemeDetection} onChange={e => setNewThemeDetection(e.target.value)} placeholder="Premissas em texto livre" />
                <small>Texto livre que entra no prompt da IA — não é expressão booleana.</small>
              </label>
              <label className="form-field mt-3">
                <span>Não identificar SUBTEMA quando</span>
                <textarea rows={2} value={newThemeNonDetection} onChange={e => setNewThemeNonDetection(e.target.value)} placeholder="Premissas em texto livre" />
              </label>
              {editingThemeId !== null ? (
                <ThemeAssignmentPanel
                  key={editingThemeId}
                  token={token}
                  themeId={editingThemeId}
                  competitorId={catalogOwner}
                  onSaved={loadThemeRuleCounts}
                />
              ) : (
                <small className="mt-3">
                  Para atribuir o subtema automaticamente por publicador ou veículo,
                  salve o subtema e abra a edição dele.
                </small>
              )}
              </SettingsFormDialog>
            )}
            <div className="panel-section">
              <h3>
                Temas e subtemas registrados ({themeGroups.length} {themeGroups.length === 1 ? "tema" : "temas"} ·{" "}
                {themes.length} {themes.length === 1 ? "subtema" : "subtemas"})
              </h3>
              <div className="theme-filters">
                <ListSearch
                  value={searchOf("temas")}
                  onChange={value => {
                    setSearchOf("temas")(value);
                    setOpenFiltered({});
                  }}
                  placeholder="Buscar por tema, subtema, marca ou termo"
                  shown={themeBlocks.length}
                  total={allThemeBlocks.length}
                />
                <label className="form-field-inline theme-brand-filter">
                  <span>Marca</span>
                  <Select
                    value={themeBrandFilter ?? ""}
                    onChange={event => {
                      setThemeBrandFilter(event.target.value ? Number(event.target.value) : null);
                      setOpenFiltered({});
                    }}
                  >
                    <option value="">Todas as marcas</option>
                    {pickerBrands.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Select>
                </label>
              </div>
              <div className="theme-block-list">
                {allThemeBlocks.length === 0 && <p>Nenhum tema cadastrado.</p>}
                {allThemeBlocks.length > 0 && themeBlocks.length === 0 && searchOf("temas").trim() === "" && (
                  <p>Nenhum tema ou subtema para esta marca.</p>
                )}
                {themeBlocks.map(block => {
                  const group = block.group;
                  const open = (themeFiltering ? openFiltered : openIdle)[block.key] ?? themeFiltering;
                  const everything = showAllOf[block.key] === true;
                  const visible = everything ? block.themes : block.themes.slice(0, THEME_BLOCK_PREVIEW);
                  const bodyId = `theme-block-${block.key}`;
                  const toggle = () =>
                    (themeFiltering ? setOpenFiltered : setOpenIdle)(current => ({
                      ...current,
                      [block.key]: !open
                    }));
                  return (
                    <section
                      key={block.key}
                      className={`theme-block${group && group.is_active ? "" : " is-inactive"}`}
                    >
                      <div className="theme-block-head">
                        <button
                          type="button"
                          className="theme-block-toggle"
                          aria-expanded={open}
                          aria-controls={bodyId}
                          aria-label={`Tema ${block.name}`}
                          onClick={toggle}
                        >
                          <ChevronRight
                            size={16}
                            aria-hidden="true"
                            className={`theme-block-chevron${open ? " rotated" : ""}`}
                          />
                          <LayoutGrid size={20} className="item-card-icon" aria-hidden="true" />
                          <span className="theme-block-title">
                            <strong>{block.name}</strong>
                            <span>
                              {group ? `${weightLabel(group.weight)} · ` : ""}
                              {themeCountLabel(block.themes.length, block.totalThemes)}
                            </span>
                          </span>
                        </button>
                        <div className="item-card-actions">
                          {canEdit && group && group.is_active && (
                            <button
                              type="button"
                              className="text-button"
                              aria-label={`Novo subtema em ${block.name}`}
                              onClick={() => openNewThemeIn(group.id)}
                            >
                              <Plus size={14} aria-hidden="true" /> Subtema
                            </button>
                          )}
                          {group ? (
                            <ActiveActions
                              canEdit={canEdit}
                              isActive={group.is_active}
                              deletedOn={group.deleted_on}
                              onToggle={active => setThemeGroupActive(group.id, active)}
                              label={group.name}
                            />
                          ) : (
                            <span className="badge-pill inactive">Desativado</span>
                          )}
                          {canEdit && group && (
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => startEditThemeGroup(group)}
                              aria-label={`Editar ${group.name}`}
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      {open && (
                        <div id={bodyId} className="theme-block-body">
                          {block.themes.length === 0 && <p>Nenhum subtema neste tema.</p>}
                          {visible.map(t => (
                            <div key={t.id} className={`item-card${t.is_active ? "" : " is-inactive"}`}>
                              <div className="item-card-info">
                                <Tag size={20} className="item-card-icon" />
                                <div>
                                  <strong>
                                    {t.name}
                                    {themeModeTag(t.classification_mode, t.deterministic_scope) &&
                                      ` (${themeModeTag(t.classification_mode, t.deterministic_scope)})`}
                                  </strong>
                                  {themeRuleCounts[t.id] > 0 && (
                                    <span className="alias-chip" title="Publicadores e veículos que atribuem este subtema automaticamente">
                                      regra automática · {themeRuleCounts[t.id]}
                                    </span>
                                  )}
                                  <span>{brandName(t.brand_id)}</span>
                                  <TermChips terms={t.terms} empty="Sem termos — só a IA identifica" />
                                </div>
                              </div>
                              <div className="item-card-actions">
                                <ActiveActions
                                  canEdit={canEdit}
                                  isActive={t.is_active}
                                  deletedOn={t.deleted_on}
                                  onToggle={active => setThemeActive(t.id, active)}
                                  label={t.name}
                                />
                                {canEdit && (
                                  <button type="button" className="icon-btn" onClick={() => startEditTheme(t)} aria-label={`Editar ${t.name}`}><Pencil size={16} /></button>
                                )}
                              </div>
                            </div>
                          ))}
                          {block.themes.length > THEME_BLOCK_PREVIEW && (
                            <button
                              type="button"
                              className="text-button theme-block-more"
                              onClick={() =>
                                setShowAllOf(current => ({ ...current, [block.key]: !everything }))
                              }
                            >
                              {everything ? "mostrar menos" : `ver todos (${block.themes.length})`}
                            </button>
                          )}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Canais Monitorados */}
        {activeTab === "canais" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Canais monitorados</h2>
                <p>
                  Perfis e canais de rede social monitorados. Veículo de imprensa,
                  TV e rádio ficam na aba <strong>Veículos</strong>.
                </p>
              </div>
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <CatalogImportButton
                    token={token}
                    companyName={companyName}
                    currentSheet="channels"
                    onFinished={onCatalogImported}
                  />
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => void createStandardChannels()}
                    disabled={standardChannelsBusy}
                    title={`Cria os que faltarem de: ${STANDARD_NETWORK_CHANNEL_NAMES.join(", ")}. Não mexe nos canais que já existem nem recria os desativados.`}
                  >
                    {standardChannelsBusy ? <LoaderCircle className="spin" size={16} /> : <WandSparkles size={16} />}
                    Criar canais padrão
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewChannel}>
                    <Plus size={16} /> Novo canal
                  </button>
                )}
              </div>
            </header>
            {channelsError && openForm !== "channel" && <div className="login-error">{channelsError}</div>}
            {channelsNotice && <div className="panel-notice"><p>{channelsNotice}</p></div>}
            {openForm === "channel" && (
              <SettingsFormDialog
                eyebrow="CANAIS MONITORADOS"
                title={editingChannelId !== null ? "Editar canal" : "Novo canal"}
                editing={editingChannelId !== null}
                values={{
                  newChannelName,
                  newChannelSource,
                  newChannelTier,
                  newChannelWeight,
                  newChannelIgnoreTerms,
                  newChannelIgnored,
                  newChannelAlias
                }}
                busy={creatingChannel}
                required={channelRequired}
                saveLabel={editingChannelId !== null ? "Salvar alterações" : "Adicionar canal"}
                error={channelsError}
                onSave={() => void addChannel()}
                onClose={resetChannelForm}
              >
                <div className="grid-fields-2">
                  <label className="form-field">
                    <span>Nome <b aria-hidden="true">*</b></span>
                    <input value={newChannelName} onChange={e => setNewChannelName(e.target.value)} placeholder="Ex: Instagram" />
                  </label>
                  <label className="form-field">
                    <span>Origem</span>
                    <Select value={newChannelSource} onChange={e => setNewChannelSource(e.target.value as "network" | "press")}>
                      <option value="network">Rede social</option>
                      <option value="press">Imprensa</option>
                    </Select>
                    <small>"Imprensa" aqui é herança do schema antigo — prefira a aba Veículos.</small>
                  </label>
                </div>
                <div className="grid-fields-2 mt-3">
                  <label className="form-field">
                    <span>Tier</span>
                    <input value={newChannelTier} onChange={e => setNewChannelTier(e.target.value)} placeholder="Ex: A" />
                    {/* Dizia "usado no cálculo do CENA", o que era falso:
                        esse par tier/peso-de-tier é um mecanismo paralelo
                        que nunca entrou em fórmula nenhuma. Quem pontua
                        canal é o campo ao lado. */}
                    <small>Código de classificação do canal ('A', 'B', 'C'…), herdado do sistema antigo. Não entra no cálculo do CENA.</small>
                  </label>
                  {newChannelSource === "network" && (
                    <label className="form-field">
                      <span>Peso no CENA</span>
                      <input
                        type="number"
                        min={0}
                        step="0.5"
                        value={newChannelWeight}
                        onChange={e => setNewChannelWeight(e.target.value)}
                        placeholder="Ex: 5"
                      />
                      <small>Quanto este canal vale no índice. Em branco usa o peso padrão da plataforma; 0 significa que o canal não pontua.</small>
                    </label>
                  )}
                </div>
                <label className="form-field-inline mt-3">
                  <input type="checkbox" checked={newChannelIgnoreTerms} onChange={e => setNewChannelIgnoreTerms(e.target.checked)} />
                  Ignorar verificação de termos — tudo deste canal entra, sem casar com os termos da marca
                </label>
                {/* Não confundir com o de cima: aquele faz TUDO entrar,
                    este faz NADA entrar. O post vira pulo deliberado no
                    upload — não erro, não fila de correção. */}
                <div className="form-field mt-3">
                  <label className="form-field-inline">
                    <input type="checkbox" checked={newChannelIgnored} onChange={e => setNewChannelIgnored(e.target.checked)} />
                    Ignorar este canal
                  </label>
                  <small>
                    Nenhum post deste canal entra. Aparece no upload como "linha
                    sem menção", não como erro nem pendência — é uma decisão de
                    cadastro, não uma falha.
                  </small>
                </div>
                {editingChannelId !== null && (
                  <AliasEditor
                    aliases={channelAliases}
                    draft={newChannelAlias}
                    onDraft={setNewChannelAlias}
                    onAdd={() => void addChannelAlias()}
                    onRemove={id => void removeChannelAlias(id)}
                  />
                )}
              </SettingsFormDialog>
            )}
            <div className="panel-section">
              {/* Com busca ativa o número é do RESULTADO, não do
                  catálogo — o servidor só conta o filtro. Chamar os dois
                  de "registrados" faria a tela dizer que a empresa tem
                  158 veículos quando ela tem 14 mil. */}
              <h3>
                {channelQuery.trim() ? "Canais encontrados" : "Canais registrados"}{" "}
                ({formatInteger(channelTotal)})
              </h3>
              <CatalogSearch
                value={channelSearch}
                onChange={setChannelSearch}
                onSubmit={searchChannels}
                placeholder="Buscar por nome ou apelido"
              />
              <div className="list-cards-grid">
                {channels.map(c => (
                  <div key={c.id} className={`item-card${c.is_active ? "" : " is-inactive"}`}>
                    <div className="item-card-info">
                      <Globe2 size={20} className="item-card-icon" />
                      <div>
                        <strong>{c.name}</strong>
                        <span>
                          {c.post_source === "press" ? "Imprensa" : "Rede social"}
                          {c.tier ? ` · Tier ${c.tier}` : ""}
                          {c.weight !== null && c.weight !== undefined ? ` · peso ${c.weight}` : ""}
                          {c.ignore_term_verification ? " · ignora termos" : ""}
                          {c.ignored ? " · ignorado" : ""}
                        </span>
                        <AliasChips aliases={c.aliases} />
                      </div>
                    </div>
                    <div className="item-card-actions">
                      <ActiveActions
                          canEdit={canEdit}
                        isActive={c.is_active}
                        deletedOn={c.deleted_on}
                        onToggle={active => setChannelActive(c.id, active)}
                        label={c.name}
                      />
                      {canEdit && (
                        <button type="button" className="icon-btn" onClick={() => startEditChannel(c)} aria-label={`Editar ${c.name}`}><Pencil size={16} /></button>
                      )}
                    </div>
                  </div>
                ))}
                {channels.length === 0 && (
                  <p>
                    {channelQuery.trim()
                      ? `Nenhum canal encontrado para "${channelQuery.trim()}".`
                      : "Nenhum canal cadastrado."}
                  </p>
                )}
              </div>
              <Pager
                page={channelPage}
                size={CATALOG_PAGE_SIZE}
                count={{ total: channelTotal }}
                onPage={setChannelPage}
              />
            </div>
          </div>
        )}

        {/* TAB: Veículos */}
        {activeTab === "veiculos" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Veículos</h2>
                <p>
                  Jornais, portais, emissoras de TV e rádio. É cadastro separado do de
                  canais porque veículo tem tier próprio (que entra no CENA) e a marcação
                  de paywall, que canal de rede não tem.
                </p>
              </div>
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <CatalogImportButton
                    token={token}
                    companyName={companyName}
                    currentSheet="vehicles"
                    onFinished={onCatalogImported}
                  />
                )}
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewVehicle}>
                    <Plus size={16} /> Novo veículo
                  </button>
                )}
              </div>
            </header>
            {vehiclesError && openForm !== "vehicle" && <div className="login-error">{vehiclesError}</div>}
            {openForm === "vehicle" && (
              <SettingsFormDialog
                eyebrow="VEÍCULOS"
                title={editingVehicleId !== null ? "Editar veículo" : "Novo veículo"}
                editing={editingVehicleId !== null}
                values={{
                  newVehicleName,
                  newVehicleKind,
                  newVehicleTier,
                  newVehicleContentOptional,
                  newVehicleIgnored,
                  newVehicleIgnoreTerms,
                  newVehicleState,
                  newVehicleRegion,
                  newVehicleCountry,
                  newVehicleBasePrice,
                  newVehicleAlias
                }}
                busy={creatingVehicle}
                required={vehicleRequired}
                saveLabel={editingVehicleId !== null ? "Salvar alterações" : "Adicionar veículo"}
                error={vehiclesError}
                wide
                onSave={() => void addVehicle()}
                onClose={resetVehicleForm}
              >
              <div className="grid-fields-2">
                <label className="form-field">
                  <span>Nome <b aria-hidden="true">*</b></span>
                  <input value={newVehicleName} onChange={e => setNewVehicleName(e.target.value)} placeholder="Ex: Folha de S.Paulo" />
                </label>
                <label className="form-field">
                  <span>Tipo</span>
                  <Select value={newVehicleKind} onChange={e => setNewVehicleKind(e.target.value as VehicleKind)}>
                    {VEHICLE_KINDS.map(kind => (
                      <option key={kind} value={kind}>{VEHICLE_KIND_LABELS[kind]}</option>
                    ))}
                  </Select>
                </label>
              </div>
              <label className="form-field mt-3">
                <span>Tier</span>
                <Select value={newVehicleTier} onChange={e => setNewVehicleTier(e.target.value)}>
                  <option value="">Sem tier</option>
                  <option value="supertier">Supertier</option>
                  <option value="tier1">Tier 1</option>
                  <option value="tier2">Tier 2</option>
                  <option value="tier3">Tier 3</option>
                  <option value="tier4">Tier 4</option>
                  <option value="tier5">Tier 5</option>
                </Select>
                <small>Alimenta o peso de veículo no cálculo do CENA.</small>
              </label>
              <div className="grid-fields-2 mt-3">
                <label className="form-field">
                  <span>UF</span>
                  <input value={newVehicleState} onChange={e => setNewVehicleState(e.target.value)} placeholder="Ex: SP" maxLength={64} />
                </label>
                <label className="form-field">
                  <span>Região</span>
                  <input value={newVehicleRegion} onChange={e => setNewVehicleRegion(e.target.value)} placeholder="Ex: Sudeste" maxLength={64} />
                </label>
                <label className="form-field">
                  <span>País</span>
                  <input value={newVehicleCountry} onChange={e => setNewVehicleCountry(e.target.value)} placeholder="Ex: Brasil" maxLength={64} />
                </label>
                <label className="form-field">
                  <span>Preço base</span>
                  <input type="number" min="0" step="0.01" value={newVehicleBasePrice} onChange={e => setNewVehicleBasePrice(e.target.value)} placeholder="Ex: 1200,00" />
                  <small>Opcional, em reais. Por enquanto só fica registrado: não entra em nota nem em valoração.</small>
                </label>
              </div>
              <div className="form-field mt-3">
                <label className="form-field-inline">
                  <input type="checkbox" checked={newVehicleContentOptional} onChange={e => setNewVehicleContentOptional(e.target.checked)} />
                  Conteúdo opcional (paywall)
                </label>
                <small>
                  Marque para veículo com paywall: a falha ao buscar o texto da matéria passa
                  a ser esperada e não vira item na fila de correção.
                </small>
              </div>
              <label className="form-field-inline mt-3">
                <input type="checkbox" checked={newVehicleIgnoreTerms} onChange={e => setNewVehicleIgnoreTerms(e.target.checked)} />
                Ignorar verificação de termos — tudo deste veículo entra, sem casar com os termos da marca
              </label>
              <div className="form-field mt-3">
                <label className="form-field-inline">
                  <input type="checkbox" checked={newVehicleIgnored} onChange={e => setNewVehicleIgnored(e.target.checked)} />
                  Ignorar este veículo
                </label>
                <small>
                  Nenhuma matéria deste veículo entra — nem passa pela coleta do
                  texto. Aparece no upload como "linha sem menção", não como erro
                  nem pendência.
                </small>
              </div>
              {editingVehicleId !== null && (
                <AliasEditor
                  aliases={vehicleAliases}
                  draft={newVehicleAlias}
                  onDraft={setNewVehicleAlias}
                  onAdd={() => void addVehicleAlias()}
                  onRemove={id => void removeVehicleAlias(id)}
                />
              )}
              </SettingsFormDialog>
            )}
            <div className="panel-section">
              {/* Ver a nota na aba Canais. */}
              <h3>
                {vehicleQuery.trim() ? "Veículos encontrados" : "Veículos registrados"}{" "}
                ({formatInteger(vehicleTotal)})
              </h3>
              <CatalogSearch
                value={vehicleSearch}
                onChange={setVehicleSearch}
                onSubmit={searchVehicles}
                placeholder="Buscar por nome ou apelido"
              />
              <div className="list-cards-grid">
                {vehicles.map(v => (
                  <div key={v.id} className={`item-card${v.is_active ? "" : " is-inactive"}`}>
                    <div className="item-card-info">
                      <Newspaper size={20} className="item-card-icon" />
                      <div>
                        <strong>{v.name}</strong>
                        <span>
                          {VEHICLE_KIND_LABELS[v.vehicle_kind] ?? v.vehicle_kind}
                          {v.tier_key ? ` · ${v.tier_key}` : ""}
                          {v.content_optional ? " · paywall" : ""}
                          {v.ignore_term_verification ? " · ignora termos" : ""}
                          {v.ignored ? " · ignorado" : ""}
                        </span>
                        {vehicleAttributesSummary(v) && (
                          <span>{vehicleAttributesSummary(v)}</span>
                        )}
                        <AliasChips aliases={v.aliases} />
                      </div>
                    </div>
                    <div className="item-card-actions">
                      <ActiveActions
                          canEdit={canEdit}
                        isActive={v.is_active}
                        deletedOn={v.deleted_on}
                        onToggle={active => setVehicleActive(v.id, active)}
                        label={v.name}
                      />
                      {canEdit && (
                        <button type="button" className="icon-btn" onClick={() => startEditVehicle(v)} aria-label={`Editar ${v.name}`}><Pencil size={16} /></button>
                      )}
                    </div>
                  </div>
                ))}
                {vehicles.length === 0 && (
                  <p>
                    {vehicleQuery.trim()
                      ? `Nenhum veículo encontrado para "${vehicleQuery.trim()}".`
                      : "Nenhum veículo cadastrado."}
                  </p>
                )}
              </div>
              <Pager
                page={vehiclePage}
                size={CATALOG_PAGE_SIZE}
                count={{ total: vehicleTotal }}
                onPage={setVehiclePage}
              />
            </div>
          </div>
        )}

        {/* TAB: Porte do publicador */}
        {activeTab === "publicadores-tipos" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Porte do publicador</h2>
                <p>
                  Faixas de número de seguidores, cada uma com um peso que entra no CENA
                  de redes sociais. A publicação cai na primeira faixa cujo intervalo
                  contém o número de seguidores do autor. Sem seguidores (ou com zero),
                  ou fora de todas as faixas, ela cai na <strong>faixa de queda</strong>,
                  que você marca abaixo.
                </p>
              </div>
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewTier}>
                    <Plus size={16} /> Nova faixa
                  </button>
                )}
              </div>
            </header>
            {tiersError && openForm !== "tier" && <div className="login-error">{tiersError}</div>}
            {publisherTiers.length > 0 && !publisherTiers.some(t => t.is_default && t.is_active) && (
              <p className="panel-notice">
                Nenhuma faixa está marcada como <strong>faixa de queda</strong>. Publicação
                de rede sem número de seguidores (ou com zero) fica sem pontuação de
                publicador.
              </p>
            )}
            {openForm === "tier" && (
              <SettingsFormDialog
                eyebrow="PORTE DO PUBLICADOR"
                title={editingTierId !== null ? "Editar faixa" : "Nova faixa"}
                editing={editingTierId !== null}
                values={{
                  newTierName,
                  newTierWeight,
                  newTierMin,
                  newTierMax,
                  newTierDefault
                }}
                busy={creatingTier}
                required={tierRequired}
                saveLabel={editingTierId !== null ? "Salvar alterações" : "Adicionar faixa"}
                error={tiersError}
                onSave={() => void addPublisherTier()}
                onClose={resetTierForm}
              >
              <div className="grid-fields-2">
                <label className="form-field">
                  <span>Nome <b aria-hidden="true">*</b></span>
                  <input
                    value={newTierName}
                    onChange={e => setNewTierName(e.target.value)}
                    placeholder="Ex: MICRO"
                  />
                  <small>
                    Só um rótulo — quem decide onde cai a publicação sem seguidores é a
                    marcação de faixa de queda abaixo.
                  </small>
                </label>
                <label className="form-field">
                  <span>Peso <b aria-hidden="true">*</b></span>
                  <input
                    type="number"
                    min={0}
                    step="0.1"
                    value={newTierWeight}
                    onChange={e => setNewTierWeight(e.target.value)}
                  />
                </label>
              </div>
              <div className="grid-fields-2 mt-3">
                <label className="form-field">
                  <span>De (seguidores)</span>
                  <input
                    type="number"
                    min={0}
                    value={newTierMin}
                    onChange={e => setNewTierMin(e.target.value)}
                    placeholder="vazio = sem faixa"
                  />
                </label>
                <label className="form-field">
                  <span>Até (seguidores)</span>
                  <input
                    type="number"
                    min={0}
                    value={newTierMax}
                    onChange={e => setNewTierMax(e.target.value)}
                    placeholder="vazio = sem limite"
                  />
                </label>
              </div>
              <label className="form-field-inline mt-3">
                <input
                  type="checkbox"
                  checked={newTierDefault}
                  onChange={e => setNewTierDefault(e.target.checked)}
                />
                <span>
                  Faixa de queda — vale para quem não tem seguidores e para quem não cai
                  em nenhuma faixa. Marcar aqui desmarca a atual.
                </span>
              </label>
              <small className="form-hint">
                Faixas não podem se sobrepor. Intervalo descoberto cai na faixa de queda.
              </small>
              </SettingsFormDialog>
            )}
            <div className="panel-section">
              <h3>Faixas cadastradas ({publisherTiers.length})</h3>
              <ListSearch
                value={searchOf("publicadores-tipos")}
                onChange={setSearchOf("publicadores-tipos")}
                placeholder="Buscar faixa de publicador"
                shown={shownPublisherTiers.length}
                total={publisherTiers.length}
              />
              <div className="list-cards-grid">
                {shownPublisherTiers.map(tier => (
                  <div key={tier.id} className={`item-card${tier.is_active ? "" : " is-inactive"}`}>
                    <div className="item-card-info">
                      <Users size={20} className="item-card-icon" />
                      <div>
                        <strong>{tier.name}</strong>
                        <span>
                          peso {tier.weight}
                          {tier.min_count === null
                            ? " · sem intervalo"
                            : ` · ${tier.min_count.toLocaleString("pt-BR")}–${
                                tier.max_count === null
                                  ? "∞"
                                  : tier.max_count.toLocaleString("pt-BR")
                              } seguidores`}
                          {tier.is_default && " · faixa de queda"}
                        </span>
                      </div>
                    </div>
                    <div className="item-card-actions">
                      <ActiveActions
                          canEdit={canEdit}
                        isActive={tier.is_active}
                        deletedOn={tier.deleted_on}
                        onToggle={active => setTierActive(tier.id, active)}
                        label={tier.name}
                      />
                      {canEdit && (
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => startEditTier(tier)}
                          aria-label={`Editar ${tier.name}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {publisherTiers.length === 0 && <p>Nenhuma faixa cadastrada.</p>}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Categorização de Publicadores */}
        {activeTab === "publicadores" && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Categorização de publicadores</h2>
                <p>
                  Pessoas e perfis que você acompanha, organizados em grupos com peso
                  próprio. O peso do grupo compete com a faixa de seguidores no CENA — o
                  maior dos dois vence.
                </p>
              </div>
              <InactiveFilter checked={showInactive} onChange={setShowInactive} />
              <div className="panel-header-actions">
                {canEdit && (
                  <CatalogImportButton
                    token={token}
                    kind="publishers"
                    onFinished={onPublishersImported}
                  />
                )}
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewGroup}>
                    <Plus size={16} /> Novo grupo
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="primary-button" onClick={openNewPerson}>
                    <Plus size={16} /> Nova pessoa
                  </button>
                )}
              </div>
            </header>
            {publishersError && openForm !== "publisherGroup" && openForm !== "publisherPerson" && (
              <div className="login-error">{publishersError}</div>
            )}

            {openForm === "publisherGroup" && (
              <SettingsFormDialog
                eyebrow="CATEGORIZAÇÃO DE PUBLICADORES"
                title={editingGroupId !== null ? "Editar grupo" : "Novo grupo"}
                editing={editingGroupId !== null}
                values={{ newGroupName, newGroupWeight }}
                busy={creatingGroup}
                required={publisherGroupRequired}
                saveLabel={editingGroupId !== null ? "Salvar alterações" : "Adicionar grupo"}
                error={publishersError}
                onSave={() => void addPublisherGroup()}
                onClose={resetGroupForm}
              >
              <div className="grid-fields-2">
                <label className="form-field">
                  <span>Nome <b aria-hidden="true">*</b></span>
                  <input
                    value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    placeholder="Ex: Autoridades"
                  />
                </label>
                <label className="form-field">
                  <span>Peso</span>
                  <input
                    type="number"
                    min={0}
                    step="0.1"
                    value={newGroupWeight}
                    onChange={e => setNewGroupWeight(e.target.value)}
                    placeholder="vazio = só rotula"
                  />
                  <small>Sem peso, o grupo organiza mas não pontua.</small>
                </label>
              </div>
              </SettingsFormDialog>
            )}

            <div className="panel-section">
              <h3>Grupos cadastrados ({publisherGroups.length})</h3>
              <ListSearch
                value={searchOf("publicadores-grupos")}
                onChange={setSearchOf("publicadores-grupos")}
                placeholder="Buscar grupo de publicador"
                shown={shownPublisherGroups.length}
                total={publisherGroups.length}
              />
              <div className="list-cards-grid mt-3">
                {shownPublisherGroups.map(group => (
                  <div key={group.id} className={`item-card${group.is_active ? "" : " is-inactive"}`}>
                    <div className="item-card-info">
                      <LayoutGrid size={20} className="item-card-icon" />
                      <div>
                        <strong>{group.name}</strong>
                        <span>
                          {group.weight === null ? "sem peso" : `peso ${group.weight}`}
                          {group.is_global ? " · do sistema" : ""}
                        </span>
                      </div>
                    </div>
                    <div className="item-card-actions">
                      {/* Grupo do sistema é vinculável por todas as
                          empresas e editável por nenhuma. */}
                      {!group.is_global && (
                        <>
                          <ActiveActions
                          canEdit={canEdit}
                            isActive={group.is_active}
                            deletedOn={group.deleted_on}
                            onToggle={active => setGroupActive(group.id, active)}
                            label={group.name}
                          />
                          {canEdit && (
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => startEditGroup(group)}
                              aria-label={`Editar ${group.name}`}
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {publisherGroups.length === 0 && <p>Nenhum grupo cadastrado.</p>}
              </div>
            </div>

            {openForm === "publisherPerson" && (
              <SettingsFormDialog
                eyebrow="CATEGORIZAÇÃO DE PUBLICADORES"
                title={editingPersonId !== null ? "Editar publicador" : "Novo publicador"}
                editing={editingPersonId !== null}
                values={{ newPersonName, newPersonProfiles, newPersonGroupIds }}
                busy={creatingPerson}
                required={personRequired}
                saveLabel={editingPersonId !== null ? "Salvar alterações" : "Adicionar publicador"}
                error={publishersError}
                wide
                onSave={() => void addPublisherPerson()}
                onClose={resetPersonForm}
              >
              <label className="form-field">
                <span>Nome <b aria-hidden="true">*</b></span>
                <input
                  value={newPersonName}
                  onChange={e => setNewPersonName(e.target.value)}
                  placeholder="Ex: Maria Silva"
                />
              </label>

              <div className="form-field mt-3">
                <span>Perfis</span>
                {newPersonProfiles.map((profile, index) => (
                  <div key={index} className="grid-fields-2 mt-3">
                    <Select
                      value={profile.platform}
                      onChange={e =>
                        setNewPersonProfiles(current =>
                          current.map((p, i) =>
                            i === index ? { ...p, platform: e.target.value } : p
                          )
                        )
                      }
                    >
                      <option value="">Escolha o canal</option>
                      {channelOptions.map(c => (
                        <option key={c.id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </Select>
                    <div className="field-with-action">
                      <input
                        value={profile.profile_url}
                        onChange={e =>
                          setNewPersonProfiles(current =>
                            current.map((p, i) =>
                              i === index ? { ...p, profile_url: e.target.value } : p
                            )
                          )
                        }
                        placeholder="https://instagram.com/perfil"
                      />
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() =>
                          setNewPersonProfiles(current =>
                            current.filter((_, i) => i !== index)
                          )
                        }
                        aria-label="Remover perfil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="text-button mt-3"
                  onClick={() =>
                    setNewPersonProfiles(current => [
                      ...current,
                      { platform: "", profile_url: "" }
                    ])
                  }
                >
                  <Plus size={14} /> Adicionar perfil
                </button>
                <small>
                  O canal precisa estar cadastrado em “Canais monitorados” — é ele que
                  casa o perfil com as publicações.
                </small>
              </div>

              <div className="form-field mt-3">
                <span>Grupos</span>
                <div className="filter-chip-row">
                  {publisherGroups
                    .filter(g => g.is_active)
                    .map(group => (
                      <button
                        key={group.id}
                        type="button"
                        className={`filter-chip ${
                          newPersonGroupIds.includes(group.id) ? "is-active" : ""
                        }`}
                        onClick={() =>
                          setNewPersonGroupIds(current =>
                            current.includes(group.id)
                              ? current.filter(id => id !== group.id)
                              : [...current, group.id]
                          )
                        }
                      >
                        {group.name}
                      </button>
                    ))}
                </div>
              </div>

              </SettingsFormDialog>
            )}

            <div className="panel-section">
              <h3>Publicadores cadastrados ({publisherPersons.length})</h3>
              <ListSearch
                value={searchOf("publicadores")}
                onChange={setSearchOf("publicadores")}
                placeholder="Buscar publicador por nome, perfil ou grupo"
                shown={shownPublisherPersons.length}
                total={publisherPersons.length}
              />
              <div className="list-cards-grid">
                {shownPublisherPersons.map(person => (
                  <div key={person.id} className={`item-card${person.is_active ? "" : " is-inactive"}`}>
                    <div className="item-card-info">
                      <Users size={20} className="item-card-icon" />
                      <div>
                        <strong>{person.name}</strong>
                        <span>
                          {person.profiles.length === 0
                            ? "sem perfil — não casa com publicações"
                            : `${person.profiles.length} perfil(is)`}
                          {person.group_ids.length > 0 &&
                            ` · ${person.group_ids
                              .map(id => publisherGroups.find(g => g.id === id)?.name)
                              .filter(Boolean)
                              .join(", ")}`}
                        </span>
                      </div>
                    </div>
                    <div className="item-card-actions">
                      <ActiveActions
                          canEdit={canEdit}
                        isActive={person.is_active}
                        deletedOn={person.deleted_on}
                        onToggle={active => setPersonActive(person.id, active)}
                        label={person.name}
                      />
                      {canEdit && (
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => startEditPerson(person)}
                          aria-label={`Editar ${person.name}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {publisherPersons.length === 0 && <p>Nenhum publicador cadastrado.</p>}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Clipadoras da Empresa */}
        {/* TAB: Importar configuração (a planilha de várias abas) */}
        {activeTab === "importar-configuracao" && canEdit && (
          <div className="ajustes-panel">
            <header className="panel-header">
              <div>
                <h2>Importar configuração</h2>
                <p>
                  Leva para esta empresa a configuração de outra (ou a sua, depois
                  de editada no Excel), aba por aba, pela mesma planilha que
                  &quot;Baixar configuração&quot; gera.
                </p>
              </div>
            </header>
            <CatalogImportPanel
              token={token}
              kind="company"
              companyName={companyName}
              onFinished={onConfigImported}
            />
          </div>
        )}

        {/* TABS: Premissas, Termos de classificação, Foco, Texto do prompt e
            Modelo de IA — as cinco antigas "Configuração de prompt" (2026-10-31).
            Um painel só e um estado só; cada aba desenha o seu bloco. */}
        {isPromptTab(activeTab) && (
          <div className="ajustes-panel prompt-settings">
            <header className="panel-header">
              <div>
                <span className="panel-kicker">Inteligência artificial</span>
                <h2>{PROMPT_TAB_COPY[activeTab].title}</h2>
                <p>{PROMPT_TAB_COPY[activeTab].description}</p>
              </div>
              <CatalogOwnerPicker
                competitors={catalogCompetitors}
                value={catalogOwner}
                onChange={setCatalogOwner}
              />
              <div className="panel-header-actions">
                {activeTab === "foco" && canEdit && catalogOwner !== null && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setInvertOpen(true)}
                    disabled={promptBrandId === null}
                  >
                    Trazer do cliente, invertido
                  </button>
                )}
                {activeTab === "premissas" && canEdit && (
                  <CatalogImportButton
                    token={token}
                    kind="rules"
                    companyName={companyName}
                    onFinished={onRulesImported}
                  />
                )}
              </div>
            </header>
            {catalogOwner !== null && (
              <div className="panel-notice">
                <p>
                  Você está na configuração de um <strong>concorrente</strong>: os textos,
                  as premissas e os termos abaixo são os que a IA usa nas menções dele.
                  A escala de sentimento e o modelo de IA são os da empresa.
                </p>
              </div>
            )}
            {promptError && <div className="login-error">{promptError}</div>}
            {promptNotice.length > 0 && (
              <div className="panel-notice">
                {promptNotice.map((aviso, i) => <p key={i}>{aviso}</p>)}
              </div>
            )}

            {/* Só o Máster vê: são as chaves que ele liga por empresa
                (decisão da dona, 2026-09-14). Com o stub de header todo
                mundo é Máster; com Keycloak o bloco some pra quem está
                abaixo. Cada chave mora na aba que ela governa. */}
            {isMaster(userProfile) && profile && activeTab === "texto-prompt" && (
              <div className="panel-section prompt-access">
                <div className="prompt-access-copy">
                  <h3>Liberação para a empresa</h3>
                  <p className="section-hint">
                    Só o Máster edita o prompt base. Ligue aqui pra quem cuida desta
                    empresa (Administrador, Especialista) poder editar também. As premissas
                    continuam editáveis por Especialista.
                  </p>
                </div>
                <div className="prompt-access-switches">
                  <label className="form-field-inline">
                    <input
                      type="checkbox"
                      checked={profile.can_edit_prompts}
                      disabled={promptBusy}
                      onChange={e => saveAiEditingAccess({ can_edit_prompts: e.target.checked })}
                    />
                    Prompt base editável pela empresa
                  </label>
                </div>
              </div>
            )}
            {isMaster(userProfile) && profile && activeTab === "modelo-ia" && (
              <div className="panel-section prompt-access">
                <div className="prompt-access-copy">
                  <h3>Liberação para a empresa</h3>
                  <p className="section-hint">
                    Só o Máster edita o modelo por tarefa. Ligue aqui pra quem cuida desta
                    empresa (Administrador, Especialista) poder editar também.
                  </p>
                </div>
                <div className="prompt-access-switches">
                  <label className="form-field-inline">
                    <input
                      type="checkbox"
                      checked={profile.can_edit_models}
                      disabled={promptBusy}
                      onChange={e => saveAiEditingAccess({ can_edit_models: e.target.checked })}
                    />
                    Modelo por tarefa editável pela empresa
                  </label>
                </div>
              </div>
            )}
            {activeTab === "texto-prompt" && promptsLocked && (
              <div className="panel-notice prompt-locked">
                <Lock size={15} aria-hidden="true" />
                <p>O prompt base desta empresa está travado: você pode ler, mas só o Máster libera a edição.</p>
              </div>
            )}
            {activeTab === "modelo-ia" && modelsLocked && (
              <div className="panel-notice prompt-locked">
                <Lock size={15} aria-hidden="true" />
                <p>O modelo por tarefa desta empresa está travado: você pode ler, mas só o Máster libera a edição.</p>
              </div>
            )}

            {/* O que está sendo editado fica preso no topo enquanto se rola:
                tudo abaixo é DESTA marca nesta fonte, e trocar a fonte troca
                a página inteira. O modelo é da empresa (sem marca nem fonte)
                e os textos são por fonte (sem marca). */}
            {activeTab !== "modelo-ia" && renderPromptTarget({
              brand: activeTab !== "texto-prompt",
              extras: activeTab !== "texto-prompt"
            })}

            {activeTab === "foco" && invertOpen && catalogOwner !== null && promptBrandId !== null && (
              <FocusInvertDialog
                sections={FOCUS_SECTIONS}
                clientBrands={activeBrands}
                readClientTerms={brandId => apiClient.listFocusTerms(token, { brandId, source: promptSource })}
                existing={focusTerms}
                targetBrandName={brandName(promptBrandId)}
                sourceLabel={promptSource === "press" ? "Imprensa" : "Redes sociais"}
                busy={promptBusy}
                onApply={items => void applyInvertedFocus(items)}
                onClose={() => setInvertOpen(false)}
              />
            )}

            {activeTab === "foco" && (
            <div className="panel-section prompt-block" id="prompt-foco">
              <h3>Foco</h3>
              <p className="section-hint">
                Diz à IA de quem a matéria fala. Só entra no prompt o foco que de fato
                aparece no texto da publicação — cadastrar não força nada. Cada item é um
                nome ou termo simples, separados por <code>;</code>, procurado como palavra
                inteira sem diferenciar maiúscula nem acento — não é expressão booleana nem
                aceita regex; os porta-vozes cadastrados entram aqui sozinhos.
              </p>
              <div className="grid-fields-2">
                {FOCUS_SECTIONS.map(secao => (
                  <div className="form-field prompt-term-field" key={secao.key}>
                    <span>{secao.label}</span>
                    {canEdit && (
                      <AddTermBox
                        value={draft[`focus:${secao.key}`] || ""}
                        onChange={value => setDraftValue(`focus:${secao.key}`, value)}
                        onAdd={() => addFocusTerm(secao.key)}
                        placeholder="Nome, apelido ou termo — vários separados por ;"
                        disabled={promptBusy}
                        label={`Adicionar ${secao.label}`}
                      />
                    )}
                    <small>{MULTI_ENTRY_HINT}</small>
                    <RemovablePills
 canEdit={canEdit}
                      items={focusTerms.filter(f => f.category === secao.key)}
                      empty="Nenhum cadastrado."
                      onRemove={id => runPromptWrite(
                        async () => { await catalogApi.deactivateFocusTerm(token, id); },
                        "Não foi possível remover o foco."
                      )}
                      onMove={id => moveFocusTerm(id, secao.key === "client" ? "competitor" : "client")}
                      moveLabel={secao.key === "client" ? "Mover para Foco concorrente" : "Mover para Foco cliente"}
                      onReactivate={id => runPromptWrite(
                        async () => { await catalogApi.updateFocusTerm(token, id, { is_active: true }); },
                        "Não foi possível reativar o foco."
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
            )}

            {activeTab === "premissas" && (
              <div className="panel-section prompt-block" id="prompt-premissas">
                <h3>Premissas por polaridade</h3>
                <p className="section-hint">
                  Abra uma polaridade para editar. As <strong>premissas</strong> vão dentro
                  do prompt e é a IA que julga.
                </p>
                {renderPolaritySections("premises")}
              </div>
            )}

            {activeTab === "termos-classificacao" && (
              <div className="panel-section prompt-block" id="prompt-termos">
                <h3>Termos por polaridade</h3>
                <p className="section-hint">
                  Abra uma polaridade para editar. Os <strong>termos</strong> decidem
                  sozinhos, sem chamar a IA.
                </p>
                {renderPolaritySections("terms")}
            {/* No modo 3 níveis a 2ª passada não existe (NEUTRO é
                resposta final), então o bloco inteiro some — a mesma
                regra de órfã visível não se aplica: os termos compostos
                aparecem na consulta das seções acima se existirem. */}
            {polarityScale !== "three_level" && (
            <div className={`prompt-polarity${expandedSection === "COMPOSTAS" ? " is-open" : ""}`} data-tone="composite">
              <button
                type="button"
                className={`category-toggle ${expandedSection === "COMPOSTAS" ? "is-open" : ""}`}
                onClick={() => setExpandedSection(prev => prev === "COMPOSTAS" ? null : "COMPOSTAS")}
                aria-expanded={expandedSection === "COMPOSTAS"}
              >
                <span className="prompt-polarity-title">
                  <span className="prompt-polarity-dot" aria-hidden="true" />
                  Combinações (2ª passada)
                </span>
                <span className="prompt-polarity-summary">
                  {(() => {
                    const total = classificationTerms.filter(t =>
                      t.is_active && COMPOSITE_SECTIONS.some(secao => secao.term === t.category)
                    ).length;
                    return `${total} ${total === 1 ? "termo" : "termos"} · só quando a IA devolve neutro`;
                  })()}
                </span>
                <ChevronRight size={16} className={expandedSection === "COMPOSTAS" ? "rotated" : ""} />
              </button>
              {expandedSection === "COMPOSTAS" && (
                <div className="category-body is-single">
                  <p className="section-hint">
                    Só entram em jogo quando a IA devolve NEUTRO: o sistema procura estes
                    termos numa segunda passada antes de desistir. Não têm premissa — são
                    atalho puro. A forma é a mesma dos termos acima: palavra inteira, sem
                    diferenciar maiúscula — mas <strong>COM acento</strong>; sem expressão
                    booleana, sem regex.
                  </p>
                  <div className="grid-fields-2">
                    {COMPOSITE_SECTIONS.map(secao => (
                      <div className="form-field prompt-term-field" key={secao.term}>
                        <span>{secao.label}</span>
                        {canEdit && (
                          <AddTermBox
                            value={draft[`term:${secao.term}:proprietary_terms`] || ""}
                            onChange={value => setDraftValue(`term:${secao.term}:proprietary_terms`, value)}
                            onAdd={() => addClassificationTerm(secao.term, "proprietary_terms")}
                            placeholder="Termo — vários separados por ;"
                            disabled={promptBusy}
                            label={`Adicionar ${secao.label}`}
                          />
                        )}
                        <small>{MULTI_ENTRY_HINT}</small>
                        <RemovablePills
 canEdit={canEdit}
                          items={classificationTerms.filter(t => t.category === secao.term)}
                          onRemove={id => runPromptWrite(
                            async () => { await catalogApi.deactivateClassificationTerm(token, id); },
                            "Não foi possível remover o termo."
                          )}
                          onReactivate={id => runPromptWrite(
                            async () => { await catalogApi.updateClassificationTerm(token, id, { is_active: true }); },
                            "Não foi possível reativar o termo."
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
              </div>
            )}

            {activeTab === "texto-prompt" && (
            <div className="panel-section prompt-block" id="prompt-textos">
              <h3>Texto do prompt</h3>
              <p className="section-hint">
                O texto enviado ao modelo, por tarefa. Os marcadores{" "}
                <code>$$$assim$$$</code> são trocados pelos dados da publicação e pelo que
                está cadastrado acima — o servidor recusa marcador que ele não saiba
                resolver, em vez de deixá-lo passar literal para a IA.
              </p>
              {prompts.length === 0 && <p>Nenhum texto de prompt para esta fonte.</p>}
              {/* No modo 3 a posição 1 da polaridade (a 2ª passada) fica
                  inalcançável — sai da listagem em vez de convidar
                  edição de texto que nunca roda (pedido da dona: a
                  quantidade de prompts reflete a escala). Filtro e sort
                  sobre CÓPIA: `.sort()` in-place mutaria o estado. */}
              {[...prompts]
                .filter(
                  p =>
                    polarityScale !== "three_level" ||
                    p.task !== "polarity_intensity" ||
                    p.position === 0
                )
                .sort((a, b) => a.task.localeCompare(b.task) || a.position - b.position)
                .map(p => {
                  const key = promptKey(p);
                  const { header, temperature } = promptDraftOf(p);
                  const dirty = header !== p.header || Number(temperature) !== p.temperature;
                  const markers = promptMarkers(header);
                  const isCustom = p.origin === "override";
                  // O texto só edita numa linha já personalizada ou depois de
                  // "Personalizar"; quem segue o geral é só leitura.
                  const editing = canEdit && !promptsLocked && (isCustom || customizing[key] === true);
                  return (
                    // Cada texto é um cartão que abre e fecha (2026-10-16): fechado,
                    // diz a tarefa, o selo, a temperatura e quantos marcadores
                    // usa; aberto, é o editor de sempre. Eram mais de dez editores
                    // abertos em fila e a página não acabava.
                    <details className={`prompt-editor${dirty ? " is-dirty" : ""}`} key={key}>
                      <summary className="prompt-editor-head">
                        <strong>{PROMPT_TASK_LABELS[p.task] ?? p.task}</strong>
                        <span className="badge-pill">
                          {p.position === 0 ? "Principal" : `Desempate ${p.position}`}
                        </span>
                        <span className={`badge-pill ${isCustom ? "active" : "inactive"}`}>
                          {isCustom ? "Personalizado" : "Segue o geral"}
                        </span>
                        {dirty && <span className="prompt-editor-dirty">Alterações não salvas</span>}
                        <span className="prompt-editor-summary">
                          Temperatura {temperature}
                          {markers.length > 0 && ` · ${markers.length} ${markers.length === 1 ? "marcador" : "marcadores"}`}
                        </span>
                        <ChevronDown size={16} aria-hidden="true" className="prompt-editor-chevron" />
                      </summary>
                      <div className="prompt-editor-body">
                      {isCustom && p.default_changed && p.default && (
                        <div className="panel-notice prompt-general-changed">
                          <p>
                            O prompt geral foi alterado depois que esta linha foi personalizada.
                            Compare com o geral abaixo e decida: ajuste o seu texto ou mantenha a
                            personalização.
                          </p>
                          <details className="prompt-general-text">
                            <summary>Ver o texto do prompt geral</summary>
                            <pre>{p.default.header}</pre>
                          </details>
                          {canEdit && !promptsLocked && (
                            <div className="edit-form-actions">
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => keepPromptCustomization(p)}
                                disabled={promptBusy}
                              >
                                Manter a personalização
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <label className="form-field">
                        <span>Texto</span>
                        <textarea
                          rows={8}
                          spellCheck={false}
                          value={header}
                          onChange={e => setDraftValue(`prompt:${key}:header`, e.target.value)}
                          disabled={!editing}
                        />
                      </label>
                      {markers.length > 0 && (
                        <p className="prompt-markers">
                          <span>Preenchido na hora com:</span>
                          {markers.map(marker => (
                            <code key={marker}>{`$$$${marker}$$$`}</code>
                          ))}
                        </p>
                      )}
                      {p.warnings.map((aviso, i) => (
                        <p key={i} className="field-warning">{aviso}</p>
                      ))}
                      <div className="prompt-editor-foot">
                        <label className="form-field prompt-temperature-field">
                          <span>Variabilidade (temperatura)</span>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="2"
                            value={temperature}
                            onChange={e => setDraftValue(`prompt:${key}:temperature`, e.target.value)}
                            disabled={!editing}
                          />
                          <small>
                            Quanto o modelo pode variar a resposta. <strong>Mantenha em 0</strong>{" "}
                            para classificação: acima disso a mesma matéria reprocessada pode
                            sair com polaridade diferente, e o cliente vê o número mudar sem
                            ninguém ter mexido em nada.
                          </small>
                        </label>
                        {canEdit && (
                          <div className="edit-form-actions">
                            {!isCustom && !editing && (
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => setCustomizing(prev => ({ ...prev, [key]: true }))}
                                disabled={promptBusy || promptsLocked}
                              >
                                <Pencil size={16} />
                                Personalizar
                              </button>
                            )}
                            {!isCustom && editing && (
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => forgetPromptDraft(p)}
                                disabled={promptBusy}
                              >
                                Cancelar
                              </button>
                            )}
                            {isCustom && (
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => setConfirmResetKey(key)}
                                disabled={promptBusy || promptsLocked}
                              >
                                <Undo2 size={16} />
                                Voltar ao geral
                              </button>
                            )}
                            {(isCustom || editing) && (
                              <button
                                type="button"
                                className={dirty ? "primary-button" : "secondary-button"}
                                onClick={() => savePromptText(p)}
                                disabled={promptBusy || promptsLocked}
                              >
                                {promptBusy ? <LoaderCircle className="spin" size={16} /> : <Save size={16} />}
                                Salvar texto
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {Number(temperature) > 0 && (
                        <p className="field-warning">
                          Com variabilidade acima de 0, reprocessar a mesma publicação pode
                          dar um resultado diferente.
                        </p>
                      )}
                      </div>
                    </details>
                  );
                })}
              {confirmResetKey !== null && (
                <ConfirmDialog
                  title="Voltar ao prompt geral?"
                  description="A personalização desta linha será apagada e ela volta a seguir o prompt geral da instalação, a partir do próximo upload. O texto personalizado não fica guardado aqui."
                  confirmLabel="Voltar ao geral"
                  danger
                  onCancel={() => setConfirmResetKey(null)}
                  onConfirm={() => {
                    const alvo = prompts.find(p => promptKey(p) === confirmResetKey);
                    setConfirmResetKey(null);
                    if (alvo) resetPromptToGeneral(alvo);
                  }}
                />
              )}
            </div>
            )}

            {activeTab === "modelo-ia" && catalogOwner !== null && (
              <div className="panel-notice">
                <p>
                  O concorrente usa o modelo de IA do cliente: não há escolha própria.
                  Volte pra &quot;A empresa&quot; em &quot;Cadastro de&quot; pra ver e trocar o modelo por tarefa.
                </p>
              </div>
            )}

            {activeTab === "modelo-ia" && catalogOwner === null && (
            <div className="panel-section prompt-block" id="prompt-modelos">
              <h3>Modelo por tarefa</h3>
              <p className="section-hint">
                Qual modelo de IA atende cada tarefa. Sem escolha própria, vale o padrão
                definido pela administração; tarefa sem nenhum dos dois não classifica.
              </p>
              <div className="list-cards-grid prompt-models">
                {promptModels.map(m => (
                  <div
                    key={m.task}
                    className={`item-card${m.model_id === null ? " is-unassigned" : ""}`}
                  >
                    <div className="item-card-info">
                      <Cpu size={20} className="item-card-icon" />
                      <div>
                        <strong>{PROMPT_TASK_LABELS[m.task] ?? m.task}</strong>
                        <span>
                          {m.model_name ?? "Nenhum modelo — esta tarefa não classifica"}
                          {m.origin === "default" && " · padrão da instalação"}
                          {m.origin === "override" && " · escolhido para esta empresa"}
                          {m.model_id !== null && !m.model_active && " · fora do catálogo ativo"}
                        </span>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="item-card-actions">
                        <div className="inline-add-box">
                          <Select
                            aria-label={`Modelo de ${PROMPT_TASK_LABELS[m.task] ?? m.task}`}
                            value={m.origin === "override" && m.model_id !== null ? String(m.model_id) : ""}
                            onChange={e => savePromptModel(m.task, e.target.value)}
                            disabled={promptBusy || modelsLocked}
                          >
                            <option value="">Usar o padrão da instalação</option>
                            {/*
                              O valor gravado que saiu do catálogo entra como
                              opção órfã: sem isto ele sumiria do seletor e a
                              tela mostraria "usar o padrão" numa tarefa que
                              tem escolha própria. Mesma regra do seletor de
                              recorte do relatório.
                            */}
                            {m.origin === "override" &&
                              m.model_id !== null &&
                              !aiModelOptions.some(o => o.id === m.model_id) && (
                                <option value={String(m.model_id)}>
                                  {m.model_name ?? `#${m.model_id}`} (desativado)
                                </option>
                              )}
                            {aiModelOptions.map(opcao => (
                              <option key={opcao.id} value={String(opcao.id)}>
                                {opcao.label ? `${opcao.label} (${opcao.name})` : opcao.name}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {aiModelOptions.length === 0 && (
                  <p>
                    Nenhum modelo disponível. A administração precisa cadastrar os modelos e a
                    chave do provedor em Administração → Modelos de IA.
                  </p>
                )}
              </div>
            </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
